import React, { useState, useEffect, useCallback } from 'react';
import T from '../styles/theme';
import { Avatar, Badge, Btn, ProgressBar, fmtDollar, Select , Checkbox} from '../components/UI';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import { useStore } from '../data/store';
import {
  DEFAULT_CONTACT_FIELDS, DEFAULT_DEAL_FIELDS, DEFAULT_COMPANY_FIELDS,
  DEFAULT_TASK_FIELDS, DEFAULT_CALL_FIELDS, DEFAULT_EMAIL_FIELDS,
  DEFAULT_SMS_FIELDS, DEFAULT_CALENDAR_FIELDS,
} from '../data/systemFields';
import { useAuth, ROLES, PERMISSIONS, ROLE_PERMISSIONS } from '../data/auth';
import { useToast } from '../components/Toast';
import supabase from '../data/supabase';
import apiFetch from '../data/api';

const cfDate = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); return m+"/"+dy+"/"+y; };
const cfDateTime = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); let h = dt.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; const mn = String(dt.getMinutes()).padStart(2,"0"); return m+"/"+dy+"/"+y+" "+h+":"+mn+" "+ap; };
/* ═══════════════════════════════════════════════════════
   ADMIN CENTER - Settings & Configuration
   ═══════════════════════════════════════════════════════ */

// ── Call disposition behaviors ──
// Five surfaced to admins in v1. The 6th constraint value (mark_closed_lost)
// is in migration 071's CHECK list for forward-compat but no UI exposes it
// yet. Order here is the order shown in the modal dropdown.
const BEHAVIOR_LABELS = {
  none: "No behavior",
  do_not_call: "Do Not Call",
  qualified: "Qualified",
  wrong_number: "Wrong Number",
  requeue_later: "Re-queue Later",
  redial: "Redial",
};
const BEHAVIOR_DESCRIPTIONS = {
  none: "Records the call, no contact-side effect.",
  do_not_call: "Contact is excluded from all dialer surfaces. Cannot be called again until manually cleared.",
  qualified: "Contact is removed from the Power Dialer queue but remains manually callable.",
  wrong_number: "Flags the contact's phone as bad. Contact is excluded from the Power Dialer queue.",
  requeue_later: "Contact returns to the Power Dialer queue after the specified number of days.",
  redial: "Logs the call as a redial event, then immediately dials the same number again. Queue position stays put.",
};

const FIELD_TYPES = [
  { id: "text", label: "Text" },
  { id: "number", label: "Number" },
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "date", label: "Date" },
  { id: "select", label: "Dropdown" },
  { id: "multiselect", label: "Multi-Select" },
  { id: "currency", label: "Currency" },
  { id: "url", label: "URL" },
  { id: "textarea", label: "Long Text" },
  { id: "checkbox", label: "Checkbox" },
  { id: "user", label: "User" },
  { id: "calculation", label: "Calculation" },
];

// Tier A: locked system fields. No edit UI exposed; their structure is
// referenced by integrations, RLS, and database schema. Tier B (Lead Source,
// Industry, Account Type, Status) and Tier C (custom fields) are editable.
const LOCKED_FIELD_IDS = new Set([
  "fn", "ln", "email", "ph", "title", "co", "owner",
]);

// Tier B: system fields whose OPTIONS are user-editable, but whose label,
// type, required-flag, and id stay locked. Keyed by entity → Set of
// system-field IDs. Today only contacts has Tier B fields; the structure
// is extensible — other entities can add their own when needed.
const TIER_B_FIELD_IDS = {
  contacts: new Set(["source", "type", "industry", "status"]),
};

// Returns the effective options array for a field — applies override > default.
// Overrides live on customFieldsState.__overrides[entity][fieldId].options.
// Used by AdminCenter for live admin display in Commit H; consumer code
// (ContactForm, ContactsList, DealRecord, CompanyRecord, etc.) will route
// through this helper in Commit I to actually honor the overrides.
function getEffectiveOptions(field, entity, customFieldsState) {
  const override = customFieldsState?.__overrides?.[entity]?.[field.id]?.options;
  if (Array.isArray(override) && override.length > 0) return override;
  return field.options || [];
}

/* DEFAULT_*_FIELDS moved to src/data/systemFields.js (Commit I) so the
   useFieldOptions hook in store.js can read them without a circular import. */

/* TEAM_MEMBERS now in store */

// Brand logos served from the Simple Icons public CDN.
// Format: https://cdn.simpleicons.org/<brand>/<hex color, no #>
const LOGO_GMAIL = "https://cdn.simpleicons.org/gmail/EA4335";
const LOGO_GOOGLE_CALENDAR = "https://cdn.simpleicons.org/googlecalendar/4285F4";

// Integrations not yet wired up — shown as muted "Coming Soon" cards,
// no Connect button, no click handler.
const COMING_SOON_INTEGRATIONS = [
  { id: "linkedin", name: "LinkedIn", desc: "Account management, KPI tracking & lead import", logo: "https://cdn.simpleicons.org/linkedin/0A66C2" },
  { id: "zapier", name: "Zapier", desc: "Connect 5,000+ apps", logo: "https://cdn.simpleicons.org/zapier/FF4A00" },
];

/* ── Profile & Account Tab (Dynamic) ── */
// ── Role color picker (self-contained, shared cache for all instances) ──
let _roleColorsCache = null;
let _roleColorsPromise = null;
async function _loadRoleColors(orgId) {
  if (_roleColorsCache) return _roleColorsCache;
  if (_roleColorsPromise) return _roleColorsPromise;
  _roleColorsPromise = supabase.from("organizations").select("role_colors").eq("id", orgId).maybeSingle().then(({ data }) => {
    _roleColorsCache = (data && data.role_colors) || {};
    return _roleColorsCache;
  }).catch(() => { _roleColorsCache = {}; return _roleColorsCache; });
  return _roleColorsPromise;
}

function RoleColorPicker({ orgId, roleKey, defaultColor }) {
  const PRESETS = ["#2E8B57", "#4059AD", "#D4763A", "#3D8B8B", "#A34F9E", "#6B83D4", "#E5B94B", "#7A7A7A"];
  const [color, setColor] = React.useState(defaultColor || "#7A7A7A");
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!orgId) return;
    _loadRoleColors(orgId).then(rc => { if (rc[roleKey]) setColor(rc[roleKey]); });
  }, [orgId, roleKey]);

  const save = async (newColor) => {
    setColor(newColor);
    setOpen(false);
    if (!orgId) return;
    _roleColorsCache = { ...(_roleColorsCache || {}), [roleKey]: newColor };
    const { error } = await supabase.from("organizations").update({ role_colors: _roleColorsCache }).eq("id", orgId);
    if (error) console.warn("[RoleColor] save failed:", error.message);
  };

  return (<div style={{ position: "relative", display: "inline-block" }} onClick={e => e.stopPropagation()}>
    <div onClick={() => setOpen(o => !o)} title="Change color" style={{ width: 14, height: 14, borderRadius: 7, background: color, cursor: "pointer", border: "1px solid rgba(255,255,255,0.12)" }} />
    {open && (<>
      <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
      <div style={{ position: "absolute", top: 22, left: 0, background: "#1C1E24", border: "1px solid rgba(217,219,221,0.2)", borderRadius: 8, padding: 8, display: "flex", gap: 6, zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        {PRESETS.map(c => (<div key={c} onClick={() => save(c)} style={{ width: 20, height: 20, borderRadius: 10, background: c, cursor: "pointer", border: c === color ? "2px solid #FAFAFA" : "1px solid rgba(255,255,255,0.1)" }} />))}
      </div>
    </>)}
  </div>);
}

function ProfileTab({ inputS }) {
  const auth = useAuth();
  const toast = useToast();
  const user = auth.currentUser;
  const userId = user?._dbId || user?.id;
  const [form, setForm] = useState({
    firstName: user?.firstName || "", lastName: user?.lastName || "",
    email: user?.email || "", phone: user?.phone || "", title: user?.title || "",
    company: "", timezone: "America/New_York",
  });
  const [pwForm, setPwForm] = useState({ current: "", new1: "", new2: "" });
  const [showSignOutOthersModal, setShowSignOutOthersModal] = useState(false);
  const [signingOutOthers, setSigningOutOthers] = useState(false);

  const handleSignOutOthers = async () => {
    setSigningOutOthers(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: "others" });
      if (error) throw error;
      toast?.success?.("Signed out of all other sessions");
      setShowSignOutOthersModal(false);
    } catch (err) {
      toast?.error?.("Failed to sign out other sessions: " + (err.message || "unknown error"));
    } finally {
      setSigningOutOthers(false);
    }
  };
  const [showPw, setShowPw] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [avatarHover, setAvatarHover] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = React.useRef(null);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate
    if (!file.type.startsWith("image/")) { alert("Please select an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { alert("Image must be under 2MB"); return; }

    try {
      const ext = file.name.split(".").pop();
      const path = `avatars/${userId}.${ext}`;
      // Upload to Supabase Storage
      const { error: upErr } = await supabase.storage.from("avatar").upload(path, file, { upsert: true });
      if (upErr) {
        // If bucket doesn't exist, fall back to base64 in DB
        console.warn("[CoreFlow] Storage upload failed, using base64:", upErr.message);
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const base64 = ev.target.result;
          setAvatarUrl(base64);
          await supabase.from("users").update({ avatar_url: base64 }).eq("id", userId);
        };
        reader.readAsDataURL(file);
        return;
      }
      const { data: urlData } = supabase.storage.from("avatar").getPublicUrl(path);
      const publicUrl = urlData?.publicUrl + "?t=" + Date.now();
      setAvatarUrl(publicUrl);
      await supabase.from("users").update({ avatar_url: publicUrl }).eq("id", userId);
    } catch (err) {
      console.warn("[CoreFlow] Avatar upload error:", err.message);
      // Fallback: store as base64
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        setAvatarUrl(base64);
        try { await supabase.from("users").update({ avatar_url: base64 }).eq("id", userId); } catch(e) {}
      };
      reader.readAsDataURL(file);
    }
  };

  // Load profile from Supabase users table
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.from("users").select("*").eq("id", userId).single();
        if (data) {
          setForm(f => ({
            ...f,
            // users.name doesn't exist as a column — the prior
            // data.name?.split() fallbacks were dead code from a stale
            // schema assumption. first_name / last_name are the real
            // sources.
            firstName: data.first_name || f.firstName,
            lastName: data.last_name || f.lastName,
            email: data.email || f.email,
            phone: data.phone || f.phone,
            title: data.title || f.title,
            timezone: data.timezone || f.timezone,
          }));
          if (data.avatar_url) setAvatarUrl(data.avatar_url);

          // Pull the real org name for the "<role> at <org>" subtitle.
          // Falls back to whatever form.company already is if the lookup errors.
          if (data.org_id) {
            const { data: org } = await supabase.from("organizations")
              .select("name").eq("id", data.org_id).maybeSingle();
            if (org?.name) setForm(f => ({ ...f, company: org.name }));
          }
        }
      } catch (e) { console.warn("[CoreFlow] Profile load:", e.message); }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    // Mirrors UserProfile.js's pattern: DB write FIRST so a failure
    // doesn't leave in-memory auth state out of sync with what's
    // actually saved. The previous version did auth-update first +
    // swallowed DB errors via console.warn, which is how the bogus
    // 'name' column write went unnoticed for so long — users saw
    // 'Saved!' while nothing actually persisted.
    try {
      const { error } = await supabase.from("users").update({
        first_name: form.firstName,
        last_name: form.lastName,
        phone: form.phone,
        title: form.title,
        timezone: form.timezone,
        updated_at: new Date().toISOString(),
      }).eq("id", userId);
      if (error) {
        console.error("[CoreFlow] Profile save failed:", error.message);
        toast?.error?.("Failed to save profile: " + error.message);
        return;
      }
    } catch (e) {
      console.error("[CoreFlow] Profile save error:", e?.message);
      toast?.error?.("Failed to save profile: " + (e?.message || "unknown error"));
      return;
    }
    // DB succeeded — sync in-memory auth state so header/nav reflect
    // the new values immediately.
    auth.updateUser(user.id, {
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone,
      title: form.title,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handlePwChange = async () => {
    if (!pwForm.current || !pwForm.new1) { setPwMsg("Fill in all fields"); return; }
    if (pwForm.new1 !== pwForm.new2) { setPwMsg("Passwords don't match"); return; }
    if (pwForm.new1.length < 8) { setPwMsg("Password must be at least 8 characters"); return; }
    // Update in Supabase auth if using supabase auth, otherwise just show success
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.new1 });
      if (error) { setPwMsg("Error: " + error.message); return; }
    } catch (e) { /* Not using supabase auth, just show success */ }
    setPwMsg("Password updated successfully");
    setPwForm({ current: "", new1: "", new2: "" });
    setTimeout(() => { setPwMsg(""); setShowPw(false); }, 2000);
  };

  const initials = (form.firstName?.[0] || "") + (form.lastName?.[0] || "");
  const labelS = { fontSize: 11, fontWeight: 400, color: T.tx3, marginBottom: 4, display: "block" };
  const rowS = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Profile & Account</h2>
        <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Manage your personal info and security</p>
      </div>

      {/* Avatar + Name header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: 20, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, marginBottom: 24 }}>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarUpload} />
        <div onClick={() => fileInputRef.current?.click()} onMouseEnter={() => setAvatarHover(true)} onMouseLeave={() => setAvatarHover(false)}
          style={{
            width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            background: avatarUrl ? "transparent" : T.blue + "15", border: "2px solid " + T.blue + "30",
            fontSize: 20, fontWeight: 400, color: T.blue, cursor: "pointer", overflow: "hidden", position: "relative",
          }}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            initials.toUpperCase()
          )}
          {avatarHover && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "50%", color: "#fff", fontSize: 10, fontWeight: 400,
            }}>Upload</div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 400, color: T.txt }}>{form.firstName} {form.lastName}</div>
          <div style={{ fontSize: 12, color: T.tx3 }}>{form.email}</div>
          <div style={{ fontSize: 11, color: T.tx2, marginTop: 2 }}>{form.title || (user?.role === "admin" ? "Admin" : "Team Member")}{form.company ? ` at ${form.company}` : ""}</div>
        </div>
      </div>

      {/* Personal Info */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Personal Information</div>
        <div style={rowS}>
          <div><label style={labelS}>First Name</label><input value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} style={{...inputS}} /></div>
          <div><label style={labelS}>Last Name</label><input value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} style={{...inputS}} /></div>
        </div>
        <div style={rowS}>
          <div><label style={labelS}>Email Address</label><input value={form.email} disabled style={{...inputS, opacity: 0.6, cursor: "not-allowed"}} /><span style={{ fontSize: 9, color: T.tx3 }}>Contact admin to change email</span></div>
          <div><label style={labelS}>Phone Number</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+1 (555) 000-0000" style={{...inputS}} /></div>
        </div>
        <div style={rowS}>
          <div><label style={labelS}>Job Title</label><input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Account Executive" style={{...inputS}} /></div>
          <div>
            <label style={labelS}>Timezone</label>
            <Select value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})} placeholder="— Select —" options={[{value:"America/New_York",label:"Eastern (ET)"},{value:"America/Chicago",label:"Central (CT)"},{value:"America/Denver",label:"Mountain (MT)"},{value:"America/Los_Angeles",label:"Pacific (PT)"},{value:"America/Anchorage",label:"Alaska (AKT)"},{value:"Pacific/Honolulu",label:"Hawaii (HT)"},{value:"Europe/London",label:"London (GMT)"},{value:"Europe/Paris",label:"Central European (CET)"},{value:"Asia/Tokyo",label:"Japan (JST)"}]} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
          <button onClick={handleSave} style={{ padding: "8px 24px", borderRadius: 8, border: "none", background: T.blue, color: "#fff", fontSize: 12, fontWeight: 400, cursor: "pointer" }}>Save Changes</button>
          {saved && <span style={{ fontSize: 11, color: "#FAFAFA", fontWeight: 400 }}>Saved</span>}
        </div>
      </div>

      {/* Security */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Security</div>
        <div style={{ padding: 16, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
          {!showPw ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Password</div>
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>Change your account password</div>
              </div>
              <button onClick={() => setShowPw(true)} style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, fontWeight: 400, cursor: "pointer" }}>Change Password</button>
            </div>
          ) : (
            <div>
              <div style={{ display: "grid", gap: 10, maxWidth: 320 }}>
                <div><label style={labelS}>Current Password</label><input type="password" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} style={{...inputS}} /></div>
                <div><label style={labelS}>New Password</label><input type="password" value={pwForm.new1} onChange={e => setPwForm({...pwForm, new1: e.target.value})} style={{...inputS}} /></div>
                <div><label style={labelS}>Confirm New Password</label><input type="password" value={pwForm.new2} onChange={e => setPwForm({...pwForm, new2: e.target.value})} style={{...inputS}} /></div>
              </div>
              {pwMsg && <div style={{ fontSize: 11, color: "#FAFAFA", marginTop: 8 }}>{pwMsg}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={handlePwChange} style={{ padding: "6px 16px", borderRadius: 8, border: "none", background: T.blue, color: "#fff", fontSize: 11, fontWeight: 400, cursor: "pointer" }}>Update Password</button>
                <button onClick={() => { setShowPw(false); setPwMsg(""); }} style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.tx2, fontSize: 11, fontWeight: 400, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Sessions */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Active Sessions</div>
        <div style={{ padding: 16, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2E8B57" }}></div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Current Session</div>
                <div style={{ fontSize: 10, color: T.tx3 }}>{(() => { const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''; const os = ua.includes('Macintosh') || ua.includes('Mac OS') ? 'macOS' : ua.includes('Windows') ? 'Windows' : ua.includes('iPhone') || ua.includes('iPad') ? 'iOS' : ua.includes('Android') ? 'Android' : 'Web'; const br = ua.includes('Edg/') ? 'Edge' : ua.includes('Chrome') ? 'Chrome' : ua.includes('Firefox') ? 'Firefox' : ua.includes('Safari') ? 'Safari' : 'Browser'; return os + ' · ' + br; })()}</div>
              </div>
            </div>
            <span style={{ fontSize: 10, color: "#FAFAFA", fontWeight: 400 }}>Active now</span>
          </div>
          <button onClick={() => setShowSignOutOthersModal(true)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 10, fontWeight: 400, cursor: "pointer" }}>Sign Out All Other Sessions</button>
        </div>
      </div>

      {showSignOutOthersModal && (
        <div onClick={() => !signingOutOthers && setShowSignOutOthersModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 400, color: T.txt, marginBottom: 8 }}>Sign Out Other Sessions</div>
            <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.6, marginBottom: 16 }}>
              This signs you out of every other browser and device where you're logged in. Your current session will stay active.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowSignOutOthersModal(false)} disabled={signingOutOthers} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={handleSignOutOthers} disabled={signingOutOthers} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: T.blue, color: "#FAFAFA", fontSize: 11, cursor: signingOutOthers ? "default" : "pointer", fontFamily: "inherit", opacity: signingOutOthers ? 0.6 : 1 }}>{signingOutOthers ? "Signing out..." : "Sign Out Others"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Notifications Tab — Category × Channel Toggle Matrix ── */
/* Uses notification_preferences table (not user_preferences) */
function NotificationsTab() {
  const auth = useAuth();
  const userId = auth.currentUser?._dbId || auth.currentUser?.id;
  const orgId = auth.currentUser?._orgId;

  const CATEGORIES = [
    { key: "deal_change",     label: "Deal changes",   desc: "Stage moves, wins, losses, assignments",      icon: "briefcase" },
    { key: "task",            label: "Tasks",          desc: "Assigned, due soon, overdue",                 icon: "check" },
    { key: "email",           label: "Inbound email",  desc: "New emails from contacts",                    icon: "mail" },
    { key: "sms",             label: "Inbound SMS",    desc: "New text messages from contacts",             icon: "chat" },
    { key: "call",            label: "Calls",          desc: "Inbound calls, missed calls",                 icon: "phone" },
    { key: "workflow",        label: "Workflows",      desc: "Triggered, completed, failed",                icon: "workflow" },
    { key: "mention",         label: "Mentions",       desc: "@mentions in team chat or notes",             icon: "chat" },
    { key: "calendar",        label: "Calendar",       desc: "Meeting reminders, new bookings",             icon: "calendar" },
    { key: "campaign",        label: "Campaigns",      desc: "Completed, bounces, errors",                  icon: "send" },
    { key: "project",         label: "Projects",       desc: "Card assignments, comments, moves on boards", icon: "folder" },
    { key: "pipeline_doctor", label: "Pipeline Doctor", desc: "AI-detected at-risk deals and stuck pipeline", icon: "target" },
    { key: "system",          label: "System",         desc: "Admin alerts, welcome messages",              icon: "info" },
  ];
  const CHANNELS = [
    { key: "in_app", label: "In-App",  icon: "bell" },
    { key: "email",  label: "Email",   icon: "mail" },
    { key: "push",   label: "Push",    icon: "zap" },
    { key: "sms",    label: "SMS",     icon: "phone" },
  ];
  const DEFAULT_CHANNEL_PREFS = {
    deal_change:     { in_app: true, email: true,  push: true,  sms: false },
    task:            { in_app: true, email: true,  push: true,  sms: false },
    email:           { in_app: true, email: false, push: true,  sms: false },
    sms:             { in_app: true, email: false, push: true,  sms: false },
    call:            { in_app: true, email: false, push: true,  sms: true  },
    workflow:        { in_app: true, email: true,  push: false, sms: false },
    mention:         { in_app: true, email: true,  push: true,  sms: false },
    calendar:        { in_app: true, email: true,  push: true,  sms: true  },
    campaign:        { in_app: true, email: true,  push: false, sms: false },
    project:         { in_app: true, email: false, push: true,  sms: false },
    pipeline_doctor: { in_app: true, email: true,  push: false, sms: false },
    system:          { in_app: true, email: false, push: false, sms: false },
  };

  const [channelPrefs, setChannelPrefs] = useState(DEFAULT_CHANNEL_PREFS);
  const [globalMute, setGlobalMute] = useState(false);
  const [quietOn, setQuietOn] = useState(false);
  const [quietStart, setQuietStart] = useState("22:00");
  const [quietEnd, setQuietEnd] = useState("08:00");
  const [quietTz, setQuietTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
  const [digestFreq, setDigestFreq] = useState("realtime");
  const [digestTime, setDigestTime] = useState("08:00");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load from notification_preferences table via API
  useEffect(() => {
    (async () => {
      try {
        const token = (await supabase.auth.getSession())?.data?.session?.access_token;
        if (!token) { setLoading(false); return; }
        const res = await fetch("/api/notifications/preferences", {
          headers: { Authorization: "Bearer " + token },
        });
        if (!res.ok) throw new Error("Failed");
        const { preferences: p } = await res.json();
        if (p) {
          if (p.channel_preferences) setChannelPrefs(cp => ({ ...cp, ...p.channel_preferences }));
          if (p.global_mute !== undefined) setGlobalMute(p.global_mute);
          if (p.quiet_hours_on !== undefined) setQuietOn(p.quiet_hours_on);
          if (p.quiet_hours_start) setQuietStart(p.quiet_hours_start);
          if (p.quiet_hours_end) setQuietEnd(p.quiet_hours_end);
          if (p.quiet_hours_tz) setQuietTz(p.quiet_hours_tz);
          if (p.email_digest_frequency) setDigestFreq(p.email_digest_frequency);
          if (p.email_digest_time) setDigestTime(p.email_digest_time);
        }
      } catch (e) { console.warn("[CoreFlow] Load notif prefs:", e.message); }
      setLoading(false);
    })();
  }, []);

  const toggleChannel = (cat, ch) => {
    setChannelPrefs(prev => ({
      ...prev,
      [cat]: { ...prev[cat], [ch]: !prev[cat]?.[ch] },
    }));
  };

  const handleSave = async () => {
    try {
      const token = (await supabase.auth.getSession())?.data?.session?.access_token;
      if (!token) return;
      await fetch("/api/notifications/preferences", {
        method: "PUT",
        headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
        body: JSON.stringify({
          channel_preferences: channelPrefs,
          global_mute: globalMute,
          quiet_hours_on: quietOn,
          quiet_hours_start: quietStart,
          quiet_hours_end: quietEnd,
          quiet_hours_tz: quietTz,
          email_digest_frequency: digestFreq,
          email_digest_time: digestTime,
        }),
      });
    } catch (e) { console.warn("[CoreFlow] Save notif prefs:", e.message); }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const Sw = ({ on, onClick }) => (
    <button style={{
      width: 20, height: 20, borderRadius: 8, cursor: "pointer", border: on ? "1.5px solid " + T.blue : "1.5px solid " + T.bdr,
      background: on ? T.blue : "transparent", display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, transition: "all 0.15s", padding: 0,
    }} onClick={onClick}>
      {on && <Icon name="check" size={12} color="#FAFAFA" />}
    </button>
  );

  const glassCard = {
    background: "#2C2E33",
    border: "1.5px solid rgba(217,219,221,0.12)", borderRadius: 8,
  };
  const inputS = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.60)", borderRadius: 8, padding: "6px 10px", color: T.txt, fontSize: 11,
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12 }}>Loading preferences...</div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Notification Preferences</h2>
        <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Control how and when you receive notifications across all channels</p>
      </div>

      {/* ── Global Mute ── */}
      <div style={{ ...glassCard, padding: 16, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 400, color: globalMute ? "#E53935" : T.txt, display: "flex", alignItems: "center", gap: 6 }}>
            {globalMute && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 8, background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontWeight: 400 }}>MUTED</span>}
            Global Mute
          </div>
          <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Silence ALL notifications. Urgent alerts will still come through.</div>
        </div>
        <Sw on={globalMute} onClick={() => setGlobalMute(!globalMute)} />
      </div>

      {/* ── Category × Channel Matrix ── */}
      <div style={{ ...glassCard, marginBottom: 20, overflow: "hidden" }}>
        {/* Header row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr repeat(4, 64px)", padding: "12px 16px", borderBottom: "1px solid " + T.bdr, background: "rgba(64,89,173,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".04em" }}>CATEGORY</div>
          {CHANNELS.map(ch => (
            <div key={ch.key} style={{ textAlign: "center", fontSize: 10, fontWeight: 400, color: T.tx3, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <Icon name={ch.icon} size={12} color={T.tx3} />
              {ch.label}
            </div>
          ))}
        </div>
        {/* Category rows */}
        {CATEGORIES.map((cat, i) => (
          <div key={cat.key} style={{
            display: "grid", gridTemplateColumns: "1fr repeat(4, 64px)", padding: "10px 16px", alignItems: "center",
            borderBottom: i < CATEGORIES.length - 1 ? "1px solid " + T.bdr : "none",
            opacity: globalMute ? 0.4 : 1, pointerEvents: globalMute ? "none" : "auto",
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon name={cat.icon} size={13} color={T.blue} />
                {cat.label}
              </div>
              <div style={{ fontSize: 10, color: T.tx3, marginTop: 1, paddingLeft: 19 }}>{cat.desc}</div>
            </div>
            {CHANNELS.map(ch => (
              <div key={ch.key} style={{ display: "flex", justifyContent: "center" }}>
                <Sw on={channelPrefs[cat.key]?.[ch.key] ?? false} onClick={() => toggleChannel(cat.key, ch.key)} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ── Quiet Hours ── */}
      <div style={{ ...glassCard, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Quiet Hours</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: quietOn ? 12 : 0 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Enable quiet hours</div>
            <div style={{ fontSize: 10, color: T.tx3, marginTop: 1 }}>Pause non-urgent notifications during set hours</div>
          </div>
          <Sw on={quietOn} onClick={() => setQuietOn(!quietOn)} />
        </div>
        {quietOn && (
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "8px 0 0" }}>
            <span style={{ fontSize: 11, color: T.tx3 }}>From</span>
            <input type="time" value={quietStart} onChange={e => setQuietStart(e.target.value)} style={inputS} />
            <span style={{ fontSize: 11, color: T.tx3 }}>to</span>
            <input type="time" value={quietEnd} onChange={e => setQuietEnd(e.target.value)} style={inputS} />
            <Select value={quietTz} onChange={e => setQuietTz(e.target.value)} style={{ ...inputS, maxWidth: 200 }}>
              {["America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Anchorage","Pacific/Honolulu","Europe/London","Europe/Paris","Asia/Tokyo","Australia/Sydney","UTC"]
                .map(tz => <option key={tz} value={tz}>{tz.replace("_"," ")}</option>)}
            </Select>
          </div>
        )}
      </div>

      {/* ── Email Digest ── */}
      <div style={{ ...glassCard, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Email Digest</div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: T.tx3 }}>Frequency:</span>
          {["realtime","hourly","daily","weekly","off"].map(f => (
            <button key={f} onClick={() => setDigestFreq(f)} style={{
              padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
              fontSize: 11, fontWeight: 400, textTransform: "capitalize",
              background: digestFreq === f ? T.blue : "rgba(0,0,0,0.20)",
              color: digestFreq === f ? "#fff" : T.tx3,
              transition: "all 0.15s",
            }}>{f}</button>
          ))}
        </div>
        {(digestFreq === "daily" || digestFreq === "weekly") && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
            <span style={{ fontSize: 11, color: T.tx3 }}>Send at:</span>
            <input type="time" value={digestTime} onChange={e => setDigestTime(e.target.value)} style={inputS} />
          </div>
        )}
      </div>

      {/* ── Save ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={handleSave} style={{ padding: "8px 24px", borderRadius: 8, border: "none", background: T.blue, color: "#fff", fontSize: 12, fontWeight: 400, cursor: "pointer", boxShadow: "0 2px 12px rgba(64,89,173,0.25)" }}>Save Preferences</button>
        {saved && <span style={{ fontSize: 11, color: "#FAFAFA", fontWeight: 400 }}>✓ Saved</span>}
      </div>
    </div>
  );
}

/* ── Email Signatures Tab (Dynamic — persists to Supabase) ── */
function SignaturesTab({ inputS }) {
  const auth = useAuth();
  const user = auth.currentUser;
  const userId = user?._dbId || user?.id;
  const orgId = user?._orgId;
  const [signatures, setSignatures] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", content: "", html: "", mode: "visual", logoUrl: "", logoPosition: "top", phone: "", website: "", title: "", company: "" });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.from("email_signatures")
          .select("*").eq("user_id", userId).order("created_at", { ascending: true });
        if (data && data.length > 0) {
          setSignatures(data);
        } else {
          const defaultSig = {
            user_id: userId, org_id: orgId, name: "Default", is_default: true,
            content: `${user?.firstName || ""} ${user?.lastName || ""}\n${user?.title || "Team Member"}`,
            html: "",
          };
          const { data: inserted } = await supabase.from("email_signatures").insert(defaultSig).select();
          if (inserted) setSignatures(inserted);
        }
      } catch (e) {
        setSignatures([{ id: "local-1", name: "Default", is_default: true, content: `${user?.firstName || ""} ${user?.lastName || ""}`, html: "" }]);
      }
      setLoading(false);
    })();
  }, [user]);

  const startEdit = (sig) => {
    setEditing(sig.id);
    // Parse existing fields from content or use stored values
    const lines = (sig.content || "").split("\n");
    setEditForm({
      name: sig.name || "",
      content: sig.content || "",
      html: sig.html || "",
      mode: sig.html ? "html" : "visual",
      logoUrl: sig.logo_url || "",
      logoPosition: sig.logo_position || "top",
      phone: sig.phone || "",
      website: sig.website || "",
      title: sig.sig_title || lines[1] || "",
      company: sig.company || "",
    });
  };
  const cancelEdit = () => { setEditing(null); };

  const buildHtmlFromVisual = () => {
    const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    const logoHtml = editForm.logoUrl ? `<img src="${editForm.logoUrl}" alt="" style="height:40px;margin-${editForm.logoPosition === "top" ? "bottom" : "top"}:8px;display:block;" />` : "";
    const parts = [];
    if (logoHtml && editForm.logoPosition === "top") parts.push(logoHtml);
    parts.push(`<strong style="font-size:14px;color:#1a1a1a;">${name}</strong>`);
    if (editForm.title) parts.push(`<span style="font-size:12px;color:#666;">${editForm.title}</span>`);
    if (editForm.company) parts.push(`<span style="font-size:12px;color:#666;">${editForm.company}</span>`);
    const contactParts = [];
    if (editForm.phone) contactParts.push(editForm.phone);
    if (user?.email) contactParts.push(user.email);
    if (contactParts.length) parts.push(`<span style="font-size:11px;color:#888;">${contactParts.join(" · ")}</span>`);
    if (editForm.website) parts.push(`<a href="${editForm.website.startsWith("http") ? editForm.website : "https://" + editForm.website}" style="font-size:11px;color:#4059AD;text-decoration:none;">${editForm.website.replace(/^https?:\/\//, "")}</a>`);
    if (logoHtml && editForm.logoPosition === "bottom") parts.push(logoHtml);
    return parts.join("<br/>");
  };

  const saveEdit = async () => {
    const finalHtml = editForm.mode === "html" ? editForm.html : buildHtmlFromVisual();
    const plainContent = editForm.mode === "html"
      ? editForm.html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim()
      : [user?.firstName + " " + user?.lastName, editForm.title, editForm.company, editForm.phone, user?.email, editForm.website].filter(Boolean).join("\n");

    try {
      await supabase.from("email_signatures").update({
        name: editForm.name, content: plainContent, html: finalHtml,
        logo_url: editForm.logoUrl, logo_position: editForm.logoPosition, phone: editForm.phone, website: editForm.website,
        sig_title: editForm.title, company: editForm.company,
        updated_at: new Date().toISOString(),
      }).eq("id", editing);
    } catch (e) { console.warn("[CoreFlow] Signature save:", e.message); }
    setSignatures(prev => prev.map(s => s.id === editing ? {
      ...s, name: editForm.name, content: plainContent, html: finalHtml,
      logo_url: editForm.logoUrl, logo_position: editForm.logoPosition, phone: editForm.phone, website: editForm.website,
      sig_title: editForm.title, company: editForm.company,
    } : s));
    setEditing(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addSig = async () => {
    const newSig = { user_id: userId, org_id: orgId, name: "New Signature", is_default: false, content: `${user?.firstName || ""} ${user?.lastName || ""}`, html: "" };
    try {
      const { data } = await supabase.from("email_signatures").insert(newSig).select();
      if (data?.[0]) { setSignatures(prev => [...prev, data[0]]); startEdit(data[0]); }
    } catch (e) {
      const local = { ...newSig, id: "local-" + Date.now() };
      setSignatures(prev => [...prev, local]);
      startEdit(local);
    }
  };

  const deleteSig = async (id) => {
    if (signatures.length <= 1) return;
    try { await supabase.from("email_signatures").delete().eq("id", id); } catch (e) {}
    setSignatures(prev => prev.filter(s => s.id !== id));
    if (editing === id) setEditing(null);
  };

  const setDefault = async (id) => {
    try {
      await supabase.from("email_signatures").update({ is_default: false }).eq("user_id", userId);
      await supabase.from("email_signatures").update({ is_default: true }).eq("id", id);
    } catch (e) {}
    setSignatures(prev => prev.map(s => ({ ...s, is_default: s.id === id })));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Max 2MB"); return; }
    setLogoUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `sig-logos/${orgId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatar").upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("avatar").getPublicUrl(path);
      if (urlData?.publicUrl) setEditForm(f => ({ ...f, logoUrl: urlData.publicUrl }));
    } catch (err) { console.warn("[Sig] Logo upload failed:", err.message); }
    setLogoUploading(false);
  };

  const editCard = { background: "#2C2E33", border: "1px solid rgba(217,219,221,0.10)", borderRadius: 8, padding: 20 };
  const fldLbl = { fontSize: 10, fontWeight: 400, color: "rgba(250,250,250,0.35)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 5, display: "block" };
  const fld = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  const defaultSig = signatures.find(s => s.is_default) || signatures[0];
  const previewHtml = defaultSig?.html || "";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", margin: 0 }}>Email Signatures</h2>
          <p style={{ fontSize: 12, color: "rgba(250,250,250,0.35)", margin: "4px 0 0" }}>Create and manage signatures with logos, links, and custom HTML</p>
        </div>
        <button onClick={addSig} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>+ New Signature</button>
      </div>

      {saved && <div style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(46,139,87,0.08)", border: "1px solid rgba(46,139,87,0.15)", color: "#2E8B57", fontSize: 12, marginBottom: 12 }}>Signature saved</div>}

      {/* Signature list */}
      {signatures.map(sig => (
        <div key={sig.id} style={{ ...editCard, marginBottom: 10 }}>
          {editing === sig.id ? (
            <div>
              {/* Mode toggle */}
              <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 8, overflow: "hidden", border: "1px solid rgba(217,219,221,0.12)", width: "fit-content" }}>
                {[{ id: "visual", label: "Visual Builder" }, { id: "html", label: "HTML Code" }].map(m => (
                  <button key={m.id} onClick={() => setEditForm(f => ({ ...f, mode: m.id }))} style={{
                    padding: "7px 18px", border: "none", cursor: "pointer", fontFamily: "inherit",
                    background: editForm.mode === m.id ? "#4059AD" : "transparent",
                    color: editForm.mode === m.id ? "#FAFAFA" : "rgba(250,250,250,0.4)",
                    fontSize: 11, fontWeight: 400,
                  }}>{m.label}</button>
                ))}
              </div>

              {/* Signature name */}
              <div style={{ marginBottom: 14 }}>
                <label style={fldLbl}>Signature Name</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={{ ...fld, maxWidth: 280 }} />
              </div>

              {editForm.mode === "visual" ? (
                <div>
                  {/* Logo upload */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={fldLbl}>Logo</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      {editForm.logoUrl ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img src={editForm.logoUrl} alt="" style={{ height: 36, borderRadius: 4 }} />
                          <button onClick={() => setEditForm(f => ({ ...f, logoUrl: "" }))} style={{ fontSize: 10, color: "#E53935", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
                        </div>
                      ) : (
                        <label style={{ padding: "8px 16px", borderRadius: 8, border: "1px dashed rgba(217,219,221,0.2)", background: "transparent", color: "rgba(250,250,250,0.4)", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                          <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                          {logoUploading ? "Uploading..." : "Upload Logo"}
                        </label>
                      )}
                    </div>
                    {editForm.logoUrl && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                        <span style={{ fontSize: 10, color: "rgba(250,250,250,0.35)" }}>Position:</span>
                        {[{ id: "top", label: "Above name" }, { id: "bottom", label: "Below signature" }].map(p => (
                          <button key={p.id} onClick={() => setEditForm(f => ({ ...f, logoPosition: p.id }))} style={{
                            padding: "4px 12px", borderRadius: 6, border: "1px solid " + (editForm.logoPosition === p.id ? "#4059AD" : "rgba(217,219,221,0.12)"),
                            background: editForm.logoPosition === p.id ? "rgba(64,89,173,0.1)" : "transparent",
                            color: editForm.logoPosition === p.id ? "#4059AD" : "rgba(250,250,250,0.4)",
                            fontSize: 10, cursor: "pointer", fontFamily: "inherit",
                          }}>{p.label}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Fields grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div><label style={fldLbl}>Job Title</label><input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Sales Director" style={fld} /></div>
                    <div><label style={fldLbl}>Company</label><input value={editForm.company} onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} placeholder="e.g. Acme Corp" style={fld} /></div>
                    <div><label style={fldLbl}>Phone</label><input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 (555) 000-0000" style={fld} /></div>
                    <div><label style={fldLbl}>Website</label><input value={editForm.website} onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))} placeholder="yourcompany.com" style={fld} /></div>
                  </div>

                  {/* Live visual preview */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={fldLbl}>Preview</label>
                    <div style={{ padding: 16, background: "#ffffff", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
                      <div dangerouslySetInnerHTML={{ __html: buildHtmlFromVisual() }} />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {/* HTML editor */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={fldLbl}>HTML Code</label>
                    <textarea value={editForm.html} onChange={e => setEditForm(f => ({ ...f, html: e.target.value }))} rows={10}
                      placeholder="Paste your HTML signature here..."
                      style={{ ...fld, fontFamily: "monospace", fontSize: 11, lineHeight: 1.6, resize: "vertical" }} />
                  </div>

                  {/* HTML preview */}
                  {editForm.html && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={fldLbl}>Preview</label>
                      <div style={{ padding: 16, background: "#ffffff", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
                        <div dangerouslySetInnerHTML={{ __html: editForm.html }} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveEdit} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Save Signature</button>
                <button onClick={cancelEdit} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.5)", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>{sig.name}</span>
                  {sig.is_default && <span style={{ fontSize: 9, fontWeight: 400, color: "#4059AD", background: "rgba(64,89,173,0.1)", padding: "2px 8px", borderRadius: 6 }}>DEFAULT</span>}
                </div>
                {sig.html ? (
                  <div style={{ padding: 12, background: "#ffffff", borderRadius: 6, display: "inline-block" }}>
                    <div dangerouslySetInnerHTML={{ __html: sig.html }} />
                  </div>
                ) : (
                  <pre style={{ fontSize: 12, color: "rgba(250,250,250,0.5)", margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit", lineHeight: 1.6 }}>{sig.content}</pre>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 16 }}>
                {!sig.is_default && <button onClick={() => setDefault(sig.id)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.4)", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Set Default</button>}
                <button onClick={() => startEdit(sig)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                {signatures.length > 1 && <button onClick={() => deleteSig(sig.id)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(139,69,87,0.2)", background: "transparent", color: "#E53935", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Email preview with default signature */}
      {defaultSig && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA", marginBottom: 10 }}>Email Preview</div>
          <div style={{ padding: 24, background: "#ffffff", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
            <div style={{ fontSize: 13, color: "#333", marginBottom: 20, lineHeight: 1.7 }}>
              Hi Sarah,<br/><br/>
              Thanks for your time today. I've attached the proposal we discussed — let me know if you have any questions or need anything adjusted.<br/><br/>
              Looking forward to hearing from you!
            </div>
            <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: 14 }}>
              {defaultSig.html ? (
                <div dangerouslySetInnerHTML={{ __html: defaultSig.html }} />
              ) : (
                <pre style={{ fontSize: 12, color: "#555", margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit", lineHeight: 1.6 }}>{defaultSig.content}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Billing Tab (dynamic usage from real data) ── */
function BillingTab() {
  const store = useStore();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [seatCount, setSeatCount] = useState(null);
  const [seatLoading, setSeatLoading] = useState(false);
  const [seatMsg, setSeatMsg] = useState(null);

  const PLAN_LABELS = {
    annual: "Annual", biannual: "Bi-Annual", monthly: "Monthly", free: "Free",
  };
  const PLAN_PRICES = {
    annual: { amount: 159, label: "$159/seat/month, billed annually" },
    biannual: { amount: 169, label: "$169/seat/month, billed every 6 months" },
    monthly: { amount: 179, label: "$179/seat/month, billed monthly" },
    free: { amount: 0, label: "No active subscription" },
  };
  const STATUS_STYLES = {
    active: { color: "#4CAF50", label: "Active" },
    trialing: { color: "#4059AD", label: "Trial" },
    past_due: { color: "#D4763A", label: "Past Due" },
    canceled: { color: "#E53935", label: "Canceled" },
    suspended: { color: "#E53935", label: "Suspended" },
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch("/api/stripe/billing");
        const data = await resp.json();
        setBilling(data);
        setSeatCount(data.seats || 1);
      } catch (e) {
        console.error("[Billing] Failed to load:", e.message);
      }
      setLoading(false);
    })();
  }, []);

  const updateSeats = async () => {
    if (seatCount === billing?.seats) return;
    setSeatLoading(true);
    setSeatMsg(null);
    try {
      const resp = await apiFetch("/api/stripe/seats", {
        method: "POST",
        body: JSON.stringify({ seats: seatCount }),
      });
      const data = await resp.json();
      if (data.success) {
        setSeatMsg({ type: "success", text: data.message });
        setBilling(prev => ({ ...prev, seats: seatCount }));
      } else {
        setSeatMsg({ type: "error", text: data.error || "Failed to update seats" });
        setSeatCount(billing?.seats || 1);
      }
    } catch (e) {
      setSeatMsg({ type: "error", text: e.message });
      setSeatCount(billing?.seats || 1);
    }
    setSeatLoading(false);
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const resp = await apiFetch("/api/stripe/portal", { method: "POST" });
      const data = await resp.json();
      if (data.url) window.open(data.url, "_blank");
    } catch (e) {
      console.error("[Billing] Portal error:", e.message);
    }
    setPortalLoading(false);
  };

  const fmtDate = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts * 1000);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const fmtCurrency = (cents, currency = "usd") => {
    if (!cents && cents !== 0) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  };

  // Real usage from store
  const [emailCount, setEmailCount] = useState(0);
  const [smsCount, setSmsCount] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const { count: eCount } = await supabase.from("emails").select("id", { count: "exact", head: true });
        const { count: sCount } = await supabase.from("sms_messages").select("id", { count: "exact", head: true });
        setEmailCount(eCount || 0);
        setSmsCount(sCount || 0);
      } catch (e) {}
    })();
  }, []);
  const contactCount = store.contacts?.length || 0;
  const teamCount = store.teamMembers?.length || 0;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 13, color: T.tx3 }}>Loading billing details...</div>
      </div>
    );
  }

  const plan = billing?.plan || "free";
  const planInfo = PLAN_PRICES[plan] || PLAN_PRICES.free;
  const statusInfo = STATUS_STYLES[billing?.plan_status] || STATUS_STYLES.active;
  const seats = billing?.seats || 1;
  const sub = billing?.subscription;
  const pm = billing?.payment_method;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Billing & Plan</h2>
        <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Manage your subscription and view usage</p>
      </div>

      {/* Current Plan */}
      <div style={{ padding: 20, background: T.blue + "08", border: "1px solid " + T.blue + "25", borderRadius: 8, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 4 }}>CURRENT PLAN</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 22, fontWeight: 400, color: T.txt }}>{PLAN_LABELS[plan] || plan}</div>
              <span style={{ fontSize: 10, fontWeight: 400, padding: "3px 8px", borderRadius: 4, background: statusInfo.color + "18", color: statusInfo.color }}>{statusInfo.label}</span>
            </div>
            <div style={{ fontSize: 13, color: T.tx2, marginTop: 4 }}>{planInfo.label}</div>
            <div style={{ fontSize: 12, color: T.tx3, marginTop: 2 }}>{seats} seat{seats !== 1 ? "s" : ""}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            {sub?.current_period_end && (
              <div style={{ fontSize: 11, color: T.tx3, marginBottom: 4 }}>
                {sub.cancel_at_period_end ? "Cancels" : "Next billing"}: {fmtDate(sub.current_period_end)}
              </div>
            )}
            {sub?.trial_end && sub.status === "trialing" && (
              <div style={{ fontSize: 11, color: T.blue, marginBottom: 4 }}>
                Trial ends: {fmtDate(sub.trial_end)}
              </div>
            )}
            {billing?.stripe_customer_id && (
              <button onClick={openPortal} disabled={portalLoading} style={{
                padding: "8px 20px", borderRadius: 8, border: "1px solid " + T.blue,
                background: "transparent", color: T.blue, fontSize: 12, fontWeight: 400,
                cursor: portalLoading ? "wait" : "pointer", opacity: portalLoading ? 0.6 : 1,
              }}>{portalLoading ? "Opening..." : "Manage Subscription"}</button>
            )}
          </div>
        </div>
      </div>

      {/* Seat Management */}
      {billing?.stripe_customer_id && plan !== "free" && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Manage Seats</div>
          <div style={{ padding: 20, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 12, color: T.tx2, marginBottom: 8 }}>
                  {teamCount} of {seats} seat{seats !== 1 ? "s" : ""} in use
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => setSeatCount(Math.max(1, (seatCount || 1) - 1))} disabled={seatLoading || seatCount <= 1} style={{
                    width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
                    background: "transparent", color: T.txt, fontSize: 16, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    opacity: seatLoading || seatCount <= 1 ? 0.3 : 1,
                  }}>−</button>
                  <span style={{ fontSize: 22, fontWeight: 400, color: T.txt, minWidth: 40, textAlign: "center" }}>{seatCount}</span>
                  <button onClick={() => setSeatCount(Math.min(50, (seatCount || 1) + 1))} disabled={seatLoading || seatCount >= 50} style={{
                    width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
                    background: "transparent", color: T.txt, fontSize: 16, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    opacity: seatLoading || seatCount >= 50 ? 0.3 : 1,
                  }}>+</button>
                  <span style={{ fontSize: 12, color: T.tx3 }}>seats</span>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                {seatCount !== seats && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: T.tx2 }}>
                      {seatCount > seats ? "+" : ""}{seatCount - seats} seat{Math.abs(seatCount - seats) !== 1 ? "s" : ""}
                    </div>
                    <div style={{ fontSize: 11, color: T.tx3 }}>
                      {seatCount > seats ? "Prorated charge applies" : "Prorated credit applies"}
                    </div>
                  </div>
                )}
                <button onClick={updateSeats} disabled={seatLoading || seatCount === seats} style={{
                  padding: "8px 20px", borderRadius: 8, border: "none",
                  background: seatCount !== seats ? T.blue : "transparent",
                  color: seatCount !== seats ? "#fff" : T.tx3,
                  fontSize: 12, fontWeight: 400, cursor: seatCount !== seats ? "pointer" : "default",
                  opacity: seatLoading ? 0.6 : 1,
                }}>{seatLoading ? "Updating..." : seatCount !== seats ? "Update Seats" : "No changes"}</button>
              </div>
            </div>
            {seatMsg && (
              <div style={{
                marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                background: seatMsg.type === "success" ? "#4CAF5012" : "#E5393512",
                color: seatMsg.type === "success" ? "#4CAF50" : "#E53935",
                border: "1px solid " + (seatMsg.type === "success" ? "#4CAF5020" : "#E5393520"),
              }}>{seatMsg.text}</div>
            )}
          </div>
        </div>
      )}

      {/* Payment Method */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Payment Method</div>
        <div style={{ padding: 16, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
          {pm ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 400, color: T.txt, textTransform: "capitalize" }}>{pm.brand}</span>
                <span style={{ fontSize: 13, color: T.tx2 }}>•••• {pm.last4}</span>
                <span style={{ fontSize: 11, color: T.tx3 }}>Expires {String(pm.exp_month).padStart(2, "0")}/{pm.exp_year}</span>
              </div>
              <button onClick={openPortal} style={{
                padding: "6px 14px", borderRadius: 8, border: "1px solid " + T.bdr,
                background: "transparent", color: T.tx2, fontSize: 11, fontWeight: 400, cursor: "pointer",
              }}>Update</button>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: T.tx3 }}>No payment method on file</div>
              {billing?.stripe_customer_id && (
                <button onClick={openPortal} style={{
                  padding: "6px 14px", borderRadius: 8, border: "1px solid " + T.blue,
                  background: "transparent", color: T.blue, fontSize: 11, fontWeight: 400, cursor: "pointer",
                }}>Add Payment Method</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Usage */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Usage</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
          {[
            { label: "Seats", value: teamCount + " / " + seats },
            { label: "Contacts", value: contactCount.toLocaleString() },
            { label: "Emails Sent", value: emailCount.toLocaleString() },
            { label: "SMS Sent", value: smsCount.toLocaleString() },
          ].map((u, i) => (
            <div key={i} style={{ padding: 16, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: T.tx3, marginBottom: 6 }}>{u.label}</div>
              <div style={{ fontSize: 18, fontWeight: 400, color: T.txt }}>{u.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Invoices */}
      {billing?.invoices?.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Recent Invoices</div>
          <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, overflow: "hidden" }}>
            {billing.invoices.map((inv, i) => (
              <div key={inv.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "12px 16px",
                borderBottom: i < billing.invoices.length - 1 ? "1px solid rgba(217,219,221,0.08)" : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 12, color: T.tx2 }}>{inv.number || "—"}</span>
                  <span style={{ fontSize: 12, color: T.txt }}>{fmtCurrency(inv.amount_paid || inv.amount_due, inv.currency)}</span>
                  <span style={{
                    fontSize: 10, padding: "2px 6px", borderRadius: 4,
                    background: inv.status === "paid" ? "#4CAF5018" : inv.status === "open" ? "#D4763A18" : "transparent",
                    color: inv.status === "paid" ? "#4CAF50" : inv.status === "open" ? "#D4763A" : T.tx3,
                  }}>{inv.status}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, color: T.tx3 }}>{fmtDate(inv.created)}</span>
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: 11, color: T.blue, textDecoration: "none",
                    }}>View</a>
                  )}
                  {inv.pdf && (
                    <a href={inv.pdf} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: 11, color: T.tx2, textDecoration: "none",
                    }}>PDF</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── A2P 10DLC Compliance Wizard ── */
function A2PComplianceWizard({ orgId, inputS, crd, numbers }) {
  const [step, setStep] = useState(0); // 0=overview, 1=business, 2=contact, 3=use-case, 4=opt-in, 5=review, 6=submitted
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [a2pData, setA2pData] = useState(null); // existing registration data

  // Form state
  const [form, setForm] = useState({
    // Step 1: Business Info
    legalName: "", dba: "", ein: "", website: "",
    businessType: "private_profit", entityType: "LLC",
    street: "", city: "", state: "", zip: "", country: "US",
    stockExchange: "", stockTicker: "",
    // Step 2: Authorized Contact
    contactFirstName: "", contactLastName: "",
    contactEmail: "", contactPhone: "", contactTitle: "",
    // Step 3: Use Case & Campaign
    useCase: "MIXED",
    useCaseDescription: "Sales outreach including follow-up to calls, meeting confirmations, and appointment reminders.",
    messageVolume: "1000-10000",
    // Step 4: Opt-in & Sample Messages
    optInMethod: "verbal",
    optInDescription: "Contacts opt in verbally during sales calls or by responding to initial outreach. All contacts are existing business relationships.",
    sampleMessage1: "Hi {{first_name}}, this is {{rep_name}} from {{company}}. Following up on our call — would Thursday at 2pm work to discuss next steps?",
    sampleMessage2: "Hi {{first_name}}, just sent over the proposal we discussed. Let me know if you have any questions. - {{rep_name}}",
    helpKeywords: "HELP, INFO",
    helpResponse: "Reply HELP for assistance. Contact support@yourcompany.com or call (555) 000-0000.",
    optOutKeywords: "STOP, UNSUBSCRIBE, CANCEL, END, QUIT",
    optOutResponse: "You have been unsubscribed and will not receive further messages. Reply START to re-subscribe.",
  });

  const upd = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Load existing status — try sync from Twilio first
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    // First check our database
    apiFetch("/api/twilio/a2p", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "status", orgId }),
    }).then(r => r.json()).then(data => {
      if (data.status && data.status !== "not_started") {
        setA2pData(data);
        setStep(6);
        setLoading(false);
      } else {
        // Nothing in DB — try syncing from Twilio account
        apiFetch("/api/twilio/a2p", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "sync", orgId }),
        }).then(r => r.json()).then(syncData => {
          if (syncData.synced && syncData.status !== "not_started") {
            setA2pData(syncData);
            setStep(6);
          }
          setLoading(false);
        }).catch(() => setLoading(false));
      }
    }).catch(() => setLoading(false));
  }, [orgId]);

  async function handleSubmit() {
    setSubmitting(true); setError("");
    try {
      const resp = await apiFetch("/api/twilio/a2p", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "register", orgId,
          brandName: form.legalName, brandWebsite: form.website, ein: form.ein,
          useCase: form.useCaseDescription,
          sampleMessages: form.sampleMessage1 + "\n\n" + form.sampleMessage2,
          contactEmail: form.contactEmail, contactPhone: form.contactPhone,
          businessType: form.businessType, entityType: form.entityType,
          street: form.street, city: form.city, state: form.state, zip: form.zip,
          optInMethod: form.optInMethod, optInDescription: form.optInDescription,
          messageVolume: form.messageVolume,
          helpResponse: form.helpResponse, optOutResponse: form.optOutResponse,
        }),
      });
      const data = await resp.json();
      if (data.success || data.brandSid) {
        setA2pData(data);
        setStep(6);
      } else {
        setError(data.error || "Submission failed");
      }
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  }

  const stepsConfig = [
    { label: "Overview", icon: "📋" },
    { label: "Business", icon: "🏢" },
    { label: "Contact", icon: "👤" },
    { label: "Campaign", icon: "4" },
    { label: "Opt-in", icon: "5" },
    { label: "Review", icon: "🔍" },
    { label: "Status", icon: "📊" },
  ];

  const labelS = { fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 };
  const reqS = { color: "#E53935", fontSize: 11 };

  if (loading) return <div style={{ ...crd, marginTop: 16, textAlign: "center", color: T.tx3 }}>Loading compliance status...</div>;

  return (
    <div style={{ ...crd, marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 4 }}>A2P 10DLC Compliance</div>
      <div style={{ fontSize: 11, color: T.tx3, marginBottom: 16 }}>Required by US carriers for business SMS. Without registration, messages may be filtered or blocked.</div>

      {/* Progress steps */}
      {step < 6 && (
        <div style={{ display: "flex", gap: 2, marginBottom: 20 }}>
          {stepsConfig.slice(0, 6).map((s, i) => (
            <div key={i} onClick={() => i <= step && setStep(i)} style={{
              flex: 1, padding: "8px 4px", textAlign: "center", borderRadius: 8, cursor: i <= step ? "pointer" : "default",
              background: i === step ? T.blue + "12" : i < step ? "#2E8B57" + "10" : T.sf,
              border: "1px solid " + (i === step ? T.blue + "30" : i < step ? "#2E8B57" + "20" : T.bdr),
            }}>
              <div style={{ fontSize: 14 }}>{i < step ? "✓" : s.icon}</div>
              <div style={{ fontSize: 9, fontWeight: 400, color: i === step ? "#FAFAFA" : i < step ? "#FAFAFA" : T.tx3, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#E5393510", color: "#E53935", fontSize: 12, marginBottom: 16, border: "1px solid #E5393525" }}>{error}</div>}

      {/* Step 0: Overview */}
      {step === 0 && (
        <div>
          <div style={{ padding: "14px 16px", borderRadius: 8, background: "#D4763A" + "08", border: "1px solid " + "#D4763A" + "20", marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 400, color: "#D4763A", marginBottom: 8 }}>Why is this required?</div>
            <div style={{ fontSize: 11, color: T.tx2, lineHeight: 1.8 }}>
              Since 2023, US carriers (AT&T, T-Mobile, Verizon) require all businesses sending Application-to-Person (A2P) SMS through local 10-digit numbers to register. This applies to sales texts, appointment reminders, and follow-ups. Without registration your messages will be throttled to ~1 msg/sec and many will be silently filtered.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            {[
              { title: "What you'll need", items: ["Legal business name & EIN", "Business address", "Authorized contact person", "Website URL"] },
              { title: "What happens", items: ["Brand registered with TCR", "Campaign reviewed by carriers", "Numbers linked to campaign", "Takes 1-5 business days"] },
              { title: "Costs", items: ["Brand registration: $4 one-time", "Campaign: $1.50/mo", "Higher throughput: 75+ msg/sec", "Better deliverability"] },
            ].map((col, i) => (
              <div key={i} style={{ padding: "12px 14px", borderRadius: 8, background: T.sf }}>
                <div style={{ fontSize: 11, fontWeight: 400, color: T.txt, marginBottom: 8 }}>{col.title}</div>
                {col.items.map((item, j) => <div key={j} style={{ fontSize: 10, color: T.tx2, lineHeight: 2 }}>• {item}</div>)}
              </div>
            ))}
          </div>
          <Btn primary onClick={() => setStep(1)} style={{ padding: "10px 28px" }}>Start Registration →</Btn>
          <button onClick={() => {
            setLoading(true);
            apiFetch("/api/twilio/a2p", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "sync", orgId }),
            }).then(r => r.json()).then(data => {
              if (data.synced && data.status !== "not_started") {
                setA2pData(data);
                setStep(6);
              } else {
                setError("No existing A2P registration found on your Twilio account.");
              }
              setLoading(false);
            }).catch(() => setLoading(false));
          }} style={{
            marginLeft: 12, background: "none", border: "1px solid " + T.bdr, borderRadius: 8,
            padding: "10px 20px", color: T.tx2, fontSize: 12, cursor: "pointer",
          }}>Already registered? Sync from Twilio</button>
        </div>
      )}

      {/* Step 1: Business Info */}
      {step === 1 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Business Information</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><label style={labelS}>Legal Business Name <span style={reqS}>*</span></label><input value={form.legalName} onChange={e => upd("legalName", e.target.value)} placeholder="Acme Sales Corp" style={inputS} /></div>
            <div><label style={labelS}>DBA / Trade Name</label><input value={form.dba} onChange={e => upd("dba", e.target.value)} placeholder="(if different)" style={inputS} /></div>
            <div><label style={labelS}>EIN / Tax ID <span style={reqS}>*</span></label><input value={form.ein} onChange={e => upd("ein", e.target.value)} placeholder="XX-XXXXXXX" style={inputS} /></div>
            <div><label style={labelS}>Website <span style={reqS}>*</span></label><input value={form.website} onChange={e => upd("website", e.target.value)} placeholder="https://acme.com" style={inputS} /></div>
            <div>
              <label style={labelS}>Business Type <span style={reqS}>*</span></label>
              <Select value={form.businessType} onChange={e => upd("businessType", e.target.value)} placeholder="— Select —" options={[{value:"private_profit",label:"Private / For Profit"},{value:"public_profit",label:"Publicly Traded"},{value:"non_profit",label:"Non-Profit"},{value:"government",label:"Government"},{value:"sole_proprietor",label:"Sole Proprietor"}]} />
            </div>
            <div>
              <label style={labelS}>Entity Type</label>
              <Select value={form.entityType} onChange={e => upd("entityType", e.target.value)} placeholder="— Select —" options={[{value:"LLC",label:"LLC"},{value:"Corporation",label:"Corporation"},{value:"Partnership",label:"Partnership"},{value:"Sole Proprietorship",label:"Sole Proprietorship"},{value:"Co-operative",label:"Co-operative"}]} />
            </div>
          </div>
          {form.businessType === "public_profit" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><label style={labelS}>Stock Exchange</label><input value={form.stockExchange} onChange={e => upd("stockExchange", e.target.value)} placeholder="NYSE, NASDAQ" style={inputS} /></div>
              <div><label style={labelS}>Stock Ticker</label><input value={form.stockTicker} onChange={e => upd("stockTicker", e.target.value)} placeholder="ACME" style={inputS} /></div>
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 8, marginTop: 8 }}>Registered Business Address</div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><label style={labelS}>Street Address <span style={reqS}>*</span></label><input value={form.street} onChange={e => upd("street", e.target.value)} placeholder="123 Main St, Suite 400" style={inputS} /></div>
            <div><label style={labelS}>City <span style={reqS}>*</span></label><input value={form.city} onChange={e => upd("city", e.target.value)} placeholder="San Francisco" style={inputS} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div><label style={labelS}>State <span style={reqS}>*</span></label><input value={form.state} onChange={e => upd("state", e.target.value)} placeholder="CA" maxLength={2} style={inputS} /></div>
            <div><label style={labelS}>ZIP Code <span style={reqS}>*</span></label><input value={form.zip} onChange={e => upd("zip", e.target.value)} placeholder="94105" style={inputS} /></div>
            <div><label style={labelS}>Country</label><Select value={form.country} onChange={e => upd("country", e.target.value)} style={inputS}><option value="US">United States</option><option value="CA">Canada</option></Select></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setStep(0)}>← Back</Btn>
            <Btn primary onClick={() => { if (!form.legalName || !form.ein || !form.website || !form.street || !form.city || !form.state || !form.zip) { setError("Please fill all required fields"); return; } setError(""); setStep(2); }}>Continue →</Btn>
          </div>
        </div>
      )}

      {/* Step 2: Authorized Contact */}
      {step === 2 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Authorized Representative</div>
          <div style={{ fontSize: 11, color: T.tx3, marginBottom: 12 }}>This person must be authorized to register the business for messaging and may be contacted for verification.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div><label style={labelS}>First Name <span style={reqS}>*</span></label><input value={form.contactFirstName} onChange={e => upd("contactFirstName", e.target.value)} placeholder="John" style={inputS} /></div>
            <div><label style={labelS}>Last Name <span style={reqS}>*</span></label><input value={form.contactLastName} onChange={e => upd("contactLastName", e.target.value)} placeholder="Smith" style={inputS} /></div>
            <div><label style={labelS}>Email <span style={reqS}>*</span></label><input value={form.contactEmail} onChange={e => upd("contactEmail", e.target.value)} placeholder="john@acme.com" style={inputS} /></div>
            <div><label style={labelS}>Phone <span style={reqS}>*</span></label><input value={form.contactPhone} onChange={e => upd("contactPhone", e.target.value)} placeholder="+1 (555) 000-0000" style={inputS} /></div>
            <div><label style={labelS}>Job Title <span style={reqS}>*</span></label><input value={form.contactTitle} onChange={e => upd("contactTitle", e.target.value)} placeholder="VP of Sales" style={inputS} /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setStep(1)}>← Back</Btn>
            <Btn primary onClick={() => { if (!form.contactFirstName || !form.contactLastName || !form.contactEmail || !form.contactPhone || !form.contactTitle) { setError("Please fill all required fields"); return; } setError(""); setStep(3); }}>Continue →</Btn>
          </div>
        </div>
      )}

      {/* Step 3: Campaign / Use Case */}
      {step === 3 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Campaign Details</div>
          <div style={{ fontSize: 11, color: T.tx3, marginBottom: 12 }}>Describe how your team will use SMS. Carriers review this to ensure compliance.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelS}>Primary Use Case <span style={reqS}>*</span></label>
              <Select value={form.useCase} onChange={e => upd("useCase", e.target.value)} placeholder="— Select —" options={[{value:"MIXED",label:"Mixed (Sales + Notifications)"},{value:"MARKETING",label:"Marketing / Promotions"},{value:"ACCOUNT_NOTIFICATION",label:"Account Notifications"},{value:"CUSTOMER_CARE",label:"Customer Care"},{value:"DELIVERY_NOTIFICATION",label:"Delivery Notifications"},{value:"FRAUD_ALERT",label:"Fraud Alerts"},{value:"HIGHER_EDUCATION",label:"Higher Education"},{value:"LOW_VOLUME",label:"Low Volume Mixed"},{value:"POLLING_VOTING",label:"Polling & Voting"},{value:"PUBLIC_SERVICE_ANNOUNCEMENT",label:"Public Service"},{value:"SECURITY_ALERT",label:"Security Alerts"}]} />
            </div>
            <div>
              <label style={labelS}>Monthly Message Volume</label>
              <Select value={form.messageVolume} onChange={e => upd("messageVolume", e.target.value)} placeholder="— Select —" options={[{value:"1-100",label:"Under 100"},{value:"100-1000",label:"100 – 1,000"},{value:"1000-10000",label:"1,000 – 10,000"},{value:"10000-100000",label:"10,000 – 100,000"},{value:"100000+",label:"100,000+"}]} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelS}>Use Case Description <span style={reqS}>*</span></label>
            <textarea value={form.useCaseDescription} onChange={e => upd("useCaseDescription", e.target.value)} rows={3} style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
            <div style={{ fontSize: 9, color: T.tx3, marginTop: 4 }}>Explain in detail what types of messages your team sends and why. Be specific — vague descriptions get rejected.</div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 8 }}>Sample Messages</div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelS}>Sample Message 1 <span style={reqS}>*</span></label>
            <textarea value={form.sampleMessage1} onChange={e => upd("sampleMessage1", e.target.value)} rows={2} style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelS}>Sample Message 2</label>
            <textarea value={form.sampleMessage2} onChange={e => upd("sampleMessage2", e.target.value)} rows={2} style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setStep(2)}>← Back</Btn>
            <Btn primary onClick={() => { if (!form.useCaseDescription || !form.sampleMessage1) { setError("Description and at least one sample message required"); return; } setError(""); setStep(4); }}>Continue →</Btn>
          </div>
        </div>
      )}

      {/* Step 4: Opt-in / Opt-out */}
      {step === 4 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Consent & Opt-out</div>
          <div style={{ fontSize: 11, color: T.tx3, marginBottom: 12 }}>Carriers require that recipients have consented to receive messages and can easily opt out.</div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelS}>How do contacts opt in? <span style={reqS}>*</span></label>
            <Select value={form.optInMethod} onChange={e => upd("optInMethod", e.target.value)} placeholder="— Select —" options={[{value:"verbal",label:"Verbal consent during phone call"},{value:"web_form",label:"Website form / landing page"},{value:"paper",label:"Paper form / in-person"},{value:"text",label:"Text message keyword (e.g. reply YES)"},{value:"mixed",label:"Multiple methods"}]} />
            <textarea value={form.optInDescription} onChange={e => upd("optInDescription", e.target.value)} rows={3} style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} placeholder="Describe how and when contacts give consent..." />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelS}>HELP Keywords</label>
              <input value={form.helpKeywords} onChange={e => upd("helpKeywords", e.target.value)} style={inputS} />
            </div>
            <div>
              <label style={labelS}>STOP Keywords</label>
              <input value={form.optOutKeywords} onChange={e => upd("optOutKeywords", e.target.value)} style={inputS} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelS}>HELP Response Message</label>
            <textarea value={form.helpResponse} onChange={e => upd("helpResponse", e.target.value)} rows={2} style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelS}>STOP / Opt-out Response Message</label>
            <textarea value={form.optOutResponse} onChange={e => upd("optOutResponse", e.target.value)} rows={2} style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setStep(3)}>← Back</Btn>
            <Btn primary onClick={() => { setError(""); setStep(5); }}>Review →</Btn>
          </div>
        </div>
      )}

      {/* Step 5: Review */}
      {step === 5 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Review & Submit</div>
          {[
            { label: "Business", items: [["Legal Name", form.legalName], ["EIN", form.ein], ["Website", form.website], ["Type", form.businessType + " / " + form.entityType], ["Address", form.street + ", " + form.city + " " + form.state + " " + form.zip]] },
            { label: "Contact", items: [["Name", form.contactFirstName + " " + form.contactLastName], ["Email", form.contactEmail], ["Phone", form.contactPhone], ["Title", form.contactTitle]] },
            { label: "Campaign", items: [["Use Case", form.useCase], ["Volume", form.messageVolume + " msgs/mo"], ["Description", form.useCaseDescription.substring(0, 100) + "..."]] },
            { label: "Compliance", items: [["Opt-in Method", form.optInMethod], ["HELP Keywords", form.helpKeywords], ["STOP Keywords", form.optOutKeywords]] },
            { label: "Numbers", items: [["Numbers to register", (numbers || []).filter(n => n.number_type !== "local_presence").length + " direct line(s)"]] },
          ].map((section, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 4 }}>{section.label.toUpperCase()}</div>
              <div style={{ padding: "10px 14px", borderRadius: 8, background: T.sf }}>
                {section.items.map(([k, v], j) => (
                  <div key={j} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, lineHeight: 2 }}>
                    <span style={{ color: T.tx3 }}>{k}</span>
                    <span style={{ color: T.txt, fontWeight: 400, textAlign: "right", maxWidth: "60%" }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ padding: "10px 14px", borderRadius: 8, background: T.blue + "06", border: "1px solid " + T.blue + "15", marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: T.tx2, lineHeight: 1.8 }}>
              By submitting, you confirm that: the information above is accurate, you are authorized to register this business, all message recipients have given consent, and your messaging complies with CTIA guidelines and TCPA regulations. One-time fee: $4.00. Monthly campaign fee: $1.50/mo.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setStep(4)}>← Back</Btn>
            <Btn primary onClick={handleSubmit} style={{ padding: "10px 28px" }}>
              {submitting ? "Submitting to carriers..." : "Submit A2P Registration"}
            </Btn>
          </div>
        </div>
      )}

      {/* Step 6: Submitted / Status */}
      {step === 6 && (
        <div>
          {(!a2pData || a2pData.status === "pending") && (
            <div style={{ padding: "24px 20px", borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.10)", textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: "rgba(212,118,58,0.1)", border: "1px solid rgba(212,118,58,0.2)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                <Icon name="clock" size={18} color="#D4763A" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Pending Carrier Review</div>
              <div style={{ fontSize: 11, color: T.tx2, lineHeight: 1.6, marginBottom: 16, maxWidth: 480, margin: "0 auto 16px" }}>
                Your A2P 10DLC registration has been submitted and is under carrier review. This typically takes 1-5 business days.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, textAlign: "center", marginBottom: 12 }}>
                <div style={{ padding: "10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)" }}>
                  <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Brand</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#D4763A" }}>{a2pData?.brandStatus || "pending"}</div>
                </div>
                <div style={{ padding: "10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)" }}>
                  <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Campaign</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#D4763A" }}>{a2pData?.campaignStatus || "pending"}</div>
                </div>
                <div style={{ padding: "10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)" }}>
                  <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Submitted</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: T.txt }}>{a2pData?.submittedAt ? cfDate(a2pData.submittedAt) : "—"}</div>
                </div>
              </div>
              <div style={{ fontSize: 10, color: T.tx3 }}>SMS is functional during review but throughput is limited (~1 msg/sec).</div>
            </div>
          )}
          {a2pData?.status === "approved" && (
            <div style={{ padding: "24px 20px", borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(46,139,87,0.2)", textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: "rgba(46,139,87,0.1)", border: "1px solid rgba(46,139,87,0.2)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                <Icon name="check" size={18} color="#2E8B57" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>A2P Registration Approved</div>
              <div style={{ fontSize: 11, color: T.tx2, lineHeight: 1.6 }}>
                Your brand and campaign are approved. Full SMS throughput and deliverability across all US carriers.
              </div>
            </div>
          )}
          {(a2pData?.status === "rejected" || a2pData?.status === "failed") && (
            <div style={{ padding: "24px 20px", borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(139,69,87,0.2)", textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: "rgba(139,69,87,0.1)", border: "1px solid rgba(139,69,87,0.2)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                <Icon name="close" size={18} color="#E53935" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Registration Not Approved</div>
              <div style={{ fontSize: 11, color: T.tx2, lineHeight: 1.6, marginBottom: 16, maxWidth: 480, margin: "0 auto 16px" }}>
                {a2pData?.rejectionReason || "Common reasons: mismatched business info, vague use case, or missing opt-in details. You can resubmit with corrected information."}
              </div>
              <button onClick={() => setStep(0)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Restart Registration</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Local Presence Pool Section ── */
function LocalPresenceSection({ orgId, inputS, crd }) {
  const [expanded, setExpanded] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [buying, setBuying] = useState(false);
  const [buyResult, setBuyResult] = useState(null);
  const [stateFilter, setStateFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  async function loadCoverage() {
    setLoadingCoverage(true);
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "coverage", orgId }),
      });
      const data = await resp.json();
      if (data.success) setCoverage(data);
    } catch (e) { console.warn("Coverage load failed:", e); }
    setLoadingCoverage(false);
  }

  useEffect(() => {
    if (expanded && !coverage) loadCoverage();
  }, [expanded]);

  function toggleCode(ac) {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(ac)) next.delete(ac); else next.add(ac);
      return next;
    });
  }

  function selectState(state) {
    if (!coverage) return;
    const stateCodes = coverage.uncovered.filter(c => c.state === state).map(c => c.areaCode);
    setSelectedCodes(prev => {
      const next = new Set(prev);
      const allSelected = stateCodes.every(ac => next.has(ac));
      stateCodes.forEach(ac => allSelected ? next.delete(ac) : next.add(ac));
      return next;
    });
  }

  async function handleBulkBuy() {
    if (selectedCodes.size === 0) return;
    if (!window.confirm(`Purchase ${selectedCodes.size} local presence numbers? This will cost $${(selectedCodes.size * 1.75).toFixed(2)}/mo.`)) return;
    setBuying(true); setBuyResult(null);
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "bulk_purchase", orgId, areaCodes: Array.from(selectedCodes) }),
      });
      const data = await resp.json();
      setBuyResult(data);
      setSelectedCodes(new Set());
      loadCoverage(); // Refresh
    } catch (e) { setBuyResult({ error: e.message }); }
    setBuying(false);
  }

  // Group uncovered by state
  const states = coverage ? [...new Set(coverage.uncovered.map(c => c.state))].sort() : [];
  const filteredUncovered = coverage ? coverage.uncovered.filter(c => {
    if (stateFilter && c.state !== stateFilter) return false;
    if (searchFilter && !c.city.toLowerCase().includes(searchFilter.toLowerCase()) && !c.areaCode.includes(searchFilter)) return false;
    return true;
  }) : [];

  return (
    <div style={{ ...crd, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setExpanded(!expanded)}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>Local Presence Pool</div>
          <div style={{ fontSize: 11, color: T.tx3 }}>Auto-match caller ID to prospect's area code for higher pickup rates</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {coverage && <span style={{ fontSize: 10, color: "#FAFAFA", fontWeight: 400 }}>{coverage.covered.length} area codes covered</span>}
          <span style={{ color: T.tx3, fontSize: 14 }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16 }}>
          {loadingCoverage ? (
            <div style={{ padding: 20, textAlign: "center", color: T.tx3, fontSize: 12 }}>Loading coverage map...</div>
          ) : coverage ? (
            <>
              {/* Coverage stats */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, padding: "12px 16px", borderRadius: 8, background: "#2E8B57" + "08", border: "1px solid " + "#2E8B57" + "20", textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: "#FAFAFA" }}>{coverage.covered.length}</div>
                  <div style={{ fontSize: 10, color: T.tx3 }}>Area Codes Covered</div>
                </div>
                <div style={{ flex: 1, padding: "12px 16px", borderRadius: 8, background: "#D4763A" + "08", border: "1px solid " + "#D4763A" + "20", textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: "#D4763A" }}>{coverage.uncovered.length}</div>
                  <div style={{ fontSize: 10, color: T.tx3 }}>Uncovered</div>
                </div>
                <div style={{ flex: 1, padding: "12px 16px", borderRadius: 8, background: T.blue + "08", border: "1px solid " + T.blue + "20", textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: "#FAFAFA" }}>{coverage.totalCodes}</div>
                  <div style={{ fontSize: 10, color: T.tx3 }}>Total US Codes</div>
                </div>
              </div>

              {/* Covered numbers */}
              {coverage.covered.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, marginBottom: 6, letterSpacing: ".06em" }}>COVERED AREA CODES</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {coverage.covered.map(c => (
                      <span key={c.areaCode} style={{
                        padding: "3px 8px", borderRadius: 8, background: "rgba(255,255,255,0.06)", color: "#FAFAFA", border: "1px solid rgba(217,219,221,0.12)",
                        fontSize: 10, fontWeight: 400, cursor: "default",
                      }}>{c.areaCode} {c.city}, {c.state}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Buy more */}
              <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, marginBottom: 8, letterSpacing: ".06em" }}>ADD COVERAGE</div>

              {/* Filters */}
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <Select value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ ...inputS, width: 120, padding: "6px 8px", fontSize: 11 }}>
                  <option value="">All States</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
                <input value={searchFilter} onChange={e => setSearchFilter(e.target.value)} placeholder="Search city or area code..."
                  style={{ ...inputS, flex: 1, padding: "6px 8px", fontSize: 11 }} />
                {stateFilter && (
                  <button onClick={() => selectState(stateFilter)} style={{
                    padding: "6px 12px", borderRadius: 8, border: "1px solid " + T.blue, background: T.blue + "08",
                    color: "#FAFAFA", fontSize: 10, fontWeight: 400, cursor: "pointer", whiteSpace: "nowrap",
                  }}>Select All {stateFilter}</button>
                )}
              </div>

              {/* Area code grid */}
              <div style={{ maxHeight: 250, overflow: "auto", borderRadius: 8, border: "1px solid " + T.bdr, marginBottom: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 0 }}>
                  {filteredUncovered.map(c => (
                    <div key={c.areaCode} onClick={() => toggleCode(c.areaCode)} style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", cursor: "pointer",
                      background: selectedCodes.has(c.areaCode) ? T.blue + "10" : "transparent",
                      borderBottom: "1px solid " + T.bdr,
                    }}>
                      <Checkbox checked={selectedCodes.has(c.areaCode)} onChange={() => {}} />
                      <span style={{ fontSize: 12, fontWeight: 400, color: T.txt, fontFamily: "inherit", width: 30 }}>{c.areaCode}</span>
                      <span style={{ fontSize: 10, color: T.tx2 }}>{c.city}, {c.state}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buy button */}
              {selectedCodes.size > 0 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 8, background: T.blue + "08", border: "1px solid " + T.blue + "20" }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{selectedCodes.size} numbers selected</span>
                    <span style={{ fontSize: 11, color: T.tx3, marginLeft: 8 }}>${(selectedCodes.size * 1.75).toFixed(2)}/mo</span>
                  </div>
                  <button onClick={handleBulkBuy} disabled={buying} style={{
                    padding: "8px 20px", borderRadius: 8, border: "none",
                    background: buying ? T.tx3 : T.blue, color: "#fff",
                    fontSize: 12, fontWeight: 400, cursor: buying ? "wait" : "pointer",
                  }}>{buying ? "Purchasing..." : "Buy Selected Numbers"}</button>
                </div>
              )}

              {/* Results */}
              {buyResult && (
                <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: buyResult.error ? "#E53935" + "10" : "#2E8B57" + "10", border: "1px solid " + (buyResult.error ? "#E53935" : "#2E8B57") + "25" }}>
                  {buyResult.error ? (
                    <div style={{ fontSize: 12, color: "#E53935" }}>{buyResult.error}</div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>✓ {buyResult.purchased?.length || 0} numbers purchased</div>
                      {buyResult.errors?.length > 0 && (
                        <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>
                          {buyResult.errors.length} failed: {buyResult.errors.map(e => e.areaCode).join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: 10, color: T.tx3, marginTop: 10 }}>
                Local presence numbers: $1.75/mo each • Used only for outbound caller ID • Auto-matched by proximity to prospect
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

/* ── Admin-Only: Connect Existing Twilio Account ── */
function AdminTwilioConnect({ orgId, inputS, onConnected, setError, setSuccess }) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState({
    accountSid: "", authToken: "", apiKey: "", apiSecret: "", twimlAppSid: "", phoneNumber: "",
  });
  const upd = (k, v) => setCreds(prev => ({ ...prev, [k]: v }));

  async function handleConnect() {
    if (!creds.accountSid || !creds.authToken || !creds.apiKey || !creds.apiSecret || !creds.twimlAppSid) {
      setError("All fields except phone number are required");
      return;
    }
    setSaving(true); setError("");
    try {
      const resp = await apiFetch("/api/twilio/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId, ...creds }),
      });
      const data = await resp.json();
      if (data.success) {
        setSuccess("Twilio account connected! " + (data.message || ""));
        setShowForm(false);
        onConnected();
      } else {
        setError(data.error || "Connection failed");
      }
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  if (!showForm) {
    return (
      <div style={{ borderTop: "1px solid " + T.bdr, paddingTop: 16, marginTop: 8 }}>
        <button onClick={() => setShowForm(true)} style={{
          display: "block", margin: "0 auto", background: "none", border: "none",
          color: T.tx3, fontSize: 11, cursor: "pointer", textDecoration: "underline",
        }}>Admin: Connect existing Twilio account</button>
      </div>
    );
  }

  const labelS = { fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 };

  return (
    <div style={{ borderTop: "1px solid " + T.bdr, paddingTop: 16, marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Connect Existing Twilio Account</div>
      <div style={{ fontSize: 10, color: T.tx3, marginBottom: 12 }}>Admin only — paste your Twilio credentials to connect an existing account.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div>
          <label style={labelS}>Account SID *</label>
          <input value={creds.accountSid} onChange={e => upd("accountSid", e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={{ ...inputS, fontFamily: "inherit", fontSize: 10 }} />
        </div>
        <div>
          <label style={labelS}>Auth Token *</label>
          <input value={creds.authToken} onChange={e => upd("authToken", e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" type="password" style={{ ...inputS, fontFamily: "inherit", fontSize: 10 }} />
        </div>
        <div>
          <label style={labelS}>API Key SID *</label>
          <input value={creds.apiKey} onChange={e => upd("apiKey", e.target.value)} placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={{ ...inputS, fontFamily: "inherit", fontSize: 10 }} />
        </div>
        <div>
          <label style={labelS}>API Secret *</label>
          <input value={creds.apiSecret} onChange={e => upd("apiSecret", e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" type="password" style={{ ...inputS, fontFamily: "inherit", fontSize: 10 }} />
        </div>
        <div>
          <label style={labelS}>TwiML App SID *</label>
          <input value={creds.twimlAppSid} onChange={e => upd("twimlAppSid", e.target.value)} placeholder="APxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={{ ...inputS, fontFamily: "inherit", fontSize: 10 }} />
        </div>
        <div>
          <label style={labelS}>Phone Number</label>
          <input value={creds.phoneNumber} onChange={e => upd("phoneNumber", e.target.value)} placeholder="+13135551234" style={{ ...inputS, fontFamily: "inherit", fontSize: 10 }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn primary onClick={handleConnect}>{saving ? "Connecting..." : "Connect Account"}</Btn>
        <Btn onClick={() => setShowForm(false)}>Cancel</Btn>
      </div>
    </div>
  );
}

/* ── Phone Numbers & Compliance Tab ── */
function PhoneNumbersTab({ inputS }) {
  const auth = useAuth();
  const store = useStore();
  const teamMembers = store.teamMembers || [];
  const orgId = auth.currentUser?._orgId;
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Purchase flow
  const [showBuy, setShowBuy] = useState(false);
  const [searchType, setSearchType] = useState("local");
  const [areaCode, setAreaCode] = useState("");
  const [searching, setSearching] = useState(false);
  const [available, setAvailable] = useState([]);
  const [buying, setBuying] = useState(null);

  // Provisioning
  const [provisioned, setProvisioned] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  // Dialer settings
  const [dialerSettings, setDialerSettings] = useState({ callerId: "", recording: "none", voicemail: "off", ringTimeout: "30" });
  const [dialerSaved, setDialerSaved] = useState(false);

  // Load dialer settings
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const { data } = await supabase.from("org_settings").select("*").eq("org_id", orgId).eq("key", "dialer_settings").maybeSingle();
        if (data?.value) setDialerSettings(prev => ({ ...prev, ...data.value }));
      } catch (e) {}
    })();
  }, [orgId]);

  const saveDialerSettings = async (newSettings) => {
    setDialerSettings(newSettings);
    try {
      await supabase.from("org_settings").upsert({
        org_id: orgId, key: "dialer_settings", value: newSettings, updated_at: new Date().toISOString(),
      }, { onConflict: "org_id,key" });
      setDialerSaved(true);
      setTimeout(() => setDialerSaved(false), 2000);
    } catch (e) { console.warn("[Dialer] Save failed:", e.message); }
  };

  const updateDialer = (field, value) => {
    const updated = { ...dialerSettings, [field]: value };
    saveDialerSettings(updated);
  };

  // Load existing numbers
  useEffect(() => {
    loadNumbers();
  }, [orgId]);

  async function loadNumbers() {
    if (!orgId) return;
    setLoading(true);
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list", orgId }),
      });
      const data = await resp.json();
      if (data.numbers) {
        setNumbers(data.numbers);
        setProvisioned(true);
      } else if (data.error?.includes("not provisioned") || data.error?.includes("No Twilio")) {
        setProvisioned(false);
      }
    } catch (e) { console.warn("Failed to load numbers:", e); }
    setLoading(false);
  }

  async function handleProvision() {
    setProvisioning(true); setError("");
    try {
      const resp = await apiFetch("/api/twilio/provision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId, orgName: auth.currentUser?.firstName + "'s Org" }),
      });
      const data = await resp.json();
      if (data.success) {
        setProvisioned(true);
        setSuccess("Phone system activated! Now search for a number below.");
      } else {
        setError(data.error || "Provisioning failed");
      }
    } catch (e) { setError(e.message); }
    setProvisioning(false);
  }

  async function searchNumbers() {
    setSearching(true); setError(""); setAvailable([]);
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: searchType === "toll_free" ? "search_toll_free" : "search", orgId, areaCode: areaCode || undefined }),
      });
      const data = await resp.json();
      if (data.numbers) setAvailable(data.numbers);
      else setError(data.error || "No numbers found");
    } catch (e) { setError(e.message); }
    setSearching(false);
  }

  async function buyNumber(number) {
    setBuying(number.phoneNumber); setError("");
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "purchase", orgId, phoneNumber: number.phoneNumber }),
      });
      const data = await resp.json();
      if (data.success) {
        setNumbers(prev => [...prev, { ...number, ...data, status: "active", purchased_at: new Date().toISOString() }]);
        setAvailable(prev => prev.filter(n => n.phoneNumber !== number.phoneNumber));
        setSuccess(`Number ${number.phoneNumber} purchased! $1.75/mo will be added to your bill.`);
        setTimeout(() => setSuccess(""), 5000);
      } else { setError(data.error || "Purchase failed"); }
    } catch (e) { setError(e.message); }
    setBuying(null);
  }

  async function releaseNumber(phoneNumber) {
    if (!window.confirm(`Release ${phoneNumber}? This number will be permanently removed.`)) return;
    setError("");
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "release", orgId, phoneNumber }),
      });
      const data = await resp.json();
      if (data.success) {
        setNumbers(prev => prev.filter(n => n.phoneNumber !== phoneNumber && n.phone_number !== phoneNumber));
        setSuccess("Number released.");
        setTimeout(() => setSuccess(""), 3000);
      } else { setError(data.error || "Failed to release"); }
    } catch (e) { setError(e.message); }
  }

  async function setPrimary(phoneNumber) {
    try {
      const resp = await apiFetch("/api/twilio/numbers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "set_primary", orgId, phoneNumber }),
      });
      const data = await resp.json();
      if (data.success) {
        setNumbers(prev => prev.map(n => ({
          ...n,
          is_primary: (n.phoneNumber || n.phone_number) === phoneNumber,
        })));
        setSuccess("Primary number updated.");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (e) { setError(e.message); }
  }

  const crd = { background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "20px 24px" };
  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.tx3 }}>Loading phone configuration...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Phone Numbers & Compliance</h2>
          <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Manage your phone numbers, caller IDs, and A2P messaging compliance</p>
        </div>
        {provisioned && <Btn primary onClick={() => setShowBuy(!showBuy)}>+ Buy Number</Btn>}
      </div>

      {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#E5393510", color: "#E53935", fontSize: 12, marginBottom: 16, border: "1px solid #E5393525" }}>{error} <button onClick={() => setError("")} style={{ float: "right", background: "none", border: "none", color: "#E53935", cursor: "pointer" }}>✕</button></div>}
      {success && <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 12, marginBottom: 16, border: "1px solid rgba(217,219,221,0.12)" }}>{success}</div>}

      {/* ── Not Provisioned ── */}
      {!provisioned && (
        <div style={{ ...crd, padding: "30px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: 24, background: "rgba(250,250,250,0.05)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="rgba(250,250,250,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
            <div style={{ fontSize: 16, fontWeight: 400, color: T.txt, marginBottom: 8 }}>Phone System Not Activated</div>
            <div style={{ fontSize: 12, color: T.tx3, marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>
              Activate your phone system to purchase numbers, make browser-based calls, and send SMS. Each number costs $1.75/month with unlimited calling & SMS.
            </div>
            <Btn primary onClick={handleProvision} style={{ padding: "12px 32px", fontSize: 14 }}>
              {provisioning ? "Activating..." : "Activate Phone System →"}
            </Btn>
          </div>

          {/* Admin-only: Connect existing Twilio */}
          {auth.currentUser?.role === "admin" && (
            <AdminTwilioConnect orgId={orgId} inputS={inputS} onConnected={() => { setProvisioned(true); loadNumbers(); }} setError={setError} setSuccess={setSuccess} />
          )}
        </div>
      )}

      {/* ── Your Numbers Directory ── */}
      {provisioned && (
        <>
          <div style={crd}>
            <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>
              Your Numbers ({numbers.length})
            </div>
            {numbers.length === 0 ? (
              <div style={{ padding: "24px 0", textAlign: "center", color: T.tx3, fontSize: 12 }}>
                No numbers yet. Click "Buy Number" to get started.
              </div>
            ) : (
              <div style={{ borderRadius: 8, border: "1px solid " + T.bdr, overflow: "hidden" }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 1.5fr 0.8fr 0.8fr 1fr", padding: "10px 16px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid " + T.bdr, alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>NUMBER</span>
                  <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>TYPE</span>
                  <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>ASSIGNED TO</span>
                  <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>STATUS</span>
                  <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>COST</span>
                  <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textAlign: "right" }}>ACTIONS</span>
                </div>
                {numbers.map((n, i) => {
                  const num = n.phoneNumber || n.phone_number;
                  const isPrimary = n.is_primary || (i === 0 && numbers.length > 0);
                  return (
                    <div key={num || i} style={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 1.5fr 0.8fr 0.8fr 1fr", alignItems: "center", padding: "12px 16px", borderBottom: i < numbers.length - 1 ? "1px solid " + T.bdr : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 400, color: T.txt, fontFamily: "inherit" }}>{num}</span>
                        {isPrimary && <span style={{ padding: "2px 6px", borderRadius: 8, background: "transparent", color: "#FAFAFA", fontSize: 9, fontWeight: 400, border: "1px solid rgba(217,219,221,0.12)" }}>PRIMARY</span>}
                      </div>
                      <div style={{ fontSize: 11, color: T.tx2 }}>
                        {n.type === "toll_free" || (num || "").startsWith("+18") ? "Toll-Free" : "Local"}
                      </div>
                      <div style={{ fontSize: 11, color: T.tx2 }}>
                        <Select
                          value={n.assigned_to || ""}
                          onChange={async (e) => {
                            const val = e.target.value || null;
                            try {
                              const token = (await supabase.auth.getSession())?.data?.session?.access_token;
                              const orgId = window.__coreflow_org_id;
                              await supabase.from("org_phone_numbers")
                                .update({ assigned_to: val })
                                .eq("org_id", orgId)
                                .filter("phone_number", "ilike", "%" + (num || "").replace(/[^\d]/g, "").slice(-10));
                              setNumbers(prev => prev.map(p => {
                                const pNum = p.phoneNumber || p.phone_number;
                                return pNum === num ? { ...p, assigned_to: val } : p;
                              }));
                            } catch (err) { console.warn("Assign error:", err.message); }
                          }}
                          style={{ padding: "4px 6px", borderRadius: 8, border: "1px solid " + T.bdr, background: T.sf, color: T.txt, fontSize: 11, cursor: "pointer", maxWidth: 150, width: "100%" }}
                        >
                          <option value="">All users</option>
                          {(teamMembers || []).map(m => (
                            <option key={m._dbId} value={m._dbId}>
                              {m.name || m.email || "User"}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <span style={{ padding: "3px 8px", borderRadius: 8, background: "transparent", color: "#FAFAFA", fontSize: 10, fontWeight: 400, border: "1px solid rgba(217,219,221,0.12)" }}>Active</span>
                      </div>
                      <div style={{ fontSize: 11, color: T.tx2 }}>$1.75/mo</div>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        {!isPrimary && (
                          <button onClick={() => setPrimary(num)} style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.03)", color: T.tx2, fontSize: 9, cursor: "pointer" }}>Set Primary</button>
                        )}
                        <button onClick={() => releaseNumber(num)} style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer" }}>Release</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ fontSize: 10, color: T.tx3, marginTop: 10 }}>
              Each number: $1.75/mo • Unlimited calling & SMS included
            </div>
          </div>

          {/* ── Buy Number Panel ── */}
          {showBuy && (
            <div style={{ ...crd, marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Purchase a Number</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {[{ id: "local", label: "Local Number", desc: "Area code specific" }, { id: "toll_free", label: "Toll-Free", desc: "800/888/877" }].map(t => (
                  <button key={t.id} onClick={() => setSearchType(t.id)} style={{
                    flex: 1, padding: "12px 16px", borderRadius: 8, textAlign: "left", cursor: "pointer",
                    border: "2px solid " + (searchType === t.id ? T.blue : T.bdr),
                    background: searchType === t.id ? T.blue + "08" : T.sf,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{t.label}</div>
                    <div style={{ fontSize: 10, color: T.tx3 }}>{t.desc}</div>
                  </button>
                ))}
              </div>
              {searchType === "local" && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 }}>Area Code (optional)</label>
                  <input value={areaCode} onChange={e => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                    placeholder="e.g. 313, 415, 212" style={{ ...inputS, width: 200 }} maxLength={3} />
                </div>
              )}
              <Btn primary onClick={searchNumbers} style={{ marginBottom: 16 }}>
                {searching ? "Searching..." : "Search Available Numbers"}
              </Btn>

              {available.length > 0 && (
                <div style={{ borderRadius: 8, border: "1px solid " + T.bdr, overflow: "hidden", maxHeight: 300, overflowY: "auto" }}>
                  {available.map((n, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: i < available.length - 1 ? "1px solid " + T.bdr : "none", background: i % 2 ? T.sf : T.card }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, fontFamily: "inherit" }}>{n.phoneNumber}</div>
                        <div style={{ fontSize: 10, color: T.tx3 }}>
                          {n.locality ? `${n.locality}, ${n.region}` : "Toll-Free"}
                          {n.capabilities?.sms && " • SMS"}{n.capabilities?.voice && " • Voice"}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 11, color: T.tx2 }}>$1.75/mo</span>
                        <button onClick={() => buyNumber(n)} disabled={!!buying} style={{
                          padding: "6px 16px", borderRadius: 8, border: "none",
                          background: buying === n.phoneNumber ? T.tx3 : T.blue, color: "#fff",
                          fontSize: 11, fontWeight: 400, cursor: buying ? "wait" : "pointer",
                        }}>{buying === n.phoneNumber ? "Purchasing..." : "Buy"}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── A2P Messaging Compliance ── */}
          <A2PComplianceWizard orgId={orgId} inputS={inputS} crd={crd} numbers={numbers} />

          {/* ── Local Presence Pool ── */}
          <LocalPresenceSection orgId={orgId} inputS={inputS} crd={crd} />

          {/* ── Caller ID & Settings ── */}
          <div style={{ ...crd, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>Dialer Settings</div>
              {dialerSaved && <span style={{ fontSize: 11, color: "#2E8B57" }}>Saved</span>}
            </div>
            <div style={{ fontSize: 10, color: T.tx3, marginBottom: 16, lineHeight: 1.5 }}>Org-wide defaults. These apply to all team members.</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 }}>Fallback Caller ID</label>
                <select value={dialerSettings.callerId} onChange={e => updateDialer("callerId", e.target.value)} style={{ ...inputS, cursor: "pointer" }}>
                  <option value="">— Select —</option>
                  {numbers.length === 0 ? (
                    <option disabled>No numbers — buy one above</option>
                  ) : numbers.map(n => {
                    const num = n.phoneNumber || n.phone_number;
                    return <option key={num} value={num}>{num} {n.is_primary ? "(Primary)" : ""}</option>;
                  })}
                </select>
                <div style={{ fontSize: 9, color: T.tx3, marginTop: 4, lineHeight: 1.5 }}>Used only when the rep has no assigned number and Local Presence doesn't match.</div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 }}>Call Recording</label>
                <select value={dialerSettings.recording} onChange={e => updateDialer("recording", e.target.value)} style={{ ...inputS, cursor: "pointer" }}>
                  <option value="all">Record all calls</option>
                  <option value="inbound">Record inbound only</option>
                  <option value="outbound">Record outbound only</option>
                  <option value="none">No recording</option>
                </select>
                <div style={{ fontSize: 9, color: T.tx3, marginTop: 4 }}>Org policy — applies to all users. Cannot be overridden.</div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 }}>Default Voicemail</label>
                <select value={dialerSettings.voicemail} onChange={e => updateDialer("voicemail", e.target.value)} style={{ ...inputS, cursor: "pointer" }}>
                  <option value="on">Enabled — record voicemail after timeout</option>
                  <option value="off">Disabled</option>
                </select>
                <div style={{ fontSize: 9, color: T.tx3, marginTop: 4 }}>Org default. Individual users can override in their profile.</div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 400, color: T.tx2, display: "block", marginBottom: 4 }}>Ring Timeout</label>
                <select value={dialerSettings.ringTimeout} onChange={e => updateDialer("ringTimeout", e.target.value)} style={{ ...inputS, cursor: "pointer" }}>
                  <option value="15">15 seconds</option>
                  <option value="20">20 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="45">45 seconds</option>
                  <option value="60">60 seconds</option>
                </select>
              </div>
            </div>

            {/* Caller ID priority */}
            <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 8, background: "rgba(64,89,173,0.04)", border: "1px solid rgba(64,89,173,0.1)" }}>
              <div style={{ fontSize: 10, fontWeight: 400, color: T.tx2, letterSpacing: ".04em", marginBottom: 6 }}>OUTBOUND CALLER ID PRIORITY</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {["Local Presence match", "User's assigned number", "Org fallback"].map((step, i) => (
                  <React.Fragment key={step}>
                    <span style={{ fontSize: 10, color: T.txt, padding: "3px 8px", borderRadius: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.08)" }}>{i + 1}. {step}</span>
                    {i < 2 && <span style={{ fontSize: 10, color: T.tx3 }}>→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Admin-only: Connect/reconnect existing Twilio */}
          {auth.currentUser?.role === "admin" && (
            <div style={{ marginTop: 16 }}>
              <AdminTwilioConnect orgId={orgId} inputS={inputS} onConnected={() => { setProvisioned(true); loadNumbers(); }} setError={setError} setSuccess={setSuccess} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Integrations Tab with real email connections ── */
function IntegrationsTab() {
  const auth = useAuth();
  const [connections, setConnections] = useState([]);
  const [calConnections, setCalConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConnections();
    const params = new URLSearchParams(window.location.search);
    if (params.get("email_connected")) loadConnections();
    // Check for calendar connection success in hash
    if (window.location.hash.includes("gcal=connected")) loadConnections();

    // Refetch when the tab regains focus (covers OAuth popup return)
    const onFocus = () => loadConnections();
    const onHashChange = () => {
      if (window.location.hash.includes("email_connected") || window.location.hash.includes("gcal=connected")) {
        loadConnections();
      }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onHashChange);

    // Realtime subscription — server-side OAuth callback inserts the row; surface it live
    const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id;
    let channel = null;
    if (userId) {
      channel = supabase
        .channel("email-conn-" + userId)
        .on("postgres_changes", { event: "*", schema: "public", table: "email_connections", filter: `user_id=eq.${userId}` }, () => loadConnections())
        .on("postgres_changes", { event: "*", schema: "public", table: "calendar_connections", filter: `user_id=eq.${userId}` }, () => loadConnections())
        .subscribe();
    }

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onHashChange);
      if (channel) supabase.removeChannel(channel);
    };
  }, [auth.currentUser?._dbId]);

  const loadConnections = async () => {
    try {
      const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id;
      if (!userId) { setLoading(false); return; }
      let query = supabase.from("email_connections")
        .select("id, provider, email, display_name, is_active, updated_at, user_id")
        .eq("is_active", true);
      if (userId) query = query.eq("user_id", userId);
      const { data, error } = await query;
      if (!error && data) setConnections(data);

      // Load calendar connections
      let calQuery = supabase.from("calendar_connections")
        .select("id, provider, email, is_active, updated_at, user_id")
        .eq("is_active", true);
      if (userId) calQuery = calQuery.eq("user_id", userId);
      const { data: calData } = await calQuery;
      if (calData) setCalConnections(calData);
    } catch (e) {
      console.warn("[CoreFlow] Failed to load connections:", e.message);
    } finally {
      setLoading(false);
    }
  };

  const connectGoogle = () => {
    const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id || "";
    const orgId = auth.currentUser?._orgId || window.__coreflow_org_id || "";
    window.location.href = `/api/auth/google?userId=${userId}&orgId=${orgId}&returnUrl=/admin`;
  };

  const connectMicrosoft = () => {
    const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id || "";
    const orgId = auth.currentUser?._orgId || window.__coreflow_org_id || "";
    window.location.href = `/api/auth/microsoft?userId=${userId}&orgId=${orgId}&returnUrl=/admin`;
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider === "google" ? "Gmail" : "Outlook"}? Emails will fall back to the platform sender.`)) return;
    try {
      await supabase.from("email_connections")
        .update({ is_active: false })
        .eq("provider", provider);
      setConnections(prev => prev.filter(c => c.provider !== provider));
    } catch (e) {
      console.warn("[CoreFlow] Disconnect failed:", e.message);
    }
  };

  const connectGoogleCalendar = () => {
    const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id || "";
    const orgId = auth.currentUser?._orgId || window.__coreflow_org_id || "";
    window.location.href = `/api/auth/google-calendar?userId=${userId}&orgId=${orgId}&returnUrl=${encodeURIComponent("/admin/integrations")}`;
  };

  const disconnectCalendar = async (provider) => {
    if (!window.confirm("Disconnect Google Calendar? Events will no longer sync.")) return;
    try {
      const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id;
      await supabase.from("calendar_connections").update({ is_active: false }).eq("provider", provider).eq("user_id", userId);
      setCalConnections(prev => prev.filter(c => c.provider !== provider));
    } catch (e) {
      console.warn("[CoreFlow] Calendar disconnect failed:", e.message);
    }
  };

  const googleConnected = connections.find(c => c.provider === "google");
  const microsoftConnected = connections.find(c => c.provider === "microsoft");

  const cardStyle = {
    padding: 20, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, marginBottom: 10,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Integrations</h2>
        <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Connect your email and tools</p>
      </div>

      {/* Email Connections */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", marginBottom: 10 }}>EMAIL ACCOUNTS</div>
        <p style={{ fontSize: 12, color: T.tx2, marginBottom: 16, lineHeight: 1.5 }}>
          Connect your email to send messages from your own address. Without a connection, emails send from the platform address.
        </p>

        {/* Gmail */}
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              background: googleConnected ? "#2E8B57" + "10" : T.sf,
              border: "1px solid " + (googleConnected ? "#2E8B57" + "25" : T.bdr),
            }}>
              <img src={LOGO_GMAIL} alt="Gmail" style={{ width: 24, height: 24 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Gmail / Google Workspace</div>
              {googleConnected ? (
                <div style={{ fontSize: 11, color: "#2E8B57", marginTop: 3 }}>
                  Connected as {googleConnected.email} — {cfDate(googleConnected.updated_at)}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 3 }}>Send emails from your Gmail address</div>
              )}
            </div>
            {googleConnected ? (
              <div style={{ display: "flex", gap: 6 }}>
                {/* Inline replacement for <Badge color="#2E8B57"> — shared
                    Badge silently drops its color prop, and its 10px radius
                    didn't match the adjacent Disconnect button's 8px. Green
                    coloring preserved here because "Connected" is a
                    meaningful status indicator, not decorative. */}
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  padding: "5px 10px", borderRadius: 8,
                  border: "1px solid rgba(46,139,87,0.3)",
                  background: "rgba(46,139,87,0.1)",
                  color: "#2E8B57", fontSize: 10, fontWeight: 400,
                }}>Connected</span>
                <button onClick={() => disconnect("google")} style={{
                  padding: "5px 10px", borderRadius: 8, border: "1px solid " + T.bdr,
                  background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer",
                }}>Disconnect</button>
              </div>
            ) : (
              <button onClick={connectGoogle} style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: T.blue, color: "#fff", fontSize: 12, fontWeight: 400, cursor: "pointer",
              }}>Connect Gmail</button>
            )}
          </div>
        </div>

        {/* Outlook */}
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              background: microsoftConnected ? "#2E8B57" + "10" : T.sf,
              border: "1px solid " + (microsoftConnected ? "#2E8B57" + "25" : T.bdr),
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-label="Outlook" role="img">
                <path fill="#0078D4" d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88M24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25m-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.24 1.56.23.72.71 1.23.48.52 1.17.81.69.3 1.56.3M7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Outlook / Microsoft 365</div>
              {microsoftConnected ? (
                <div style={{ fontSize: 11, color: "#2E8B57", marginTop: 3 }}>
                  Connected as {microsoftConnected.email} — {cfDate(microsoftConnected.updated_at)}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 3 }}>Send emails from your Outlook address</div>
              )}
            </div>
            {microsoftConnected ? (
              <div style={{ display: "flex", gap: 6 }}>
                {/* Inline replacement for <Badge color="#2E8B57"> — shared
                    Badge silently drops its color prop, and its 10px radius
                    didn't match the adjacent Disconnect button's 8px. Green
                    coloring preserved here because "Connected" is a
                    meaningful status indicator, not decorative. */}
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  padding: "5px 10px", borderRadius: 8,
                  border: "1px solid rgba(46,139,87,0.3)",
                  background: "rgba(46,139,87,0.1)",
                  color: "#2E8B57", fontSize: 10, fontWeight: 400,
                }}>Connected</span>
                <button onClick={() => disconnect("microsoft")} style={{
                  padding: "5px 10px", borderRadius: 8, border: "1px solid " + T.bdr,
                  background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer",
                }}>Disconnect</button>
              </div>
            ) : (
              <button onClick={connectMicrosoft} style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: T.blue, color: "#fff", fontSize: 12, fontWeight: 400, cursor: "pointer",
              }}>Connect Outlook</button>
            )}
          </div>
        </div>
      </div>

      {/* Calendar Connections */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", marginBottom: 10 }}>CALENDAR</div>
        <p style={{ fontSize: 12, color: T.tx2, marginBottom: 16, lineHeight: 1.5 }}>
          Connect your calendar to sync events, enable booking pages, and see your schedule inside CoreFlow.
        </p>

        {/* Google Calendar */}
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              background: calConnections.find(c => c.provider === "google") ? "#2E8B57" + "10" : T.sf,
              border: "1px solid " + (calConnections.find(c => c.provider === "google") ? "#2E8B57" + "25" : T.bdr),
            }}>
              <img src={LOGO_GOOGLE_CALENDAR} alt="Google Calendar" style={{ width: 24, height: 24 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Google Calendar</div>
              {calConnections.find(c => c.provider === "google") ? (
                <div style={{ fontSize: 11, color: "#2E8B57", marginTop: 3 }}>
                  Connected {calConnections.find(c => c.provider === "google").email ? "as " + calConnections.find(c => c.provider === "google").email : ""} — syncing events
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 3 }}>Sync your Google Calendar events and enable booking pages</div>
              )}
            </div>
            {calConnections.find(c => c.provider === "google") ? (
              <div style={{ display: "flex", gap: 6 }}>
                {/* Inline replacement for <Badge color="#2E8B57"> — shared
                    Badge silently drops its color prop, and its 10px radius
                    didn't match the adjacent Disconnect button's 8px. Green
                    coloring preserved here because "Connected" is a
                    meaningful status indicator, not decorative. */}
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  padding: "5px 10px", borderRadius: 8,
                  border: "1px solid rgba(46,139,87,0.3)",
                  background: "rgba(46,139,87,0.1)",
                  color: "#2E8B57", fontSize: 10, fontWeight: 400,
                }}>Connected</span>
                <button onClick={() => disconnectCalendar("google")} style={{
                  padding: "5px 10px", borderRadius: 8, border: "1px solid " + T.bdr,
                  background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer",
                }}>Disconnect</button>
              </div>
            ) : (
              <button onClick={connectGoogleCalendar} style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: "#4285F4", color: "#fff", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
              }}>Connect Calendar</button>
            )}
          </div>
        </div>
      </div>

      {/* Coming Soon */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", marginBottom: 10 }}>COMING SOON</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {COMING_SOON_INTEGRATIONS.map(int => (
            <div key={int.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 16, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, opacity: 0.6 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                background: T.sf, border: "1px solid " + T.bdr,
              }}>
                {int.id === "linkedin" ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" aria-label="LinkedIn" role="img">
                    <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.852 3.37-1.852 3.601 0 4.267 2.37 4.267 5.455v6.288zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                ) : (
                  <img src={int.logo} alt={int.name} style={{ width: 22, height: 22 }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{int.name}</div>
                <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>{int.desc}</div>
              </div>
              <Badge color={T.tx3}>Coming Soon</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════ */
/* GOVERNANCE TAB                                  */
/* ═══════════════════════════════════════════════ */
function GeneralSettingsTab({ orgId }) {
  const [form, setForm] = useState({
    name: "", company_domain: "", timezone: "America/Los_Angeles",
    currency: "USD", date_format: "YYYY-MM-DD", fiscal_year_start: "January",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const { data } = await supabase.from("organizations")
        .select("name, company_domain, timezone, currency, date_format, fiscal_year_start")
        .eq("id", orgId).maybeSingle();
      if (data) {
        setForm(f => ({
          name: data.name ?? f.name,
          company_domain: data.company_domain ?? "",
          timezone: data.timezone ?? "America/Los_Angeles",
          currency: data.currency ?? "USD",
          date_format: data.date_format ?? "YYYY-MM-DD",
          fiscal_year_start: data.fiscal_year_start ?? "January",
        }));
      }
      setLoading(false);
    })();
  }, [orgId]);

  const save = async () => {
    if (!orgId) return;
    setSaving(true);
    const { error } = await supabase.from("organizations").update({
      name: form.name,
      company_domain: form.company_domain,
      timezone: form.timezone,
      currency: form.currency,
      date_format: form.date_format,
      fiscal_year_start: form.fiscal_year_start,
    }).eq("id", orgId);
    setSaving(false);
    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } else {
      alert("Failed to save: " + error.message);
    }
  };

  const fields = [
    { key: "name", label: "Workspace Name" },
    { key: "company_domain", label: "Company Domain" },
    { key: "timezone", label: "Timezone" },
    { key: "currency", label: "Currency" },
    { key: "date_format", label: "Date Format" },
    { key: "fiscal_year_start", label: "Fiscal Year Start" },
  ];

  return (<>
    {fields.map(f => (
      <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid " + T.bdr }}>
        <span style={{ fontSize: 12, color: T.tx3, minWidth: 160 }}>{f.label}</span>
        <input
          value={form[f.key] || ""}
          onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
          disabled={loading}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.03)", color: T.txt, fontSize: 12, fontFamily: "inherit", outline: "none", maxWidth: 300, flex: 1 }}
        />
      </div>
    ))}
    <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
      <button onClick={save} disabled={saving || loading} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: saving ? "default" : "pointer", fontFamily: "inherit", opacity: saving || loading ? 0.6 : 1 }}>
        {saving ? "Saving..." : "Save Changes"}
      </button>
      {saved && <span style={{ fontSize: 11, color: "#2E8B57" }}>Saved</span>}
    </div>
  </>);
}

function GovernanceTab({ orgId, store }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState("all");

  const { governanceSettings = {}, setGovernanceSettings, requiredContactFields = [], setRequiredContactFields, requiredDealFields = [], setRequiredDealFields, stageGates = {}, setStageGates, pipelineStages = [] } = store;

  const crd = { padding: "20px", borderRadius: 10, background: "#2C2E33", border: "1px solid rgba(250,250,250,0.06)", marginBottom: 16 };
  const lbl = { fontSize: 12, color: "#a0a3ab", marginBottom: 6, fontWeight: 400 };
  const sH = { fontSize: 16, fontWeight: 400, color: "#FAFAFA", margin: "0 0 4px" };
  const sP = { fontSize: 12, color: "#a0a3ab", margin: "0 0 16px" };

  const contactFieldOptions = [
    { key: "email", label: "Email" },
    { key: "ph", label: "Phone" },
    { key: "co", label: "Company" },
    { key: "title", label: "Job Title" },
    { key: "industry", label: "Industry" },
    { key: "source", label: "Lead Source" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
  ];

  const dealFieldOptions = [
    { key: "value", label: "Deal Value" },
    { key: "close_date", label: "Close Date" },
    { key: "contact_id", label: "Associated Contact" },
  ];

  const toggleContactField = (key) => {
    const current = requiredContactFields || [];
    const next = current.includes(key) ? current.filter(f => f !== key) : [...current, key];
    setRequiredContactFields(next);
  };

  const toggleDealField = (key) => {
    const current = requiredDealFields || [];
    const next = current.includes(key) ? current.filter(f => f !== key) : [...current, key];
    setRequiredDealFields(next);
  };

  const updateGov = (key, val) => {
    setGovernanceSettings(prev => ({ ...prev, [key]: val }));
  };

  const updateStageGate = (stageName, key, val) => {
    setStageGates(prev => ({
      ...prev,
      [stageName]: { ...(prev[stageName] || {}), [key]: val }
    }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "set_governance", orgId,
          requiredContactFields, requiredDealFields, stageGates, governanceSettings,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error("[GovernanceTab] Failed to save governance settings:", e);
      toast?.error?.("Failed to save governance settings: " + (e.message || "unknown error"));
    }
    setSaving(false);
  };

  const loadAuditLogs = async () => {
    setLoadingLogs(true);
    try {
      const resp = await apiFetch("/api/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list", orgId, entityType: logFilter === "all" ? undefined : logFilter, limit: 100 }),
      });
      const data = await resp.json();
      setAuditLogs(data.logs || []);
    } catch (e) { console.error("Load audit logs failed:", e); }
    setLoadingLogs(false);
  };

  useEffect(() => { loadAuditLogs(); }, [logFilter]);

  const fmtDate = (d) => { if (!d) return ""; const dt = new Date(d); return dt.toLocaleDateString() + " " + dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={sH}>Governance & Audit</h2>
          <p style={{ ...sP, margin: 0 }}>Control data quality, enforce sales process, and track every change</p>
        </div>
        <button onClick={saveAll} disabled={saving} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: saving ? "#535C6E" : "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
          {saving ? "Saving..." : saved ? "Saved" : "Save Changes"}
        </button>
      </div>

      {/* Enforcement Toggles */}
      <div style={crd}>
        <h3 style={{ ...sH, fontSize: 14 }}>Enforcement</h3>
        <p style={sP}>Enable governance rules across your organization</p>
        {[
          { key: "enforceRequiredFields", label: "Enforce required fields", desc: "Block saving contacts or deals with missing required fields" },
          { key: "enforceStageGates", label: "Enforce stage gates", desc: "Require specific actions before deals can move to certain stages" },
          { key: "requireStageNotes", label: "Require notes on stage changes", desc: "Reps must add a note explaining why they moved a deal to a new stage" },
        ].map(item => (
          <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid rgba(250,250,250,0.04)" }}>
            <div>
              <div style={{ fontSize: 13, color: "#FAFAFA", fontWeight: 400 }}>{item.label}</div>
              <div style={{ fontSize: 11, color: "#a0a3ab" }}>{item.desc}</div>
            </div>
            <Checkbox checked={!!governanceSettings[item.key]} onChange={() => updateGov(item.key, !governanceSettings[item.key])} />
          </div>
        ))}
      </div>

      {/* Required Contact Fields */}
      <div style={crd}>
        <h3 style={{ ...sH, fontSize: 14 }}>Required Contact Fields</h3>
        <p style={sP}>These fields must be filled when creating or editing a contact</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {contactFieldOptions.map(f => (
            <div key={f.key} onClick={() => toggleContactField(f.key)}
              style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 400, cursor: "pointer",
                background: requiredContactFields.includes(f.key) ? "#4059AD" : "rgba(250,250,250,0.04)",
                color: requiredContactFields.includes(f.key) ? "#FAFAFA" : "#a0a3ab",
                border: "1px solid " + (requiredContactFields.includes(f.key) ? "#4059AD" : "rgba(250,250,250,0.08)") }}>
              {f.label}
            </div>
          ))}
        </div>
      </div>

      {/* Required Deal Fields */}
      <div style={crd}>
        <h3 style={{ ...sH, fontSize: 14 }}>Required Deal Fields</h3>
        <p style={sP}>These fields must be filled when creating or editing a deal</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {dealFieldOptions.map(f => (
            <div key={f.key} onClick={() => toggleDealField(f.key)}
              style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 400, cursor: "pointer",
                background: requiredDealFields.includes(f.key) ? "#4059AD" : "rgba(250,250,250,0.04)",
                color: requiredDealFields.includes(f.key) ? "#FAFAFA" : "#a0a3ab",
                border: "1px solid " + (requiredDealFields.includes(f.key) ? "#4059AD" : "rgba(250,250,250,0.08)") }}>
              {f.label}
            </div>
          ))}
        </div>
      </div>

      {/* Stage Gates */}
      <div style={crd}>
        <h3 style={{ ...sH, fontSize: 14 }}>Stage Gates</h3>
        <p style={sP}>Define what must happen before a deal can enter each stage</p>
        {(pipelineStages || []).filter(s => s.name !== "Discovery").map(stage => {
          const gate = stageGates[stage.name] || {};
          return (
            <div key={stage.name} style={{ padding: "14px 0", borderBottom: "1px solid rgba(250,250,250,0.04)" }}>
              <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 10 }}>{stage.name}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {[
                  { key: "requireCall", label: "Require logged call" },
                  { key: "requireEmail", label: "Require sent email" },
                  { key: "requireNote", label: "Require note" },
                  { key: "requireStageNote", label: "Require note on move" },
                ].map(opt => {
                  const checked = !!gate[opt.key];
                  return (
                    // Wrapping div drives state — click anywhere on the row toggles.
                    // Checkbox's own onChange is a no-op so the inner-button click
                    // still bubbles to this div and only one toggle fires.
                    <div key={opt.key}
                      onClick={() => updateStageGate(stage.name, opt.key, !checked)}
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#a0a3ab", cursor: "pointer" }}>
                      <Checkbox size={14} checked={checked} onChange={() => {}} />
                      {opt.label}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 11, color: "#a0a3ab" }}>Min activities required: </label>
                <input type="number" min="0" max="20" value={gate.minActivities || 0}
                  onChange={e => updateStageGate(stage.name, "minActivities", parseInt(e.target.value) || 0)}
                  style={{ width: 50, padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(250,250,250,0.1)", background: "rgba(250,250,250,0.04)", color: "#FAFAFA", fontSize: 12, fontFamily: "inherit" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Audit Log */}
      <div style={crd}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <h3 style={{ ...sH, fontSize: 14 }}>Audit Log</h3>
            <p style={{ ...sP, margin: "4px 0 0" }}>Track every change made to contacts, deals, and settings</p>
          </div>
          <select value={logFilter} onChange={e => setLogFilter(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(250,250,250,0.1)", background: "#2C2E33", color: "#FAFAFA", fontSize: 12, fontFamily: "inherit" }}>
            <option value="all">All Changes</option>
            <option value="contact">Contacts</option>
            <option value="deal">Deals</option>
          </select>
        </div>
        {loadingLogs && <div style={{ fontSize: 12, color: "#a0a3ab", padding: "20px 0", textAlign: "center" }}>Loading audit log...</div>}
        {!loadingLogs && auditLogs.length === 0 && (
          <div style={{ fontSize: 12, color: "#a0a3ab", padding: "30px 0", textAlign: "center" }}>
            No changes recorded yet. Changes will appear here as your team edits contacts and deals.
          </div>
        )}
        {!loadingLogs && auditLogs.length > 0 && (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {auditLogs.map(log => (
              <div key={log.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(250,250,250,0.04)", fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "#FAFAFA", fontWeight: 400 }}>{log.user_name || "System"}</span>
                  <span style={{ color: "#535C6E" }}>{fmtDate(log.created_at)}</span>
                </div>
                <div style={{ color: "#a0a3ab" }}>
                  {log.action === "stage_change" && (
                    <span>Moved <span style={{ color: "#FAFAFA" }}>{log.entity_name}</span> from {log.old_value} to <span style={{ color: "#FFB547" }}>{log.new_value}</span></span>
                  )}
                  {log.action === "update" && log.changes && (
                    <span>
                      Updated <span style={{ color: "#FAFAFA" }}>{log.entity_name}</span>
                      {Object.entries(typeof log.changes === "string" ? JSON.parse(log.changes) : log.changes).map(([field, vals]) => (
                        <div key={field} style={{ marginTop: 2, paddingLeft: 8, fontSize: 11 }}>
                          {field}: <span style={{ color: "#E53935", textDecoration: "line-through" }}>{vals.old || "empty"}</span> → <span style={{ color: "#00D68F" }}>{vals.new || "empty"}</span>
                        </div>
                      ))}
                    </span>
                  )}
                  {log.action === "create" && (
                    <span>Created <span style={{ color: "#FAFAFA" }}>{log.entity_type}</span>: {log.entity_name}</span>
                  )}
                  {log.action === "delete" && (
                    <span>Deleted <span style={{ color: "#FAFAFA" }}>{log.entity_type}</span>: {log.entity_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// EMAIL ACCESS TAB — admin-managed inbox-access grants. Mirrors the
// users.permissions per-user override model (auth.js) but at the
// connection level: each row's shared_with array names extra users
// who can read that connection's emails in the UI.
// ═══════════════════════════════════════════════════════════════════
function EmailAccessTab() {
  const store = useStore();
  const auth = useAuth();
  const { emailConnections, teamMembers, updateConnectionSharedWith } = store;

  if (!auth.hasPermission || !auth.hasPermission("email_access_manage")) {
    return (
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Email Inbox Access</h2>
        <p style={{ fontSize: 12, color: T.tx3, margin: "12px 0 0" }}>You don't have permission to manage email access.</p>
      </div>
    );
  }

  const conns = emailConnections || [];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Email Inbox Access</h2>
        <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>
          Manage who can view each connected inbox's emails. The connection owner always has access.
        </p>
      </div>
      {conns.length === 0 ? (
        <div style={{ padding: 16, borderRadius: 8, border: "1px dashed " + T.bdr, background: T.sf, fontSize: 12, color: T.tx3 }}>
          No email connections yet. Users can connect their inbox under <span style={{ color: T.tx2 }}>Profile &amp; Account</span>.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {conns.map(conn => (
            <ConnectionAccessCard
              key={conn.id}
              connection={conn}
              teamMembers={teamMembers || []}
              onSave={(next) => updateConnectionSharedWith(conn.id, next)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionAccessCard({ connection, teamMembers, onSave }) {
  const owner = teamMembers.find(u => (u._dbId || u.id) === connection.user_id);
  const ownerName = owner ? (owner.name || ((owner.fn || "") + " " + (owner.ln || "")).trim()) : "Unknown user";
  const initialShared = Array.isArray(connection.shared_with) ? connection.shared_with : [];
  const [sharedWith, setSharedWith] = useState(initialShared);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Eligible delegates: every other team member. Owner is implicit and not
  // shown. Connections with null user_id (allowed by schema) leave owner
  // null — every member is a candidate.
  const otherMembers = teamMembers.filter(u => {
    const uid = u._dbId || u.id;
    return connection.user_id ? uid !== connection.user_id : true;
  });

  const toggleUser = (userId) => {
    setSharedWith(prev => prev.includes(userId)
      ? prev.filter(id => id !== userId)
      : [...prev, userId]
    );
  };

  // Dirty check: array equality regardless of order.
  const dirty = (() => {
    if (sharedWith.length !== initialShared.length) return true;
    const a = new Set(sharedWith), b = new Set(initialShared);
    for (const id of a) if (!b.has(id)) return true;
    return false;
  })();

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(sharedWith);
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 14, borderRadius: 8, border: "1px solid " + T.bdr, background: T.sf }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, color: T.txt, fontWeight: 400 }}>{connection.email || "(no address)"}</div>
          <div style={{ fontSize: 10, color: T.tx3, marginTop: 2, textTransform: "uppercase", letterSpacing: ".06em" }}>
            {connection.provider || "?"} · Owner: {ownerName} (always has access)
          </div>
        </div>
        {connection.is_active === false && (
          <Badge color="rgba(217,219,221,0.18)">disconnected</Badge>
        )}
      </div>

      <div style={{ fontSize: 11, color: T.tx3, marginBottom: 8 }}>Grant access to:</div>
      {otherMembers.length === 0 ? (
        <div style={{ fontSize: 11, color: T.tx3, fontStyle: "italic", paddingBottom: 8 }}>No other team members in this org yet.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6, marginBottom: 12 }}>
          {otherMembers.map(u => {
            const uid = u._dbId || u.id;
            const checked = sharedWith.includes(uid);
            const displayName = u.name || ((u.fn || "") + " " + (u.ln || "")).trim() || u.email || "user";
            return (
              <label key={uid} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 6, background: checked ? "rgba(64,89,173,0.12)" : "transparent", border: "1px solid " + (checked ? "rgba(64,89,173,0.35)" : T.bdr), cursor: "pointer", fontFamily: "inherit" }}>
                <Checkbox checked={checked} onChange={() => toggleUser(uid)} />
                <span style={{ fontSize: 12, color: T.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
              </label>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, color: error ? "#E53935" : T.tx3 }}>{error || (dirty ? "Unsaved changes" : "Up to date")}</span>
        <Btn primary onClick={handleSave} disabled={!dirty || saving} style={{ fontSize: 11 }}>
          {saving ? "Saving…" : "Save"}
        </Btn>
      </div>
    </div>
  );
}

export default function AdminCenter({ initialTab, onTabChange, isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const toast = useToast();
  const orgId = auth.currentUser?._orgId || window.__coreflow_org_id;
  const userId = auth.currentUser?._dbId || auth.currentUser?.id;
  const [tab, setTabState] = useState(initialTab || "profile");
  const [showDeleteWsModal, setShowDeleteWsModal] = useState(false);
  const [editingQuotaId, setEditingQuotaId] = useState(null);
  const [editingQuotaValue, setEditingQuotaValue] = useState("");
  const setTab = useCallback((t) => {
    setTabState(t);
    if (onTabChange) onTabChange(t);
  }, [onTabChange]);

  // ── Email Connection Success ──
  const [emailConnectSuccess, setEmailConnectSuccess] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("email_connected");
    const email = params.get("email");
    if (provider) {
      setEmailConnectSuccess({ provider, email: email ? decodeURIComponent(email) : null });
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      // Auto-navigate to integrations tab
      setTabState("integrations");
      // Auto-dismiss after 8 seconds
      setTimeout(() => setEmailConnectSuccess(null), 8000);
    }
  }, []);

  // Sync if initialTab changes (e.g. deep link navigation)
  useEffect(() => {
    if (initialTab && initialTab !== tab) {
      setTabState(initialTab);
    }
  }, [initialTab]);
  const [fieldEntity, setFieldEntity] = useState("contacts");

  // ── CORE AGENTS CONFIG STATE ──
  const CORE_AGENTS = [
    { id: "pipeline", name: "The Pipeline Doctor", icon: "target", color: T.blue,
      desc: "Moves stale deals back, creates follow-up tasks, flags risk",
      settings: [
        { key: "auto_move_stale", label: "Auto-move stale deals back a stage", desc: "When a deal has no activity past the threshold, move it back" },
        { key: "create_followup_tasks", label: "Create follow-up tasks for at-risk deals", desc: "Auto-assign a task to the deal owner when risk is detected" },
        { key: "flag_no_activity", label: "Flag deals with no recent activity", desc: "Highlight deals where no calls, emails, or notes exist" },
        { key: "weekly_pipeline_report", label: "Weekly pipeline health report", desc: "AI-generated summary of pipeline movement every Monday" },
        { key: "forecast_adjustments", label: "Suggest forecast adjustments", desc: "Recommend probability changes based on deal behavior" },
      ],
      config: [
        { key: "stale_threshold", label: "Stale Deal Threshold", options: ["7 days", "14 days", "21 days", "30 days"] },
        { key: "risk_sensitivity", label: "Risk Sensitivity", options: ["Conservative (flag early)", "Balanced", "Relaxed (flag late)"] },
        { key: "min_deal_value", label: "Minimum Deal Value", options: ["All deals", "$10,000+", "$25,000+", "$50,000+", "$100,000+"] },
        { key: "auto_action", label: "Auto-Action Mode", options: ["Suggest only", "Act + notify", "Act silently"] },
      ]
    },
    { id: "callprep", name: "Call Briefer", icon: "phone", color: "#D4763A",
      desc: "Queues top calls with AI briefs, openers, and talking points",
      settings: [
        { key: "auto_brief_before_call", label: "Auto-generate brief before scheduled calls", desc: "Create a briefing doc when a call is upcoming" },
        { key: "include_deal_context", label: "Include deal stage and value in briefs", desc: "Pull pipeline context into the pre-call document" },
        { key: "suggest_openers", label: "Suggest personalized call openers", desc: "AI writes an opening line based on last interaction" },
        { key: "objection_prep", label: "Prepare objection responses", desc: "Pre-load likely objections based on deal stage and industry" },
        { key: "post_call_summary", label: "Auto-summarize after call ends", desc: "Generate bullet-point summary with next steps" },
      ],
      config: [
        { key: "brief_timing", label: "Generate Brief Before", options: ["15 minutes", "30 minutes", "1 hour", "2 hours"] },
        { key: "brief_depth", label: "Brief Detail Level", options: ["Quick (key points)", "Standard (points + context)", "Deep (full analysis)"] },
        { key: "auto_queue", label: "Auto-Queue Priority Calls", options: ["Off", "Top 3 daily", "Top 5 daily", "All hot leads"] },
      ]
    },
    { id: "email", name: "The Drafter", icon: "mail", color: "#2E8B57",
      desc: "Drafts personalized emails from deal context and activity history",
      settings: [
        { key: "auto_draft_followups", label: "Auto-draft follow-ups for stale conversations", desc: "Generate drafts when no reply after 3+ days" },
        { key: "personalize_from_calls", label: "Reference recent call notes in emails", desc: "Pull talking points from last call into the draft" },
        { key: "ab_subject_lines", label: "Generate A/B subject line options", desc: "Provide 2-3 subject variants for each draft" },
        { key: "match_contact_tone", label: "Match the contact's communication style", desc: "Analyze past replies to mirror their tone and length" },
      ],
      config: [
        { key: "default_tone", label: "Default Tone", options: ["Professional", "Casual", "Friendly", "Urgent", "Consultative"] },
        { key: "max_length", label: "Max Email Length", options: ["Short (under 100 words)", "Medium (100-200 words)", "Long (200+ words)"] },
        { key: "signature_style", label: "Sign-off Style", options: ["Best regards", "Thanks", "Cheers", "Talk soon", "No sign-off"] },
      ]
    },
    { id: "winloss", name: "Win/Loss Analyst", icon: "target", color: "#6B5B95",
      desc: "Analyzes deal patterns, identifies what wins and loses, flags at-risk deals",
      settings: [
        { key: "auto_analyze_closed", label: "Auto-analyze every closed deal", desc: "Run win/loss analysis when a deal hits Closed Won or Lost" },
        { key: "pattern_detection", label: "Detect winning patterns across pipeline", desc: "Identify commonalities in won deals (timing, actions, stages)" },
        { key: "at_risk_scoring", label: "Score at-risk deals based on loss patterns", desc: "Flag open deals that resemble past losses" },
        { key: "weekly_insights", label: "Weekly win/loss insights digest", desc: "AI summary of what worked and what didn't this week" },
        { key: "competitor_correlation", label: "Correlate wins/losses with competitors", desc: "Track which competitors you win and lose against" },
      ],
      config: [
        { key: "analysis_depth", label: "Analysis Depth", options: ["Summary only", "Summary + recommendations", "Full deep dive"] },
        { key: "lookback_period", label: "Lookback Period", options: ["30 days", "60 days", "90 days", "All time"] },
      ]
    },
    { id: "battlecards", name: "Battle Cards", icon: "star", color: "#3D8B8B",
      desc: "Competitor intelligence, objection handling, and positioning strategies",
      settings: [
        { key: "auto_generate_cards", label: "Auto-generate battle cards from deal notes", desc: "Create cards when competitor names are mentioned in notes" },
        { key: "update_from_calls", label: "Update cards from call transcripts", desc: "Pull new competitor intel from call recordings" },
        { key: "suggest_positioning", label: "Suggest positioning when competitor is tagged", desc: "Show relevant battle card when a competitor is linked to a deal" },
        { key: "track_competitor_wins", label: "Track win rate against each competitor", desc: "Build a competitive scorecard over time" },
      ],
      config: [
        { key: "card_detail", label: "Card Detail Level", options: ["Quick reference", "Standard", "Comprehensive"] },
        { key: "auto_refresh", label: "Auto-Refresh Cards", options: ["Weekly", "Bi-weekly", "Monthly", "Manual only"] },
      ],
      customFields: [
        { key: "company_name", label: "Your Company Name", type: "text", placeholder: "e.g. Acme Corp" },
        { key: "company_description", label: "What You Sell", type: "textarea", placeholder: "Brief description of your product/service, target market, and key value props" },
        { key: "key_differentiators", label: "Key Differentiators", type: "textarea", placeholder: "What makes you different? Pricing, features, support, integrations, speed, etc." },
        { key: "competitors", label: "Known Competitors (one per line)", type: "textarea", placeholder: "HubSpot\nSalesforce\nPipedrive\nClose.io" },
        { key: "industry", label: "Your Industry", options: ["B2B SaaS", "B2B Services", "B2C", "E-commerce", "Healthcare", "Financial Services", "Real Estate", "Education", "Manufacturing", "Other"] },
      ]
    },
    { id: "hygiene", name: "Data Hygiene", icon: "check", color: "#E53935",
      desc: "Cleans contacts, fixes data, reschedules overdue tasks",
      settings: [
        { key: "flag_missing_fields", label: "Flag contacts with missing key fields", desc: "Highlight contacts without email, phone, or company" },
        { key: "detect_duplicates", label: "Detect and merge duplicate contacts", desc: "Find contacts with matching names or emails" },
        { key: "reschedule_overdue", label: "Auto-reschedule overdue tasks", desc: "Move overdue tasks to tomorrow automatically" },
        { key: "stale_contact_cleanup", label: "Flag stale contacts for archiving", desc: "Identify contacts with no activity for 90+ days" },
        { key: "data_quality_score", label: "Calculate org-wide data quality score", desc: "Daily score based on completeness, accuracy, freshness" },
      ],
      config: [
        { key: "scan_frequency", label: "Scan Frequency", options: ["Daily", "Weekly", "Manual only"] },
        { key: "auto_fix_mode", label: "Auto-Fix Mode", options: ["Suggest only", "Fix + notify", "Fix silently"] },
        { key: "archive_threshold", label: "Stale Contact Threshold", options: ["60 days", "90 days", "120 days", "180 days"] },
      ]
    },
  ];
  const [agentConfigs, setAgentConfigs] = useState(() => {
    const saved = {};
    CORE_AGENTS.forEach(a => { saved[a.id] = { enabled: true, settings: {}, config: {} }; a.settings.forEach(s => { saved[a.id].settings[s.key] = true; }); a.config.forEach(c => { saved[a.id].config[c.key] = c.options?.[0] || ""; }); });
    return saved;
  });
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [agentsSaving, setAgentsSaving] = useState(false);
  const [agentsSaved, setAgentsSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.from("agent_configs").select("*").eq("org_id", orgId).limit(1).maybeSingle();
        if (data?.config) setAgentConfigs(prev => ({ ...prev, ...data.config }));
      } catch (e) {}
    })();
  }, [orgId]);

  const saveAgentConfigs = async () => {
    setAgentsSaving(true);
    try {
      await supabase.from("agent_configs").upsert({ org_id: orgId, config: agentConfigs, updated_at: new Date().toISOString() }, { onConflict: "org_id" });
      setAgentsSaved(true);
      setTimeout(() => setAgentsSaved(false), 2000);
    } catch (e) { console.warn("[Agents] Save failed:", e.message); }
    setAgentsSaving(false);
  };

  const toggleAgentSetting = (agentId, settingKey) => {
    setAgentConfigs(prev => ({ ...prev, [agentId]: { ...prev[agentId], settings: { ...prev[agentId].settings, [settingKey]: !prev[agentId].settings[settingKey] } } }));
  };

  const updateAgentConfig = (agentId, configKey, value) => {
    setAgentConfigs(prev => ({ ...prev, [agentId]: { ...prev[agentId], config: { ...prev[agentId].config, [configKey]: value } } }));
  };

  const toggleAgentEnabled = (agentId) => {
    setAgentConfigs(prev => ({ ...prev, [agentId]: { ...prev[agentId], enabled: !prev[agentId].enabled } }));
  };

  // ── USER INVITE FORM ──
  const [showInvite, setShowInvite] = useState(false);
  const [expandedRole, setExpandedRole] = useState(null);
  const [invForm, setInvForm] = useState({ firstName: "", lastName: "", email: "", role: "sdr", title: "", quota: "" });
  const [invMsg, setInvMsg] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editPerms, setEditPerms] = useState(null); // userId being edited for permissions

  // System fields (read-only base structure)
  const systemFields = {
    contacts: DEFAULT_CONTACT_FIELDS,
    deals: DEFAULT_DEAL_FIELDS,
    companies: DEFAULT_COMPANY_FIELDS,
    tasks: DEFAULT_TASK_FIELDS,
    calls: DEFAULT_CALL_FIELDS,
    emails: DEFAULT_EMAIL_FIELDS,
    sms: DEFAULT_SMS_FIELDS,
    calendar: DEFAULT_CALENDAR_FIELDS,
  };

  // Merged = system + custom from store
  const currentFields = [...(systemFields[fieldEntity] || []), ...(store.customFields?.[fieldEntity] || [])];

  // New / edit field form. editingFieldId is null in add mode, or the
  // existing field's id when editing a Tier C custom field.
  const [showNewField, setShowNewField] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [nfLabel, setNfLabel] = useState("");
  const [nfType, setNfType] = useState("text");
  const [nfRequired, setNfRequired] = useState(false);
  const [nfOptions, setNfOptions] = useState("");
  // Call Dispositions modal state. editingDisposition is the row being
  // edited (with id) or { id: null, label, behavior, requeue_days } for
  // the create flow. null when modal is closed.
  const [editingDisposition, setEditingDisposition] = useState(null);
  const [savingDisposition, setSavingDisposition] = useState(false);
  // Calculation fields
  const [nfCalcFormula, setNfCalcFormula] = useState("");
  const [nfCalcFormat, setNfCalcFormat] = useState("number"); // number, currency, percent
  // Tier B "Edit Options" modal — mutually exclusive with the Edit Field
  // modal above. editingOptionsFieldId is null when closed, or the system
  // field's id when open. optionsArray is the editable list of option strings
  // (one row per entry); empty/whitespace entries are filtered on Save so the
  // user can append blanks without persisting them.
  const [editingOptionsFieldId, setEditingOptionsFieldId] = useState(null);
  const [optionsArray, setOptionsArray] = useState([]);

  // Pipeline editor — reads from store
  const pipeStages = store.pipelineStages;
  const setPipeStages = store.setPipelineStages;
  const [newStageName, setNewStageName] = useState("");
  const [editingStage, setEditingStage] = useState(null);

  // Lead scoring rules
  const [scoringRules, setScoringRules] = useState([
    { id: 1, condition: "Email opened", points: 5, active: true },
    { id: 2, condition: "Email replied", points: 15, active: true },
    { id: 3, condition: "Meeting booked", points: 25, active: true },
    { id: 4, condition: "Call > 5 min", points: 10, active: true },
    { id: 5, condition: "Visited pricing page", points: 20, active: true },
    { id: 6, condition: "Downloaded whitepaper", points: 10, active: false },
    { id: 7, condition: "No activity > 14 days", points: -15, active: true },
    { id: 8, condition: "Deal created", points: 30, active: true },
  ]);

  // Load scoring rules from Supabase.
  // Stored in org_settings (org-scoped JSONB key/value). Previously stored
  // in user_preferences but that table is being switched to user-scoped RLS
  // in migration 046, which would break this org-wide setting for any admin
  // who didn't write the row.
  useEffect(() => {
    if (!orgId) return;
    supabase.from("org_settings").select("value").eq("org_id", orgId).eq("key", "lead_scoring_rules").maybeSingle()
      .then(({ data }) => { if (data?.value && Array.isArray(data.value)) setScoringRules(data.value); });
  }, [orgId]);

  // ── Teams State ──
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [newTeamForm, setNewTeamForm] = useState({ name: "", description: "", manager_id: "" });
  const [editingTeam, setEditingTeam] = useState(null); // team id being edited
  const [editingTeamResources, setEditingTeamResources] = useState(null); // team id for resource config
  const [localTeamRes, setLocalTeamRes] = useState({ bookingLinks: [], scripts: [], docs: [] });

  // Load teams
  useEffect(() => {
    if (!orgId) return;
    setTeamsLoading(true);
    Promise.all([
      supabase.from("teams").select("*").eq("org_id", orgId).order("created_at"),
      supabase.from("team_members").select("*"),
    ]).then(([teamsRes, membersRes]) => {
      if (teamsRes.data) setTeams(teamsRes.data);
      if (membersRes.data) setTeamMembers(membersRes.data);
    }).finally(() => setTeamsLoading(false));
  }, [orgId]);

  const createTeam = async () => {
    if (!newTeamForm.name.trim()) return;
    const { data, error } = await supabase.from("teams").insert({
      org_id: orgId, name: newTeamForm.name.trim(), description: newTeamForm.description.trim(),
      manager_id: newTeamForm.manager_id || null,
      dialer_resources: { bookingLinks: [], scripts: [], docs: [] },
    }).select().single();
    if (data) { setTeams(prev => [...prev, data]); setShowNewTeam(false); setNewTeamForm({ name: "", description: "", manager_id: "" }); }
    if (error) console.warn("[CoreFlow] Create team error:", error.message);
  };

  const updateTeam = async (id, updates) => {
    const { error } = await supabase.from("teams").update({ ...updates, updated_at: new Date().toISOString() }).eq("id", id);
    if (!error) setTeams(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  const deleteTeam = async (id) => {
    if (!window.confirm("Delete this team? Members won't be deleted.")) return;
    await supabase.from("teams").delete().eq("id", id);
    setTeams(prev => prev.filter(t => t.id !== id));
    setTeamMembers(prev => prev.filter(m => m.team_id !== id));
  };

  const addTeamMember = async (teamId, memberId) => {
    if (!teamId || !memberId) return;
    const exists = teamMembers.find(m => m.team_id === teamId && m.user_id === memberId);
    if (exists) return;
    const { data } = await supabase.from("team_members").insert({ team_id: teamId, user_id: memberId, role: "member" }).select().single();
    if (data) setTeamMembers(prev => [...prev, data]);
  };

  const removeTeamMember = async (teamId, memberId) => {
    await supabase.from("team_members").delete().eq("team_id", teamId).eq("user_id", memberId);
    setTeamMembers(prev => prev.filter(m => !(m.team_id === teamId && m.user_id === memberId)));
  };

  const saveTeamResources = async (teamId, resources) => {
    const clean = {
      bookingLinks: (resources.bookingLinks || []).filter(b => b.name && b.url).map(({ id, name, url }) => ({ id, name, url })),
      scripts: (resources.scripts || []).filter(s => s.name || s.content).map(({ id, name, content }) => ({ id, name, content })),
      docs: (resources.docs || []).filter(d => d.name && d.url).map(({ id, name, url }) => ({ id, name, url })),
    };
    await updateTeam(teamId, { dialer_resources: clean });
  };

  const tabs = [
    { id: "profile", label: "Profile & Account" },
    { id: "notifications", label: "Notifications" },
    { id: "signatures", label: "Email Signatures" },
    { id: "phone_numbers", label: "Phone Numbers" },
    { id: "integrations", label: "Integrations" },
    { id: "email_access", label: "Email Access" },
    { id: "users", label: "Users & Permissions" },
    { id: "teams", label: "Teams" },
    { id: "fields", label: "Custom Fields" },
    { id: "dispositions", label: "Call Dispositions" },
    { id: "pipeline", label: "Pipeline" },
    { id: "governance", label: "Governance" },
    { id: "team", label: "Quotas" },
    { id: "scoring", label: "Lead Scoring" },
    { id: "billing", label: "Billing & Plan" },
    { id: "general", label: "General" },
  ];

  // Reset all new/edit-field form state to defaults.
  const resetFieldForm = () => {
    setNfLabel(""); setNfType("text"); setNfRequired(false); setNfOptions("");
    setNfCalcFormula(""); setNfCalcFormat("number");
    setEditingFieldId(null);
    setShowNewField(false);
  };

  // Open the form pre-populated from an existing custom field (Tier C only).
  // Closes the Tier B Edit Options modal if it's open — only one editor at
  // a time, and stale state from the other panel mustn't leak in.
  const openEditField = (f) => {
    setEditingOptionsFieldId(null);
    setOptionsArray([]);
    setEditingFieldId(f.id);
    setNfLabel(f.label || "");
    setNfType(f.type || "text");
    setNfRequired(!!f.required);
    setNfOptions(Array.isArray(f.options) ? f.options.join(", ") : "");
    setNfCalcFormula(f.formula || "");
    setNfCalcFormat(f.calcFormat || "number");
    setShowNewField(true);
  };

  // Unified save handler. Branches on editingFieldId — null = add new,
  // set = update existing custom field. Field id is never edited; stable
  // ids keep references (filters, exports, integrations) intact.
  const saveField = () => {
    if (!nfLabel.trim()) return;
    const isSelectType = nfType === "select" || nfType === "multiselect";
    const payload = {
      label: nfLabel,
      type: nfType,
      required: nfRequired,
      options: isSelectType ? nfOptions.split(",").map(s => s.trim()).filter(Boolean) : undefined,
    };
    if (nfType === "calculation") {
      payload.formula = nfCalcFormula;
      payload.calcFormat = nfCalcFormat;
    }
    if (editingFieldId) {
      store.updateCustomField(fieldEntity, editingFieldId, payload);
    } else {
      store.addCustomField(fieldEntity, {
        id: "custom_" + Date.now(),
        system: false,
        ...payload,
      });
    }
    resetFieldForm();
  };

  // Back-compat alias — existing primary button onClick used `addField`.
  const addField = saveField;

  const removeField = (id) => {
    store.removeCustomField(fieldEntity, id);
  };

  // ── Tier B: Edit Options (label/type/required stay locked) ──
  // Open the Edit Options modal pre-populated with the field's effective
  // options (override > default). Closes the Edit Field form if open.
  const openEditOptions = (f) => {
    resetFieldForm();
    const eff = getEffectiveOptions(f, fieldEntity, store.customFields);
    setOptionsArray(Array.isArray(eff) ? [...eff] : []);
    setEditingOptionsFieldId(f.id);
  };

  const closeEditOptions = () => {
    setEditingOptionsFieldId(null);
    setOptionsArray([]);
  };

  // Per-row helpers for the editable options stack.
  const updateOption = (i, val) => setOptionsArray(prev => prev.map((o, idx) => idx === i ? val : o));
  const removeOption = (i) => setOptionsArray(prev => prev.filter((_, idx) => idx !== i));
  const addOption = () => setOptionsArray(prev => [...prev, ""]);

  // Persist an override via the typed setSystemFieldOptions helper. AdminCenter
  // stays agnostic of the underlying storage shape — the helper encapsulates
  // it. Persistence (localStorage + organizations.custom_fields JSONB) is
  // handled inside the store via the existing setCustomFields path. Empty /
  // whitespace-only entries are filtered out so the user can leave blank rows
  // mid-edit without polluting the saved list.
  const saveOptions = () => {
    if (!editingOptionsFieldId) return;
    const newOptions = optionsArray.map(s => (s || "").trim()).filter(Boolean);
    store.setSystemFieldOptions(fieldEntity, editingOptionsFieldId, newOptions);
    closeEditOptions();
  };

  const inputS = { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "8px 10px", color: T.txt, fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div style={{ display: "flex", height: "100%", background: "transparent" }}>

      {/* ── Email Connection Success Overlay ── */}
      {emailConnectSuccess && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}
          onClick={() => setEmailConnectSuccess(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 12, padding: "40px 48px", textAlign: "center", maxWidth: 420, boxShadow: "0 40px 100px rgba(0,0,0,0.4)" }}>
            <div style={{ width: 56, height: 56, borderRadius: 28, background: "rgba(46,139,87,0.12)", border: "1px solid rgba(46,139,87,0.25)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E8B57" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div style={{ fontSize: 20, color: "#FAFAFA", marginBottom: 6 }}>Email Connected</div>
            <div style={{ fontSize: 13, color: T.tx3, marginBottom: 4 }}>
              {emailConnectSuccess.provider === "google" ? "Google" : emailConnectSuccess.provider === "microsoft" ? "Microsoft" : emailConnectSuccess.provider} account successfully linked
            </div>
            {emailConnectSuccess.email && <div style={{ fontSize: 12, color: "#4059AD", marginBottom: 20 }}>{emailConnectSuccess.email}</div>}
            <div style={{ fontSize: 11, color: T.tx3, marginBottom: 24, lineHeight: 1.6 }}>Your emails will now sync with CoreFlow. You can send and receive emails directly from contact records.</div>
            <button onClick={() => setEmailConnectSuccess(null)}
              style={{ padding: "10px 32px", borderRadius: 8, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
              Continue to Settings
            </button>
          </div>
        </div>
      )}

      {/* Left: Settings nav — hidden on mobile */}
      {!isMobile && <div style={{ width: 200, flexShrink: 0, borderRight: "1px solid " + T.bdr, background: "#2C2E33", overflowY: "auto" }}>
        <div style={{ padding: "16px 12px 8px", fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".1em" }}>SETTINGS</div>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
            border: "none", borderLeft: tab === t.id ? "3px solid #4059AD" : "3px solid transparent",
            background: tab === t.id ? "#4059AD" + "12" : "transparent",
            color: tab === t.id ? "#FAFAFA" : T.tx2,
            fontSize: 12, fontWeight: tab === t.id ? 700 : 500, cursor: "pointer",
          }}>{t.label}</button>
        ))}

        <div style={{ padding: "16px 12px 8px", fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".1em", marginTop: 16 }}>AI CONFIGURATION</div>
        {[
          { id: "ai_agents", label: "Core Agents" },
          { id: "ai_scoring", label: "AI Lead Scoring" },
          { id: "transcription", label: "Transcription" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
            border: "none", borderLeft: tab === t.id ? "3px solid #4059AD" : "3px solid transparent",
            background: tab === t.id ? "#4059AD" + "12" : "transparent",
            color: tab === t.id ? "#FAFAFA" : T.tx2,
            fontSize: 12, fontWeight: tab === t.id ? 700 : 500, cursor: "pointer",
          }}>{t.label}</button>
        ))}
      </div>}

      {/* Right: Content */}
      <div style={{ flex: 1, overflow: "auto", padding: isMobile ? 12 : 24 }}>
        {/* Mobile tab selector */}
        {isMobile && (
          <select value={tab} onChange={e => setTab(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "#33363D", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", marginBottom: 14, appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' fill='none' stroke-width='1.5'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", paddingRight: 28 }}>
            {tabs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            <option value="ai_agents">Core Agents</option>
            <option value="ai_scoring">AI Lead Scoring</option>
            <option value="transcription">Transcription</option>
          </select>
        )}

        {/* ═══ CUSTOM FIELDS ═══ */}
        {/* ═══ PROFILE & ACCOUNT ═══ */}
        {tab === "profile" && <ProfileTab inputS={inputS} />}

        {/* ═══ NOTIFICATIONS ═══ */}
        {tab === "notifications" && <NotificationsTab />}

        {/* ═══ EMAIL SIGNATURES ═══ */}
        {tab === "signatures" && <SignaturesTab inputS={inputS} />}

        {/* ═══ PHONE NUMBERS & COMPLIANCE ═══ */}
        {tab === "phone_numbers" && <PhoneNumbersTab inputS={inputS} />}

        {/* ═══ BILLING ═══ */}
        {tab === "billing" && <BillingTab />}

        {tab === "fields" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Custom Fields</h2>
                <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Define the data structure for your CRM objects</p>
              </div>
              <Btn primary onClick={() => { resetFieldForm(); setShowNewField(true); }}>+ Add Field</Btn>
            </div>

            {/* Entity tabs — solid-fill active state matches the canonical
                Required Fields chip pattern at line 2884. 1px border on both
                branches eliminates the 1px layout shift the prior
                2px-vs-1px swap caused on click. */}
            <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
              {["contacts", "deals", "companies", "tasks", "calls", "emails", "sms", "calendar"].map(e => (
                <button key={e} onClick={() => setFieldEntity(e)} style={{
                  padding: "8px 20px", borderRadius: 8,
                  border: "1px solid " + (fieldEntity === e ? "#4059AD" : T.bdr),
                  background: fieldEntity === e ? "#4059AD" : T.card,
                  color: fieldEntity === e ? "#FAFAFA" : T.tx2,
                  fontSize: 12, fontWeight: 400, cursor: "pointer", textTransform: "capitalize",
                }}>{e}</button>
              ))}
            </div>

            {/* Field list */}
            <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, overflow: "hidden" }}>
              {/* Header */}
              <div style={{ display: "flex", padding: "10px 16px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid " + T.bdr }}>
                <span style={{ flex: 2, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>FIELD NAME</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>TYPE</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>REQUIRED</span>
                <span style={{ width: 120, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textAlign: "right" }}>ACTIONS</span>
              </div>
              {currentFields.map((f, i) => (
                <div key={f.id} style={{
                  display: "flex", alignItems: "center", padding: "10px 16px",
                  borderBottom: i < currentFields.length - 1 ? "1px solid " + T.bdr : "none",
                  opacity: f.system ? 1 : 0.95,
                }}>
                  <div style={{ flex: 2, display: "flex", alignItems: "center", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{f.label}</div>
                      {f.system && <span style={{ fontSize: 9, color: T.tx3 }}>System field</span>}
                      {f.type === "calculation" && f.formula && <span style={{ fontSize: 9, color: "#6B5B95", fontFamily: "inherit" }}>= {f.formula}</span>}
                      {(() => {
                        const eff = getEffectiveOptions(f, fieldEntity, store.customFields);
                        return eff.length > 0 ? <span style={{ fontSize: 9, color: T.tx3 }}>{eff.join(", ")}</span> : null;
                      })()}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    {/* Inline replacement for <Badge color={...}> — the shared
                        Badge component silently drops its color prop. 6px
                        radius (vs. shared Badge's 10px) was the actual fix
                        for the pill-capsule shape on this 20px-tall element.
                        Single muted neutral style matches the canonical
                        unselected-chip pattern elsewhere in AdminCenter
                        (per-type color coding was tried in 0ba0b1d0 and
                        reverted — looked noisy across the field list). */}
                    <span style={{
                      padding: "3px 8px",
                      borderRadius: 6,
                      border: "1px solid rgba(250,250,250,0.08)",
                      background: "rgba(250,250,250,0.04)",
                      color: T.tx2,
                      fontSize: 10,
                      fontWeight: 400,
                      textTransform: "lowercase",
                    }}>{f.type}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    {f.required && <span style={{ fontSize: 10, color: "#E53935", fontWeight: 400 }}>Required</span>}
                  </div>
                  <div style={{ width: 120, textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {f.system && TIER_B_FIELD_IDS[fieldEntity]?.has(f.id) && (
                      <button onClick={() => openEditOptions(f)} style={{
                        background: "none", border: "1px solid " + T.bdr, borderRadius: 3,
                        color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                      }}>Edit Options</button>
                    )}
                    {!f.system && !LOCKED_FIELD_IDS.has(f.id) && (
                      <button onClick={() => openEditField(f)} style={{
                        background: "none", border: "1px solid " + T.bdr, borderRadius: 3,
                        color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                      }}>Edit</button>
                    )}
                    {!f.system && (
                      <button onClick={() => removeField(f.id)} style={{
                        background: "none", border: "1px solid " + "#E53935" + "30", borderRadius: 3,
                        color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                      }}>Remove</button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Tier C: New / Edit Custom Field modal */}
            <Modal
              open={showNewField}
              title={editingFieldId ? "EDIT CUSTOM FIELD" : "NEW CUSTOM FIELD"}
              width={480}
              onClose={resetFieldForm}
              footer={
                <>
                  <Btn onClick={resetFieldForm}>Cancel</Btn>
                  <Btn primary onClick={saveField}>{editingFieldId ? "Save Changes" : "Create Field"}</Btn>
                </>
              }
            >
              <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Field Label</div>
                  <input value={nfLabel} onChange={e => setNfLabel(e.target.value)} placeholder="e.g. LinkedIn URL" autoFocus style={inputS} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Type</div>
                  <Select value={nfType} onChange={e => setNfType(e.target.value)} style={{ ...inputS, cursor: "pointer" }}>
                    {FIELD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </Select>
                </div>
              </div>
              {(nfType === "select" || nfType === "multiselect") && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Options (comma-separated)</div>
                  <input value={nfOptions} onChange={e => setNfOptions(e.target.value)} placeholder="Option 1, Option 2, Option 3" style={inputS} />
                </div>
              )}
              {nfType === "calculation" && (
                <div style={{ marginBottom: 12, background: "rgba(255,255,255,0.03)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#6B5B95", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>fx</span> Calculation Configuration
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Formula</div>
                    <input value={nfCalcFormula} onChange={e => setNfCalcFormula(e.target.value)}
                      placeholder="e.g. val * prob / 100" style={{ ...inputS, fontFamily: "inherit" }} />
                    <div style={{ fontSize: 9, color: T.tx3, marginTop: 4, lineHeight: 1.5 }}>
                      Use field IDs from this entity. Available: {currentFields.filter(f => f.type === "number" || f.type === "currency").map(f => <code key={f.id} style={{ background: T.blue + "10", padding: "1px 4px", borderRadius: 2, marginRight: 4, color: T.blue }}>{f.id}</code>)}
                      <br />Operators: + - * / ( ) min() max() round()
                      <br />Special: <code style={{ background: "#6B5B95" + "10", padding: "1px 4px", borderRadius: 2, color: "#6B5B95" }}>deal_count</code> <code style={{ background: "#6B5B95" + "10", padding: "1px 4px", borderRadius: 2, color: "#6B5B95" }}>total_deal_value</code> <code style={{ background: "#6B5B95" + "10", padding: "1px 4px", borderRadius: 2, color: "#6B5B95" }}>days_since_created</code> <code style={{ background: "#6B5B95" + "10", padding: "1px 4px", borderRadius: 2, color: "#6B5B95" }}>days_since_activity</code>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Display Format</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[
                        { id: "number", label: "Number (1,234)" },
                        { id: "currency", label: "Currency ($1,234)" },
                        { id: "percent", label: "Percent (12.3%)" },
                      ].map(f => (
                        <button key={f.id} onClick={() => setNfCalcFormat(f.id)} style={{
                          flex: 1, padding: "6px 8px", borderRadius: 8, fontSize: 10, fontWeight: 400, cursor: "pointer",
                          border: "1px solid " + (nfCalcFormat === f.id ? "#6B5B95" : T.bdr),
                          background: nfCalcFormat === f.id ? "#6B5B95" : T.card,
                          color: nfCalcFormat === f.id ? "#FAFAFA" : T.tx3,
                        }}>{f.label}</button>
                      ))}
                    </div>
                  </div>
                  {nfCalcFormula && (
                    <div style={{ marginTop: 10, padding: "8px 10px", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
                      <div style={{ fontSize: 9, color: T.tx3, marginBottom: 3 }}>Preview</div>
                      <div style={{ fontSize: 13, fontWeight: 400, color: "#6B5B95", fontFamily: "inherit" }}>
                        = {nfCalcFormula} {"->"} {nfCalcFormat === "currency" ? "$X,XXX" : nfCalcFormat === "percent" ? "XX.X%" : "X,XXX"}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.tx2, cursor: "pointer" }}>
                  <Checkbox checked={nfRequired} onChange={e => setNfRequired(e.target.checked)} />
                  Required field
                </label>
              </div>
            </Modal>

            {/* Tier B: Edit Options modal — system field, options-only edit */}
            {(() => {
              const f = editingOptionsFieldId ? currentFields.find(x => x.id === editingOptionsFieldId) : null;
              return (
                <Modal
                  open={editingOptionsFieldId !== null}
                  title={`EDIT OPTIONS — ${f?.label?.toUpperCase() || ""}`}
                  width={420}
                  onClose={closeEditOptions}
                  footer={
                    <>
                      <Btn onClick={closeEditOptions}>Cancel</Btn>
                      <Btn primary onClick={saveOptions}>Save Options</Btn>
                    </>
                  }
                >
                  <div style={{ fontSize: 11, color: T.tx3, marginBottom: 12, lineHeight: 1.5 }}>
                    System field — only the dropdown options can be edited. Label, type, and required-flag stay locked.
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto", marginBottom: 10 }}>
                    {optionsArray.map((opt, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        background: "rgba(255,255,255,0.04)",
                        borderRadius: 10,
                        padding: "4px 4px 4px 14px",
                      }}>
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(i, e.target.value)}
                          style={{
                            flex: 1, background: "transparent", border: "none", outline: "none",
                            color: T.txt, fontSize: 13, padding: "6px 0", fontFamily: "inherit",
                          }}
                          placeholder="Option name"
                        />
                        <button
                          onClick={() => removeOption(i)}
                          aria-label="Remove option"
                          style={{
                            width: 28, height: 28, borderRadius: 8, border: "none",
                            background: "transparent", color: T.tx3, fontSize: 16, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >×</button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={addOption}
                    style={{
                      background: "transparent", border: "1px dashed rgba(217,219,221,0.20)",
                      color: T.tx3, padding: "8px 14px", borderRadius: 10,
                      cursor: "pointer", fontSize: 12, width: "100%", fontFamily: "inherit",
                    }}
                  >+ Add option</button>
                </Modal>
              );
            })()}
          </div>
        )}

        {/* ═══ CALL DISPOSITIONS ═══ */}
        {tab === "dispositions" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Call Dispositions</h2>
                <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>
                  Configure the dispositions reps choose after each call. Behaviors automatically apply to the contact (e.g., "Do Not Call" excludes from all dialer surfaces).
                </p>
              </div>
              <Btn primary onClick={() => setEditingDisposition({ id: null, label: "", behavior: "none", requeue_days: 3 })}>+ Add Disposition</Btn>
            </div>

            {/* Disposition list — same card pattern as Custom Fields */}
            <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, overflow: "hidden" }}>
              {/* Header */}
              <div style={{ display: "flex", padding: "10px 16px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid " + T.bdr }}>
                <span style={{ flex: 2, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>LABEL</span>
                <span style={{ flex: 2, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>BEHAVIOR</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>RE-QUEUE</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>STATUS</span>
                <span style={{ width: 140, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textAlign: "right" }}>ACTIONS</span>
              </div>

              {(store.callDispositions || []).length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: T.tx3, fontSize: 12 }}>
                  No dispositions configured yet. Click "+ Add Disposition" to create one.
                </div>
              )}

              {(store.callDispositions || []).map((d, i, arr) => (
                <div key={d.id} style={{
                  display: "flex", alignItems: "center", padding: "10px 16px",
                  borderBottom: i < arr.length - 1 ? "1px solid " + T.bdr : "none",
                  opacity: d.enabled ? 1 : 0.5,
                }}>
                  <div style={{ flex: 2, fontSize: 12, fontWeight: 400, color: T.txt }}>{d.label}</div>
                  <div style={{ flex: 2 }}>
                    {/* Behavior pill — neutral muted style per commit 0ba0b1d0
                        (NOT color-coded; matches Custom Fields type-badge). */}
                    <span style={{
                      padding: "3px 8px", borderRadius: 6,
                      border: "1px solid rgba(250,250,250,0.08)",
                      background: "rgba(250,250,250,0.04)",
                      color: T.tx2, fontSize: 10, fontWeight: 400,
                    }}>{BEHAVIOR_LABELS[d.behavior] || d.behavior}</span>
                  </div>
                  <div style={{ flex: 1, fontSize: 11, color: T.tx2 }}>
                    {d.behavior === "requeue_later" ? `${d.requeue_days || 3} days` : "—"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{
                      padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 400,
                      background: d.enabled ? "rgba(46,139,87,0.1)" : "rgba(250,250,250,0.04)",
                      color: d.enabled ? "#2E8B57" : T.tx3,
                      border: "1px solid " + (d.enabled ? "rgba(46,139,87,0.3)" : "rgba(250,250,250,0.08)"),
                    }}>{d.enabled ? "Active" : "Disabled"}</span>
                  </div>
                  <div style={{ width: 140, textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button onClick={() => setEditingDisposition({ ...d })} style={{
                      background: "none", border: "1px solid " + T.bdr, borderRadius: 3,
                      color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                    }}>Edit</button>
                    <button onClick={async () => {
                      try {
                        await store.toggleCallDisposition(d.id, !d.enabled);
                        toast?.success?.(d.enabled ? "Disposition disabled" : "Disposition enabled");
                      } catch (e) {
                        toast?.error?.("Update failed: " + e.message);
                      }
                    }} style={{
                      background: "none", border: "1px solid " + T.bdr, borderRadius: 3,
                      color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                    }}>{d.enabled ? "Disable" : "Enable"}</button>
                  </div>
                </div>
              ))}
            </div>

            {/* New / Edit Disposition modal */}
            <Modal
              open={!!editingDisposition}
              title={editingDisposition?.id ? "EDIT DISPOSITION" : "NEW DISPOSITION"}
              width={460}
              onClose={() => setEditingDisposition(null)}
              footer={
                <>
                  <Btn onClick={() => setEditingDisposition(null)}>Cancel</Btn>
                  <Btn primary
                    onClick={async () => {
                      if (!editingDisposition?.label?.trim()) return;
                      setSavingDisposition(true);
                      try {
                        if (editingDisposition.id) {
                          await store.updateCallDisposition(editingDisposition.id, editingDisposition);
                          toast?.success?.("Disposition updated");
                        } else {
                          await store.addCallDisposition(editingDisposition);
                          toast?.success?.("Disposition created");
                        }
                        setEditingDisposition(null);
                      } catch (e) {
                        toast?.error?.("Save failed: " + e.message);
                      } finally {
                        setSavingDisposition(false);
                      }
                    }}
                  >
                    {savingDisposition ? "Saving..." : (editingDisposition?.id ? "Save Changes" : "Create Disposition")}
                  </Btn>
                </>
              }
            >
              {editingDisposition && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Label</div>
                    <input
                      value={editingDisposition.label || ""}
                      onChange={(e) => setEditingDisposition({ ...editingDisposition, label: e.target.value })}
                      placeholder="e.g. Connected — gatekeeper"
                      autoFocus
                      style={inputS}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Behavior</div>
                    <Select
                      value={editingDisposition.behavior}
                      onChange={(e) => setEditingDisposition({ ...editingDisposition, behavior: e.target.value })}
                      style={{ ...inputS, cursor: "pointer" }}
                    >
                      {Object.entries(BEHAVIOR_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </Select>
                    <div style={{ fontSize: 11, color: T.tx3, marginTop: 6, lineHeight: 1.5 }}>
                      {BEHAVIOR_DESCRIPTIONS[editingDisposition.behavior]}
                    </div>
                  </div>

                  {editingDisposition.behavior === "requeue_later" && (
                    <div>
                      <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>Re-queue after (days)</div>
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={editingDisposition.requeue_days || 3}
                        onChange={(e) => setEditingDisposition({ ...editingDisposition, requeue_days: parseInt(e.target.value, 10) || 3 })}
                        style={inputS}
                      />
                    </div>
                  )}
                </div>
              )}
            </Modal>
          </div>
        )}

        {/* ═══ PIPELINE EDITOR ═══ */}
        {tab === "pipeline" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Pipeline Configuration</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Configure deal stages, probabilities, and automation triggers</p>
            </div>

            <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ display: "flex", padding: "10px 16px", background: "rgba(255,255,255,0.03)", borderBottom: "1px solid " + T.bdr }}>
                <span style={{ width: 40, fontSize: 10, fontWeight: 400, color: T.tx3 }}>ORDER</span>
                <span style={{ flex: 2, fontSize: 10, fontWeight: 400, color: T.tx3 }}>STAGE NAME</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 400, color: T.tx3 }}>PROBABILITY</span>
                <span style={{ flex: 1, fontSize: 10, fontWeight: 400, color: T.tx3 }}>COLOR</span>
                <span style={{ width: 100, fontSize: 10, fontWeight: 400, color: T.tx3, textAlign: "right" }}>ACTIONS</span>
              </div>
              {pipeStages.map((s, i) => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid " + T.bdr }}>
                  <div style={{ width: 40 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button onClick={() => { if (i > 0) { const n = [...pipeStages]; [n[i-1], n[i]] = [n[i], n[i-1]]; setPipeStages(n); }}} disabled={i === 0}
                        style={{ background: "none", border: "none", color: i === 0 ? T.bdr : T.tx3, fontSize: 10, cursor: i === 0 ? "default" : "pointer", padding: 0 }}>{"+"}</button>
                      <button onClick={() => { if (i < pipeStages.length - 1) { const n = [...pipeStages]; [n[i], n[i+1]] = [n[i+1], n[i]]; setPipeStages(n); }}} disabled={i === pipeStages.length - 1}
                        style={{ background: "none", border: "none", color: i === pipeStages.length - 1 ? T.bdr : T.tx3, fontSize: 10, cursor: i === pipeStages.length - 1 ? "default" : "pointer", padding: 0 }}>{"-"}</button>
                    </div>
                  </div>
                  <div style={{ flex: 2 }}>
                    {editingStage === s.id ? (
                      <input autoFocus value={s.name} onChange={e => setPipeStages(prev => prev.map(x => x.id === s.id ? { ...x, name: e.target.value } : x))}
                        onBlur={() => setEditingStage(null)} onKeyDown={e => e.key === "Enter" && setEditingStage(null)}
                        style={{ ...inputS, maxWidth: 200 }} />
                    ) : (
                      <span onClick={() => setEditingStage(s.id)} style={{ fontSize: 13, fontWeight: 400, color: T.txt, cursor: "pointer", borderBottom: "1px dashed " + T.bdr }}>
                        {s.name}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="range" min={0} max={100} value={s.prob}
                      onChange={e => setPipeStages(prev => prev.map(x => x.id === s.id ? { ...x, prob: parseInt(e.target.value) } : x))}
                      style={{ width: 80, accentColor: s.color }} />
                    <span style={{ fontSize: 11, color: T.tx2, fontWeight: 400, minWidth: 30 }}>{s.prob}%</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ width: 20, height: 20, borderRadius: 8, background: s.color, border: "2px solid " + s.color + "50" }} />
                  </div>
                  <div style={{ width: 100, textAlign: "right" }}>
                    <button onClick={() => setPipeStages(prev => prev.filter(x => x.id !== s.id))} style={{
                      background: "none", border: "1px solid " + "#E53935" + "30", borderRadius: 3,
                      color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                    }}>Remove</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add stage */}
            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              <input value={newStageName} onChange={e => setNewStageName(e.target.value)} placeholder="New stage name..."
                onKeyDown={e => {
                  if (e.key === "Enter" && newStageName.trim()) {
                    setPipeStages(prev => [...prev, { id: Date.now(), name: newStageName, color: T.blue, prob: 50 }]);
                    setNewStageName("");
                  }
                }}
                style={{ ...inputS, maxWidth: 250 }} />
              <Btn primary onClick={() => {
                if (newStageName.trim()) {
                  setPipeStages(prev => [...prev, { id: Date.now(), name: newStageName, color: T.blue, prob: 50 }]);
                  setNewStageName("");
                }
              }}>Add Stage</Btn>
            </div>

            {/* ── LEAD STAGES ── */}
            <div style={{ marginTop: 32 }}>
              <h2 style={{ fontSize: 16, fontWeight: 400, color: T.txt, margin: "0 0 4px" }}>Lead Stages</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "0 0 14px" }}>Configure the lifecycle stages for contacts</p>

              <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, overflow: "hidden" }}>
                {store.leadStages.map((s, i) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: i < store.leadStages.length - 1 ? "1px solid " + T.bdr : "none" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button onClick={() => { if (i > 0) { const n = [...store.leadStages]; [n[i-1], n[i]] = [n[i], n[i-1]]; store.setLeadStages(n); }}} disabled={i === 0}
                        style={{ background: "none", border: "none", color: i === 0 ? T.bdr : T.tx3, fontSize: 10, cursor: i === 0 ? "default" : "pointer", padding: 0 }}>{"+"}</button>
                      <button onClick={() => { if (i < store.leadStages.length - 1) { const n = [...store.leadStages]; [n[i], n[i+1]] = [n[i+1], n[i]]; store.setLeadStages(n); }}} disabled={i === store.leadStages.length - 1}
                        style={{ background: "none", border: "none", color: i === store.leadStages.length - 1 ? T.bdr : T.tx3, fontSize: 10, cursor: i === store.leadStages.length - 1 ? "default" : "pointer", padding: 0 }}>{"-"}</button>
                    </div>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: s.color, border: "1px solid " + s.color + "50" }} />
                    <input value={s.name} onChange={e => store.setLeadStages(prev => prev.map(x => x.id === s.id ? { ...x, name: e.target.value } : x))}
                      style={{ ...inputS, maxWidth: 200, padding: "4px 8px" }} />
                    <input type="color" value={s.color} onChange={e => store.setLeadStages(prev => prev.map(x => x.id === s.id ? { ...x, color: e.target.value } : x))}
                      style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer" }} />
                    <div style={{ flex: 1 }} />
                    <button onClick={() => store.setLeadStages(prev => prev.filter(x => x.id !== s.id))} style={{
                      background: "none", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                    }}>Remove</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Btn onClick={() => store.setLeadStages(prev => [...prev, { id: Date.now(), name: "New Stage", color: T.blue }])}>+ Add Lead Stage</Btn>
              </div>
            </div>

            {/* ── CUSTOM PIPELINES ── */}
            <div style={{ marginTop: 32 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 400, color: T.txt, margin: "0 0 4px" }}>Custom Pipelines</h2>
                  <p style={{ fontSize: 12, color: T.tx3, margin: 0 }}>Create custom kanban boards for any workflow</p>
                </div>
                <Btn primary onClick={() => store.addPipeline({
                  name: "New Pipeline",
                  icon: "calendar",
                  stages: [
                    { id: 1, name: "To Do", color: "#535C6E" },
                    { id: 2, name: "In Progress", color: "#FFB547" },
                    { id: 3, name: "Done", color: "#00D68F" },
                  ],
                  fields: [
                    { id: "title", label: "Title", type: "text", required: true },
                    { id: "description", label: "Description", type: "textarea" },
                    { id: "assignee", label: "Assignee", type: "text" },
                    { id: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Urgent"] },
                  ],
                })}>+ Create Pipeline</Btn>
              </div>

              {store.customPipelines.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8 }}>
                  <div style={{ fontSize: 13, color: T.tx3, marginBottom: 8 }}>No custom pipelines yet</div>
                  <div style={{ fontSize: 11, color: T.tx3 }}>Create pipelines for onboarding, support tickets, projects, or any workflow</div>
                </div>
              ) : store.customPipelines.map(pipe => (
                <div key={pipe.id} style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                  {/* Pipeline header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid " + T.bdr, background: T.sf }}>
                    <input value={pipe.name} onChange={e => store.updatePipeline(pipe.id, { name: e.target.value })}
                      style={{ background: "transparent", border: "none", outline: "none", fontSize: 14, fontWeight: 400, color: T.txt, flex: 1 }} />
                    <span style={{ fontSize: 10, color: T.tx3 }}>{pipe.stages?.length || 0} stages</span>
                    <button onClick={() => store.removePipeline(pipe.id)} style={{
                      background: "none", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, color: "#FAFAFA", fontSize: 9, padding: "3px 8px", cursor: "pointer",
                    }}>Delete</button>
                  </div>
                  {/* Stages */}
                  <div style={{ padding: "8px 16px" }}>
                    <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 6 }}>STAGES</div>
                    {(pipe.stages || []).map((s, i) => (
                      <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid " + T.bdr + "40" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <button onClick={() => { if (i > 0) { const ns = [...pipe.stages]; [ns[i-1], ns[i]] = [ns[i], ns[i-1]]; store.updatePipeline(pipe.id, { stages: ns }); }}} disabled={i === 0}
                            style={{ background: "none", border: "none", color: i === 0 ? T.bdr : T.tx3, fontSize: 8, cursor: i === 0 ? "default" : "pointer", padding: 0 }}>{"+"}</button>
                          <button onClick={() => { if (i < (pipe.stages || []).length - 1) { const ns = [...pipe.stages]; [ns[i], ns[i+1]] = [ns[i+1], ns[i]]; store.updatePipeline(pipe.id, { stages: ns }); }}} disabled={i === (pipe.stages || []).length - 1}
                            style={{ background: "none", border: "none", color: i === (pipe.stages || []).length - 1 ? T.bdr : T.tx3, fontSize: 8, cursor: i === (pipe.stages || []).length - 1 ? "default" : "pointer", padding: 0 }}>{"-"}</button>
                        </div>
                        <div style={{ width: 12, height: 12, borderRadius: 2, background: s.color }} />
                        <input value={s.name} onChange={e => {
                          const ns = (pipe.stages || []).map(x => x.id === s.id ? { ...x, name: e.target.value } : x);
                          store.updatePipeline(pipe.id, { stages: ns });
                        }} style={{ ...inputS, padding: "3px 6px", fontSize: 11, flex: 1 }} />
                        <input type="color" value={s.color} onChange={e => {
                          const ns = (pipe.stages || []).map(x => x.id === s.id ? { ...x, color: e.target.value } : x);
                          store.updatePipeline(pipe.id, { stages: ns });
                        }} style={{ width: 22, height: 22, border: "none", background: "none", cursor: "pointer" }} />
                        <button onClick={() => store.updatePipeline(pipe.id, { stages: (pipe.stages || []).filter(x => x.id !== s.id) })}
                          style={{ background: "none", border: "none", color: "#FAFAFA", fontSize: 10, cursor: "pointer" }}>{"X"}</button>
                      </div>
                    ))}
                    <button onClick={() => store.updatePipeline(pipe.id, { stages: [...(pipe.stages || []), { id: Date.now(), name: "New Stage", color: T.blue }] })}
                      style={{ background: "none", border: "1px dashed " + T.bdr, borderRadius: 8, padding: "4px 10px", color: T.tx3, fontSize: 10, cursor: "pointer", marginTop: 6, width: "100%" }}>
                      + Add Stage
                    </button>
                  </div>
                  {/* Fields */}
                  <div style={{ padding: "8px 16px", borderTop: "1px solid " + T.bdr }}>
                    <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 6 }}>PROPERTIES / FIELDS</div>
                    {(pipe.fields || []).map((f, i) => (
                      <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid " + T.bdr + "40" }}>
                        <input value={f.label} onChange={e => {
                          const nf = (pipe.fields||[]).map(x => x.id === f.id ? { ...x, label: e.target.value } : x);
                          store.updatePipeline(pipe.id, { fields: nf });
                        }} style={{ ...inputS, padding: "3px 6px", fontSize: 11, flex: 1 }} />
                        <Select value={f.type} onChange={e => {
                          const nf = (pipe.fields||[]).map(x => x.id === f.id ? { ...x, type: e.target.value } : x);
                          store.updatePipeline(pipe.id, { fields: nf });
                        }} style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 3, padding: "3px 4px", color: T.txt, fontSize: 9, outline: "none" }}>
                          {FIELD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </Select>
                        {(f.type === "select" || f.type === "multiselect") && (
                          <input value={(f.options||[]).join(", ")} onChange={e => {
                            const nf = (pipe.fields||[]).map(x => x.id === f.id ? { ...x, options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } : x);
                            store.updatePipeline(pipe.id, { fields: nf });
                          }} placeholder="Options (comma-separated)" style={{ ...inputS, padding: "3px 6px", fontSize: 9, flex: 1 }} />
                        )}
                        {f.type === "calculation" && (
                          <input value={f.formula||""} onChange={e => {
                            const nf = (pipe.fields||[]).map(x => x.id === f.id ? { ...x, formula: e.target.value } : x);
                            store.updatePipeline(pipe.id, { fields: nf });
                          }} placeholder="Formula (e.g. amount * 0.1)" style={{ ...inputS, padding: "3px 6px", fontSize: 9, flex: 1, fontFamily: "inherit" }} />
                        )}
                        <label style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: T.tx3, whiteSpace: "nowrap" }}>
                          <Checkbox checked={!!f.required} onChange={() => {
                            const nf = (pipe.fields||[]).map(x => x.id === f.id ? { ...x, required: !f.required } : x);
                            store.updatePipeline(pipe.id, { fields: nf });
                          }} size={14} />
                          Req
                        </label>
                        <button onClick={() => store.updatePipeline(pipe.id, { fields: (pipe.fields||[]).filter(x => x.id !== f.id) })}
                          style={{ background: "none", border: "none", color: "#FAFAFA", fontSize: 10, cursor: "pointer" }}>{"X"}</button>
                      </div>
                    ))}
                    <button onClick={() => store.updatePipeline(pipe.id, { fields: [...(pipe.fields || []), { id: "f_" + Date.now(), label: "New Field", type: "text", required: false }] })}
                      style={{ background: "none", border: "1px dashed " + T.bdr, borderRadius: 8, padding: "4px 10px", color: T.tx3, fontSize: 10, cursor: "pointer", marginTop: 6, width: "100%" }}>
                      + Add Field
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ EMAIL ACCESS ═══ */}
        {tab === "email_access" && <EmailAccessTab />}

        {/* ═══ TEAM ═══ */}
        {/* ═══ USERS & PERMISSIONS ═══ */}
        {tab === "users" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Users & Permissions</h2>
                <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>{(auth.users || []).length} total users · {(auth.users || []).filter(u => u.status === "active").length} active</p>
              </div>
              {auth.hasPermission("admin_team") && (
                <button onClick={() => { setShowInvite(!showInvite); setInvMsg(""); }} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>+ Invite User</button>
              )}
            </div>

            {/* Invite Form */}
            {showInvite && (
              <div style={{ background: "#2C2E33", border: "1px solid rgba(64,89,173,0.25)", borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA", marginBottom: 14 }}>Invite New User</div>
                {invMsg && <div style={{ fontSize: 12, color: invMsg.startsWith("Invite sent") ? "#2E8B57" : "#E53935", marginBottom: 10, padding: "10px 14px", borderRadius: 8, background: invMsg.startsWith("Invite sent") ? "rgba(46,139,87,0.08)" : "rgba(139,69,87,0.08)", border: "1px solid " + (invMsg.startsWith("Invite sent") ? "rgba(46,139,87,0.2)" : "rgba(139,69,87,0.2)") }}>{invMsg}</div>}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div><label style={{ fontSize: 10, color: T.tx3, marginBottom: 4, display: "block" }}>FIRST NAME</label><input style={inputS} value={invForm.firstName} onChange={e => setInvForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Sarah" /></div>
                  <div><label style={{ fontSize: 10, color: T.tx3, marginBottom: 4, display: "block" }}>LAST NAME</label><input style={inputS} value={invForm.lastName} onChange={e => setInvForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Johnson" /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                  <div><label style={{ fontSize: 10, color: T.tx3, marginBottom: 4, display: "block" }}>EMAIL</label><input style={inputS} type="email" value={invForm.email} onChange={e => setInvForm(f => ({ ...f, email: e.target.value }))} placeholder="sarah@company.com" /></div>
                  <div><label style={{ fontSize: 10, color: T.tx3, marginBottom: 4, display: "block" }}>ROLE</label><Select style={inputS} value={invForm.role} onChange={e => setInvForm(f => ({ ...f, role: e.target.value }))}>{Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</Select></div>
                  <div><label style={{ fontSize: 10, color: T.tx3, marginBottom: 4, display: "block" }}>TITLE</label><input style={inputS} value={invForm.title} onChange={e => setInvForm(f => ({ ...f, title: e.target.value }))} placeholder="Account Exec" /></div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => { const res = await auth.inviteUser(invForm); if (res.success) { setInvMsg("Invite sent to " + invForm.email); setInvForm({ firstName: "", lastName: "", email: "", role: "sdr", title: "", quota: "" }); } else setInvMsg("Error: " + (res.error || "Failed")); }} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Send Invite</button>
                  <button onClick={() => setShowInvite(false)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.5)", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                </div>
              </div>
            )}

            {/* User List — Clean table layout */}
            <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.10)", borderRadius: 8, overflow: "hidden", marginBottom: 24 }}>
              {/* Header */}
              <div style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 0.7fr 1.5fr", padding: "10px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.02)" }}>
                <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>USER</span>
                <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>ROLE</span>
                <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>STATUS</span>
                <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textAlign: "right" }}>ACTIONS</span>
              </div>

              {(auth.users || []).map((u, idx) => {
                const role = ROLES[u.role] || {};
                const isMe = u.id === auth.currentUser?.id;
                const canManage = auth.hasPermission("admin_team") && !isMe;
                const initials = (u.firstName?.[0] || "") + (u.lastName?.[0] || "");

                return (
                  <div key={u.id}>
                    <div style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 0.7fr 1.5fr", alignItems: "center", padding: "12px 16px", borderBottom: idx < (auth.users || []).length - 1 || editPerms === u.id ? "1px solid rgba(217,219,221,0.06)" : "none" }}>
                      {/* User info */}
                      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        {u.avatarUrl || u.avatar_url ? (
                          <img src={u.avatarUrl || u.avatar_url} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(217,219,221,0.08)" }} />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(64,89,173,0.12)", fontSize: 13, fontWeight: 400, color: "#4059AD", flexShrink: 0 }}>{initials}</div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{u.firstName} {u.lastName}</span>
                            {isMe && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "rgba(64,89,173,0.12)", color: "#4059AD", fontWeight: 400 }}>YOU</span>}
                          </div>
                          <div style={{ fontSize: 10, color: T.tx3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                        </div>
                      </div>

                      {/* Role */}
                      {canManage ? (
                        <select value={u.role} onChange={e => { const res = auth.changeRole(u.id, e.target.value); if (!res.success) alert(res.error); }} style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit", maxWidth: 120 }}>
                          {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      ) : (
                        <span style={{ fontSize: 11, color: "#FAFAFA" }}>{role.label}</span>
                      )}

                      {/* Status */}
                      <span style={{ fontSize: 10, color: u.status === "active" ? "#2E8B57" : u.status === "suspended" ? "#E53935" : T.tx3 }}>{u.status || "active"}</span>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        {canManage && (
                          <>
                            <button onClick={() => setEditPerms(editPerms === u.id ? null : u.id)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid " + (editPerms === u.id ? "#4059AD" : "rgba(217,219,221,0.12)"), background: editPerms === u.id ? "rgba(64,89,173,0.1)" : "transparent", color: editPerms === u.id ? "#4059AD" : "rgba(250,250,250,0.5)", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Permissions</button>
                            {u.status === "active" ? (
                              <button onClick={() => { if (window.confirm("Suspend " + u.firstName + "?")) auth.suspendUser(u.id); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.4)", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Suspend</button>
                            ) : u.status === "suspended" ? (
                              <button onClick={() => auth.activateUser(u.id)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(46,139,87,0.2)", background: "transparent", color: "#2E8B57", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Activate</button>
                            ) : null}
                            <button onClick={() => { if (window.confirm("Remove " + u.firstName + " permanently?")) auth.removeUser(u.id); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(139,69,87,0.2)", background: "transparent", color: "#E53935", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Inline Permission Editor */}
                    {editPerms === u.id && (
                      <div style={{ padding: "16px 16px 16px 64px", background: "rgba(255,255,255,0.01)", borderBottom: "1px solid rgba(217,219,221,0.06)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <span style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>Permissions for {u.firstName}</span>
                          <div style={{ display: "flex", gap: 6 }}>
                            {u.permissions && <button onClick={() => auth.setUserPermissions(u.id, null)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.4)", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Reset to role defaults</button>}
                            <button onClick={() => setEditPerms(null)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(250,250,250,0.4)", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
                          </div>
                        </div>
                        {(() => {
                          const rawPerms = u.permissions || ROLE_PERMISSIONS[u.role] || [];
                          const currentPerms = Array.isArray(rawPerms) ? rawPerms : (typeof rawPerms === "string" ? (() => { try { return JSON.parse(rawPerms); } catch { return []; } })() : []);
                          const groups = {};
                          Object.entries(PERMISSIONS).forEach(([key, p]) => { if (!groups[p.group]) groups[p.group] = []; groups[p.group].push({ key, ...p }); });
                          return Object.entries(groups).map(([group, perms]) => (
                            <div key={group} style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 6 }}>{group.toUpperCase()}</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "4px 16px" }}>
                                {perms.map(p => {
                                  const active = currentPerms.includes(p.key);
                                  return (
                                    <button key={p.key} onClick={() => {
                                      const newPerms = active ? currentPerms.filter(k => k !== p.key) : [...currentPerms, p.key];
                                      auth.setUserPermissions(u.id, newPerms);
                                    }} title={p.desc} style={{ fontSize: 10, padding: "5px 0", cursor: "pointer", border: "none", background: "transparent", display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
                                      <span style={{ width: 18, height: 18, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: active ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", background: active ? "#4059AD" : "transparent", transition: "all 0.15s" }}>
                                        {active && <Icon name="check" size={10} color="#FAFAFA" />}
                                      </span>
                                      <span style={{ color: active ? "#FAFAFA" : T.tx3 }}>{p.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Role Reference */}
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginBottom: 12 }}>Role Reference</h3>
              {Object.entries(ROLES).map(([key, r]) => {
                const perms = ROLE_PERMISSIONS[key] || [];
                const isExpanded = expandedRole === key;
                const defaultColor = ({ sdr: "#2E8B57", account_exec: T.blue, manager: "#D4763A", customer_success: "#3D8B8B" })[key] || "#7A7A7A";
                return (
                  <div key={key} style={{ marginBottom: 4 }}>
                    <div onClick={() => setExpandedRole(isExpanded ? null : key)} style={{ display: "grid", gridTemplateColumns: "28px 100px 1fr auto auto", alignItems: "center", gap: 12, padding: "10px 14px", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.10)", borderRadius: isExpanded ? "8px 8px 0 0" : 8, cursor: "pointer" }}>
                      <RoleColorPicker orgId={orgId} roleKey={key} defaultColor={defaultColor} />
                      <span style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA", padding: "3px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.04)", textAlign: "center" }}>{r.label}</span>
                      <span style={{ fontSize: 11, color: T.tx2 }}>{r.description}</span>
                      <span style={{ fontSize: 10, color: T.tx3 }}>{perms.length} permissions</span>
                      <Icon name={isExpanded ? "chevronU" : "chevronD"} size={12} color={T.tx3} />
                    </div>
                    {isExpanded && (
                      <div style={{ padding: "12px 14px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(217,219,221,0.10)", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {perms.map(p => {
                            const perm = PERMISSIONS[p];
                            return <span key={p} style={{ fontSize: 9, fontWeight: 400, color: "rgba(250,250,250,0.5)", padding: "3px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.06)", background: "rgba(255,255,255,0.02)" }}>{perm?.label || p}</span>;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ TEAMS ═══ */}
        {tab === "teams" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", margin: 0 }}>Teams</h2>
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 4 }}>Create teams, assign members, and configure team-specific dialer resources</div>
              </div>
              <button onClick={() => setShowNewTeam(!showNewTeam)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                + New Team
              </button>
            </div>

            {/* New Team Form */}
            {showNewTeam && (
              <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, padding: 20, marginBottom: 16, borderLeft: "3px solid #4059AD" }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", marginBottom: 12 }}>Create Team</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 200px" }}>
                    <div style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>Team Name</div>
                    <input value={newTeamForm.name} onChange={e => setNewTeamForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. SDR Team, Enterprise AEs"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ flex: "1 1 200px" }}>
                    <div style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>Description</div>
                    <input value={newTeamForm.description} onChange={e => setNewTeamForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ flex: "0 0 180px" }}>
                    <div style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 4 }}>Manager</div>
                    <select value={newTeamForm.manager_id} onChange={e => setNewTeamForm(p => ({ ...p, manager_id: e.target.value }))}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "#33363D", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit" }}>
                      <option value="">No manager</option>
                      {(store.teamMembers || auth.teamMembers || []).map(u => (
                        <option key={u.id} value={u._dbId || u.id}>{u.name || u.fn || u.first_name || ""} {u.ln || u.last_name || ""}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={createTeam} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Create Team</button>
                  <button onClick={() => setShowNewTeam(false)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Teams List */}
            {teamsLoading && <div style={{ color: T.tx3, fontSize: 11, padding: 20 }}>Loading teams...</div>}
            {!teamsLoading && teams.length === 0 && !showNewTeam && (
              <div style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, padding: 60, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#FAFAFA", marginBottom: 4 }}>No teams yet</div>
                <div style={{ fontSize: 11, color: T.tx3, marginBottom: 16 }}>Teams let you organize reps and configure different dialer resources per group</div>
                <button onClick={() => setShowNewTeam(true)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>+ Create First Team</button>
              </div>
            )}

            {teams.map(team => {
              const members = teamMembers.filter(m => m.team_id === team.id);
              const manager = (store.teamMembers || []).find(u => (u._dbId || u.id) === team.manager_id);
              const isEditing = editingTeam === team.id;
              const isEditingResources = editingTeamResources === team.id;
              const teamRes = team.dialer_resources || { bookingLinks: [], scripts: [], docs: [] };

              return (
                <div key={team.id} style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, padding: 18, marginBottom: 10 }}>
                  {/* Team Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, color: "#FAFAFA" }}>{team.name}</div>
                      {team.description && <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>{team.description}</div>}
                      {manager && <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Manager: {manager.name || ((manager.fn || manager.first_name || "") + " " + (manager.ln || manager.last_name || ""))}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => { if (isEditingResources) { setEditingTeamResources(null); } else { setEditingTeamResources(team.id); setLocalTeamRes(JSON.parse(JSON.stringify(teamRes))); } }}
                        style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: isEditingResources ? "rgba(64,89,173,0.15)" : "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>
                        {isEditingResources ? "Close Resources" : "Dialer Resources"}
                      </button>
                      <button onClick={() => setEditingTeam(isEditing ? null : team.id)}
                        style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: isEditing ? "rgba(255,255,255,0.06)" : "transparent", color: "#FAFAFA", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>
                        {isEditing ? "Done" : "Manage Members"}
                      </button>
                      <button onClick={() => deleteTeam(team.id)}
                        style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid rgba(139,69,87,0.2)", background: "transparent", color: "#E53935", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
                    </div>
                  </div>

                  {/* Members Row */}
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {members.map(m => {
                      const user = (store.teamMembers || []).find(u => (u._dbId || u.id) === m.user_id);
                      return (
                        <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,219,221,0.06)", fontSize: 10, color: "#FAFAFA" }}>
                          {user ? (user.name || (user.fn || user.first_name || "") + " " + (user.ln || user.last_name || "")) : "Unknown"}
                          {isEditing && (
                            <button onClick={() => removeTeamMember(team.id, m.user_id)}
                              style={{ background: "none", border: "none", color: "#E53935", cursor: "pointer", fontSize: 12, padding: "0 2px" }}>×</button>
                          )}
                        </div>
                      );
                    })}
                    {members.length === 0 && <span style={{ fontSize: 10, color: T.tx3 }}>No members yet</span>}
                  </div>

                  {/* Add Members */}
                  {isEditing && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                      <div style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>ADD MEMBER</div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(store.teamMembers || []).filter(u => !members.find(m => m.user_id === (u._dbId || u.id))).map(u => (
                          <button key={u.id} onClick={() => addTeamMember(team.id, u._dbId || u.id)}
                            style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                            + {u.name || ((u.fn || u.first_name || "") + " " + (u.ln || u.last_name || ""))}
                          </button>
                        ))}
                        {(store.teamMembers || []).filter(u => !members.find(m => m.user_id === (u._dbId || u.id))).length === 0 && (
                          <span style={{ fontSize: 10, color: T.tx3 }}>All users are already in this team</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Team Dialer Resources */}
                  {isEditingResources && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
                      <div style={{ fontSize: 11, color: "#FAFAFA", marginBottom: 4 }}>Dialer Resources for {team.name}</div>
                      <div style={{ fontSize: 9, color: T.tx3, marginBottom: 12 }}>These override org-level resources for members of this team</div>

                      {/* Booking Links */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase" }}>Booking Links ({(localTeamRes.bookingLinks || []).length})</span>
                          <button onClick={() => setLocalTeamRes({ ...localTeamRes, bookingLinks: [...(localTeamRes.bookingLinks || []), { id: Date.now(), name: "", url: "" }] })}
                            style={{ fontSize: 9, color: "#FAFAFA", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
                        </div>
                        {(localTeamRes.bookingLinks || []).map((bl, i) => (
                          <div key={bl.id || i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                            <input placeholder="Name" value={bl.name} onChange={e => { const bls = [...(localTeamRes.bookingLinks || [])]; bls[i] = { ...bls[i], name: e.target.value }; setLocalTeamRes({ ...localTeamRes, bookingLinks: bls }); }}
                              style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit" }} />
                            <input placeholder="https://calendly.com/..." value={bl.url} onChange={e => { const bls = [...(localTeamRes.bookingLinks || [])]; bls[i] = { ...bls[i], url: e.target.value }; setLocalTeamRes({ ...localTeamRes, bookingLinks: bls }); }}
                              style={{ flex: 2, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit" }} />
                            <button onClick={() => { const bls = (localTeamRes.bookingLinks || []).filter((_, j) => j !== i); setLocalTeamRes({ ...localTeamRes, bookingLinks: bls }); }}
                              style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12 }}>×</button>
                          </div>
                        ))}
                      </div>

                      {/* Scripts */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase" }}>Sales Scripts ({(localTeamRes.scripts || []).length})</span>
                          <button onClick={() => setLocalTeamRes({ ...localTeamRes, scripts: [...(localTeamRes.scripts || []), { id: Date.now(), name: "", content: "" }] })}
                            style={{ fontSize: 9, color: "#FAFAFA", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
                        </div>
                        {(localTeamRes.scripts || []).map((sc, i) => (
                          <div key={sc.id || i} style={{ marginBottom: 6 }}>
                            <input placeholder="Script name" value={sc.name} onChange={e => { const scs = [...(localTeamRes.scripts || [])]; scs[i] = { ...scs[i], name: e.target.value }; setLocalTeamRes({ ...localTeamRes, scripts: scs }); }}
                              style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 4 }} />
                            <div style={{ display: "flex", gap: 6 }}>
                              <textarea placeholder="Script content..." value={sc.content} onChange={e => { const scs = [...(localTeamRes.scripts || [])]; scs[i] = { ...scs[i], content: e.target.value }; setLocalTeamRes({ ...localTeamRes, scripts: scs }); }}
                                style={{ flex: 1, minHeight: 60, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 10, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
                              <button onClick={() => { const scs = (localTeamRes.scripts || []).filter((_, j) => j !== i); setLocalTeamRes({ ...localTeamRes, scripts: scs }); }}
                                style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12, alignSelf: "flex-start" }}>×</button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Documents */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontSize: 9, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase" }}>Documents ({(localTeamRes.docs || []).length})</span>
                          <button onClick={() => setLocalTeamRes({ ...localTeamRes, docs: [...(localTeamRes.docs || []), { id: Date.now(), name: "", url: "" }] })}
                            style={{ fontSize: 9, color: "#FAFAFA", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
                        </div>
                        {(localTeamRes.docs || []).map((doc, i) => (
                          <div key={doc.id || i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                            <input placeholder="Document name" value={doc.name} onChange={e => { const ds = [...(localTeamRes.docs || [])]; ds[i] = { ...ds[i], name: e.target.value }; setLocalTeamRes({ ...localTeamRes, docs: ds }); }}
                              style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit" }} />
                            <input placeholder="URL..." value={doc.url} onChange={e => { const ds = [...(localTeamRes.docs || [])]; ds[i] = { ...ds[i], url: e.target.value }; setLocalTeamRes({ ...localTeamRes, docs: ds }); }}
                              style={{ flex: 2, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit" }} />
                            <button onClick={() => { const ds = (localTeamRes.docs || []).filter((_, j) => j !== i); setLocalTeamRes({ ...localTeamRes, docs: ds }); }}
                              style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12 }}>×</button>
                          </div>
                        ))}
                      </div>

                      <button onClick={() => { saveTeamResources(team.id, localTeamRes); setEditingTeamResources(null); }}
                        style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#4059AD", color: "#D9DBDD", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Save Resources</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ QUOTAS (simplified team tab) ═══ */}
        {tab === "team" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Quota Management</h2>
                <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Set revenue quotas and daily KPI targets by role</p>
              </div>
            </div>

            {/* ── Daily KPI Targets by Role ── */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Daily Activity Targets by Role</div>
              <p style={{ fontSize: 11, color: T.tx3, margin: "0 0 14px" }}>These targets appear in each rep's daily AI briefing. Edit a field and it auto-saves.</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr repeat(3, 100px)", gap: 0, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".05em", padding: "0 14px 8px" }}>
                <div>ROLE</div><div style={{ textAlign: "center" }}>DIALS / DAY</div><div style={{ textAlign: "center" }}>EMAILS / DAY</div><div style={{ textAlign: "center" }}>DMs / DAY</div>
              </div>
              {["sdr", "account_exec", "manager", "customer_success"].map(role => {
                const t = (store.kpiTargets || {})[role] || { dials: 0, emails: 0, linkedin_dms: 0 };
                const roleLabels = { sdr: "SDR / Appointment Setter", account_exec: "Account Executive", manager: "Sales Manager", customer_success: "Customer Success" };
                const roleColors = { sdr: "#2E8B57", account_exec: T.blue, manager: "#D4763A", customer_success: "#3D8B8B" };
                const updateKpi = (field, val) => {
                  const num = parseInt(val) || 0;
                  store.setKpiTargets(prev => ({ ...prev, [role]: { ...prev[role], [field]: num } }));
                };
                return (
                  <div key={role} style={{ display: "grid", gridTemplateColumns: "1fr repeat(3, 100px)", gap: 0, alignItems: "center", padding: "10px 14px", background: T.card, borderRadius: 8, marginBottom: 4, border: "1.5px solid rgba(217,219,221,0.12)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 8, background: roleColors[role] }} />
                      <span style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{roleLabels[role]}</span>
                    </div>
                    {["dials", "emails", "linkedin_dms"].map(field => (
                      <div key={field} style={{ textAlign: "center" }}>
                        <input type="number" value={t[field] || 0} onChange={e => updateKpi(field, e.target.value)}
                          style={{ width: 56, padding: "6px 4px", borderRadius: 8, border: "1px solid " + T.bdr, background: T.sf, color: T.txt, fontSize: 14, fontWeight: 400, textAlign: "center", outline: "none", fontFamily: "inherit" }} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* ── Revenue Quotas by Rep ── */}
            <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Revenue Quotas by Rep</div>
            {store.teamMembers.map(m => {
              const memberDeals = store.deals.filter(d => {
                const contact = store.contacts.find(c => c.id === d.ct);
                return contact?.owner === m._dbId || contact?.owner === m.name;
              });
              const activeDeals = memberDeals.filter(d => d.stage !== "Closed Won").length;
              const wonRevenue = memberDeals.filter(d => d.stage === "Closed Won").reduce((a, d) => a + (d.val || 0), 0);
              const pct = m.quota > 0 ? Math.round((wonRevenue / m.quota) * 100) : 0;

              return (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: 16, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, marginBottom: 8 }}>
                <Avatar text={m.avatar} url={m.avatarUrl || m.avatar_url} size={44} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: T.tx2 }}>{m.role} {"\u00b7"} {m.email}</div>
                </div>
                <div style={{ textAlign: "center", padding: "0 16px" }}>
                  <div style={{ fontSize: 18, fontWeight: 400, color: T.txt }}>{activeDeals}</div>
                  <div style={{ fontSize: 9, color: T.tx3 }}>Active Deals</div>
                </div>
                <div style={{ textAlign: "center", padding: "0 16px", minWidth: 140 }}>
                  <div style={{ fontSize: 14, fontWeight: 400, color: "#2E8B57" }}>
                    {fmtDollar(wonRevenue)}
                    <span style={{ fontSize: 10, color: T.tx3, fontWeight: 400 }}> / </span>
                    {editingQuotaId === m.id ? (
                      <input
                        autoFocus
                        type="number"
                        value={editingQuotaValue}
                        onChange={e => setEditingQuotaValue(e.target.value)}
                        onKeyDown={async e => {
                          if (e.key === "Escape") { setEditingQuotaId(null); return; }
                          if (e.key !== "Enter") return;
                          const num = Number(editingQuotaValue);
                          if (!Number.isFinite(num) || num < 0) { toast?.error?.("Quota must be a positive number"); return; }
                          try {
                            await store.updateTeamMember(m.id, { quota: num });
                            setEditingQuotaId(null);
                          } catch (err) {
                            console.error("[AdminCenter Quotas] Failed to update quota:", err);
                            toast?.error?.("Failed to update quota: " + (err.message || "unknown error"));
                          }
                        }}
                        onBlur={() => setEditingQuotaId(null)}
                        style={{ width: 80, fontSize: 11, padding: "2px 6px", borderRadius: 4, border: "1px solid " + T.blue, background: "rgba(255,255,255,0.06)", color: T.txt, fontFamily: "inherit", outline: "none", textAlign: "right" }}
                      />
                    ) : (
                      <span onClick={() => { setEditingQuotaId(m.id); setEditingQuotaValue(String(m.quota || 0)); }} style={{ fontSize: 10, color: T.blue, fontWeight: 400, cursor: "pointer", borderBottom: "1px dashed " + T.blue + "40" }}>
                        {fmtDollar(m.quota)}
                      </span>
                    )}
                  </div>
                  <ProgressBar value={wonRevenue} max={m.quota} color={pct >= 80 ? "#2E8B57" : pct >= 50 ? "#D4763A" : "#E53935"} height={4} />
                  <div style={{ fontSize: 9, color: T.tx3, marginTop: 2 }}>{pct}% of quota</div>
                </div>
                <Badge color={m.status === "active" ? "#2E8B57" : "#E53935"}>{m.status}</Badge>
              </div>
              );
            })}
          </div>
        )}
        {/* ═══ LEAD SCORING ═══ */}
        {tab === "scoring" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Lead Scoring Rules</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Configure how contacts earn or lose points. Active rules are applied automatically when events occur.</p>
            </div>

            <div style={{ borderRadius: 8, overflow: "hidden" }}>
              {scoringRules.map((r, i) => (
                <div key={r.id} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                  borderBottom: i < scoringRules.length - 1 ? "1px solid rgba(217,219,221,0.06)" : "none",
                  opacity: r.active ? 1 : 0.4,
                }}>
                  <button onClick={() => {
                    const prev = scoringRules;
                    const updated = scoringRules.map(x => x.id === r.id ? { ...x, active: !x.active } : x);
                    setScoringRules(updated);
                    // Persist to Supabase (org_settings — org-scoped, see load comment above)
                    supabase.from("org_settings").upsert({ org_id: orgId, key: "lead_scoring_rules", value: updated }, { onConflict: "org_id,key" }).then(({ error }) => {
                      if (error) {
                        setScoringRules(prev); // rollback
                        console.error("[AdminCenter Scoring] Failed to save lead score rule:", error);
                        toast?.error?.("Failed to save lead score rule: " + error.message);
                      }
                    });
                  }} style={{
                    width: 20, height: 20, borderRadius: 8, flexShrink: 0,
                    border: "1.5px solid " + (r.active ? "#4059AD" : "rgba(217,219,221,0.25)"),
                    background: r.active ? "#4059AD" : "transparent",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {r.active && <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </button>
                  <div style={{ flex: 1, fontSize: 12, color: "#FAFAFA", fontWeight: 400 }}>{r.condition}</div>
                  <input type="number" value={r.points} onChange={e => {
                    const updated = scoringRules.map(x => x.id === r.id ? { ...x, points: parseInt(e.target.value) || 0 } : x);
                    setScoringRules(updated);
                  }} onBlur={() => {
                    supabase.from("org_settings").upsert({ org_id: orgId, key: "lead_scoring_rules", value: scoringRules }, { onConflict: "org_id,key" }).then(({ error }) => {
                      if (error) {
                        console.error("[AdminCenter Scoring] Failed to save lead score points:", error);
                        toast?.error?.("Failed to save lead score rule: " + error.message);
                      }
                    });
                  }} style={{ ...inputS, width: 60, textAlign: "right", fontSize: 12, fontWeight: 400, padding: "4px 8px" }} />
                  <span style={{ fontSize: 11, color: T.tx3, minWidth: 20 }}>pts</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button onClick={() => {
                const prev = scoringRules;
                const newRule = { id: Date.now(), condition: "New rule", points: 5, active: true };
                const updated = [...scoringRules, newRule];
                setScoringRules(updated);
                supabase.from("org_settings").upsert({ org_id: orgId, key: "lead_scoring_rules", value: updated }, { onConflict: "org_id,key" }).then(({ error }) => {
                  if (error) {
                    setScoringRules(prev); // rollback
                    console.error("[AdminCenter Scoring] Failed to add lead score rule:", error);
                    toast?.error?.("Failed to save lead score rule: " + error.message);
                  }
                });
              }} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>+ Add Rule</button>
            </div>
          </div>
        )}

        {/* ═══ INTEGRATIONS ═══ */}
        {tab === "integrations" && (
          <IntegrationsTab />
        )}

        {/* ═══ GOVERNANCE ═══ */}
        {tab === "governance" && (
          <GovernanceTab orgId={orgId} store={store} />
        )}

        {/* ═══ GENERAL ═══ */}
        {tab === "general" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>General Settings</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Account and workspace configuration</p>
            </div>

            <GeneralSettingsTab orgId={orgId} />

            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Danger Zone</h3>
              <div style={{ padding: 16, background: "#E53935" + "06", border: "1px solid " + "#E53935" + "20", borderRadius: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Delete Workspace</div>
                    <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>Permanently delete all data. This cannot be undone.</div>
                  </div>
                  <button onClick={() => setShowDeleteWsModal(true)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer" }}>Delete Workspace</button>
                </div>
              </div>
            </div>
            {showDeleteWsModal && (
              <div onClick={() => setShowDeleteWsModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div onClick={e => e.stopPropagation()} style={{ width: 440, maxWidth: "92vw", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(229,57,53,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#E53935", fontSize: 14, fontWeight: 500 }}>!</div>
                    <div style={{ fontSize: 15, fontWeight: 400, color: T.txt }}>Delete Workspace</div>
                  </div>
                  <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.6, marginBottom: 16 }}>
                    Deleting a workspace removes all contacts, deals, calls, emails, and team data permanently. For safety, workspace deletion is handled manually by the support team.
                  </div>
                  <div style={{ padding: 12, background: "rgba(255,255,255,0.04)", border: "1px solid " + T.bdr, borderRadius: 8, fontSize: 11, color: T.tx2, marginBottom: 16 }}>
                    To request deletion, email <a href="mailto:support@core-flow.io?subject=Workspace%20deletion%20request" style={{ color: T.blue, textDecoration: "none" }}>support@core-flow.io</a> from your account email with your workspace ID.
                    {orgId && <div style={{ marginTop: 6, fontSize: 10, color: T.tx3 }}>Workspace ID: <span style={{ color: T.txt, fontFamily: "monospace" }}>{orgId}</span></div>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button onClick={() => setShowDeleteWsModal(false)} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
                    <a href="mailto:support@core-flow.io?subject=Workspace%20deletion%20request" style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "#E53935", color: "#FAFAFA", fontSize: 11, textDecoration: "none", fontFamily: "inherit" }}>Email Support</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ AI AGENTS — Full Configuration ═══ */}
        {tab === "ai_agents" && <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Core Agents</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Configure AI-powered automation for your team</p>
            </div>
            <button onClick={saveAgentConfigs} disabled={agentsSaving} style={{
              padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
              background: agentsSaved ? "rgba(46,139,87,0.12)" : "transparent",
              color: agentsSaved ? "#2E8B57" : "#FAFAFA", fontSize: 12, fontWeight: 400,
              cursor: agentsSaving ? "default" : "pointer", fontFamily: "inherit", transition: "all 0.2s",
            }}>{agentsSaving ? "Saving..." : agentsSaved ? "Saved" : "Save Changes"}</button>
          </div>

          {CORE_AGENTS.map(agent => {
            const ac = agentConfigs[agent.id] || { enabled: true, settings: {}, config: {} };
            const isExpanded = expandedAgent === agent.id;
            const enabledCount = Object.values(ac.settings).filter(Boolean).length;
            const gc = { background: "#2C2E33", border: "1.5px solid rgba(217,219,221,0.12)", borderRadius: 8 };
            const sLbl = { fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 };
            const iS = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", fontSize: 12, color: T.txt, outline: "none", background: "rgba(255,255,255,0.06)", fontFamily: "inherit", boxSizing: "border-box" };
            const Ck = ({ on, onClick: oc }) => (
              <button style={{ width: 20, height: 20, borderRadius: 8, cursor: "pointer", border: on ? "1.5px solid " + T.blue : "1.5px solid " + T.bdr, background: on ? T.blue : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s", padding: 0 }} onClick={oc}>
                {on && <Icon name="check" size={12} color="#FAFAFA" />}
              </button>
            );

            return (
              <div key={agent.id} style={{ ...gc, marginBottom: 8, overflow: "hidden" }}>
                <div onClick={() => setExpandedAgent(isExpanded ? null : agent.id)} style={{
                  display: "flex", alignItems: "center", gap: 14, padding: 16, cursor: "pointer",
                  background: isExpanded ? "rgba(255,255,255,0.02)" : "transparent", transition: "background 0.15s",
                }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: agent.color + "12", border: "1px solid " + agent.color + "25", flexShrink: 0 }}>
                    <Icon name={agent.icon} size={18} color={agent.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, display: "flex", alignItems: "center", gap: 8 }}>
                      {agent.name}
                      <span style={{ fontSize: 10, color: T.tx3, fontWeight: 400 }}>{enabledCount}/{agent.settings.length} active</span>
                    </div>
                    <div style={{ fontSize: 11, color: T.tx2, marginTop: 2, lineHeight: 1.4 }}>{agent.desc}</div>
                  </div>
                  <Ck on={ac.enabled} onClick={e => { e.stopPropagation(); toggleAgentEnabled(agent.id); }} />
                  <Icon name={isExpanded ? "chevronU" : "chevronD"} size={14} color={T.tx3} />
                </div>

                {isExpanded && (
                  <div style={{ padding: "0 16px 16px", opacity: ac.enabled ? 1 : 0.4, pointerEvents: ac.enabled ? "auto" : "none" }}>
                    <div style={{ padding: "12px 0 16px", borderTop: "1px solid rgba(217,219,221,0.08)" }}>
                      <div style={sLbl}>Behaviors</div>
                      {agent.settings.map(s => (
                        <div key={s.key} onClick={() => toggleAgentSetting(agent.id, s.key)} style={{
                          display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", cursor: "pointer",
                          borderBottom: "1px solid rgba(217,219,221,0.04)",
                        }}>
                          <div style={{ marginTop: 1 }}>
                            <Ck on={ac.settings[s.key]} onClick={e => { e.stopPropagation(); toggleAgentSetting(agent.id, s.key); }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{s.label}</div>
                            <div style={{ fontSize: 10, color: T.tx3, marginTop: 2, lineHeight: 1.4 }}>{s.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {agent.config.length > 0 && (
                      <div style={{ paddingTop: 4 }}>
                        <div style={sLbl}>Configuration</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          {agent.config.map(c => (
                            <div key={c.key}>
                              <div style={{ fontSize: 11, fontWeight: 400, color: T.tx2, marginBottom: 5 }}>{c.label}</div>
                              <select value={ac.config[c.key] || c.options?.[0] || ""} onChange={e => updateAgentConfig(agent.id, c.key, e.target.value)} style={{ ...iS, cursor: "pointer" }}>
                                {c.options.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {agent.customFields && agent.customFields.length > 0 && (
                      <div style={{ paddingTop: 12, borderTop: "1px solid rgba(217,219,221,0.08)", marginTop: 12 }}>
                        <div style={sLbl}>Company Profile</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {agent.customFields.map(f => (
                            <div key={f.key}>
                              <div style={{ fontSize: 11, fontWeight: 400, color: T.tx2, marginBottom: 5 }}>{f.label}</div>
                              {f.options ? (
                                <select value={ac.config[f.key] || f.options?.[0] || ""} onChange={e => updateAgentConfig(agent.id, f.key, e.target.value)} style={{ ...iS, cursor: "pointer" }}>
                                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : f.type === "textarea" ? (
                                <textarea value={ac.config[f.key] || ""} onChange={e => updateAgentConfig(agent.id, f.key, e.target.value)} placeholder={f.placeholder} rows={3} style={{ ...iS, resize: "vertical", lineHeight: 1.6 }} />
                              ) : (
                                <input value={ac.config[f.key] || ""} onChange={e => updateAgentConfig(agent.id, f.key, e.target.value)} placeholder={f.placeholder} style={iS} />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>}


        {/* ═══ AI SCORING ═══ */}
        {tab === "ai_scoring" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>AI Lead Scoring</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Machine learning model trained on your conversion data</p>
            </div>
            <div style={{ padding: 20, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 24 }}>
                {[
                  { label: "Model Accuracy", value: "94.2%", color: "#2E8B57" },
                  { label: "Training Samples", value: "2,847", color: T.blue },
                  { label: "Last Trained", value: "2 hours ago", color: "#D4763A" },
                  { label: "Avg Score Delta", value: "+12.4", color: "#6B5B95" },
                ].map(s => (
                  <div key={s.label} style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 400, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: T.tx3, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: 16, background: T.blue + "06", border: "1px solid " + T.blue + "15", borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 400, color: T.blue, marginBottom: 6 }}><Icon name="ai" size={11} color={T.blue} /> CORE SCORING SIGNALS</div>
              {["Email reply rate (weight: 0.28)", "Meeting frequency (weight: 0.22)", "Decision-maker engagement (weight: 0.19)", "Deal velocity (weight: 0.16)", "Content downloads (weight: 0.15)"].map((s, i) => (
                <div key={i} style={{ fontSize: 11, color: T.tx2, padding: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#FAFAFA" }}>{i + 1}.</span> {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ TRANSCRIPTION ═══ */}
        {tab === "transcription" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 400, color: T.txt, margin: 0 }}>Call Transcription</h2>
              <p style={{ fontSize: 12, color: T.tx3, margin: "4px 0 0" }}>Automatic transcription, summaries, and action item extraction</p>
            </div>
            {[
              { label: "Auto-transcribe calls", desc: "Transcribe all calls over 1 minute", on: true },
              { label: "AI Call Summary", desc: "Generate bullet-point summaries after every call", on: true },
              { label: "Action Item Extraction", desc: "Automatically identify follow-ups and next steps", on: true },
              { label: "Sentiment Analysis", desc: "Track positive/negative sentiment throughout calls", on: false },
              { label: "Competitor Mentions", desc: "Flag when competitors are mentioned on calls", on: true },
              { label: "Coaching Alerts", desc: "Alert managers when calls need coaching review", on: false },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, marginBottom: 6 }}>
                {/* Replaced green iOS-style pill toggle with the platform's
                    standard Checkbox to match Email Access / Required Fields
                    / Governance toggles elsewhere in AdminCenter. Static-
                    display pattern (no state) matches existing non-functional
                    behavior — these settings aren't wired to a backend yet. */}
                <Checkbox checked={s.on} onChange={() => {}} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
